// Demand Forecast dashboard (14_DemandForecastDashboard.jsx) — the screen's strings in the portal's four languages
// (2026-10-21). `v` holds the labels of values the code also compares (statuses, decisions, actions): the code keeps
// the English value and only the label shown is looked up here. `col` serves the tables and the CSV headings
// (the CSV files are exports only — nothing in the portal reads them back).

const en = {
  top: {
    title: "Demand Forecast",
    actualsThrough: "Actuals through {{date}}",
    subtitle: "Weekly ERPQuantity forecast",
    env: "Staging API",
    refreshDataTitle: "Refresh forecast mart, forecast output, and backtest",
    refreshData: "Refresh Data",
    refresh: "Refresh",
    loading: "Loading",
  },
  f: {
    horizon: "Horizon",
    weeksN: "{{n}} weeks",
    noMatches: "No matches",
    clear: "Clear",
  },
  msg: {
    refreshRunning: "Refresh Data is running: mart, forecast, and backtest. This can take 2-3 minutes.",
    refreshed: "Forecast data refreshed: {{steps}} at {{time}}",
    done: "done",
    cannotParse: "Cannot read the {{endpoint}} response",
    cannotLoad: "Cannot load {{endpoint}}",
  },
  stat: {
    w1: "W+1 Forecast",
    w4: "4W Forecast",
    weeksAvailable: "{{n}}W available",
    total: "Total Forecast",
    skus: "{{n}} SKUs",
    wape: "Backtest WAPE",
    totalLevel: "Total level",
  },
  tab: {
    forecast: "Forecast",
    sku: "SKU Detail",
    backtest: "Backtest",
  },
  level: {
    total: "Total",
    branch_channel: "Branch / Channel",
    sku: "SKU",
  },
  u: {
    pts: "{{n}} pts",
    qty: "{{n}} qty",
    rows: "{{n}} rows",
    weeks: "weeks",
    ofWindows: "of {{n}} windows",
    skuMovers: "SKU movers",
  },
  v: {
    all: "All",
    review: "Review",
    watch: "Watch",
    ok: "OK",
    keepCurrent: "Keep current",
    increaseForecast: "Increase forecast",
    decreaseForecast: "Decrease forecast",
    aligned: "Aligned",
    high: "High",
    medium: "Medium",
    low: "Low",
    helps: "Helps",
    hurts: "Hurts",
    neutral: "Neutral",
    unknown: "Unknown",
    unknownReason: "Unknown reason",
    unknownRisk: "Unknown risk",
    reviewManually: "Review manually",
    needsData: "Needs data",
    keepBaseline: "Keep baseline",
    pilotCandidate: "Pilot candidate",
    reviewCandidate: "Review candidate",
    hold: "Hold",
    lowVolumeWatch: "Low volume watch",
    reviewNewDemand: "Review new demand",
    reviewDroppedDemand: "Review dropped demand",
    manualReview: "Manual review",
    acceptMovement: "Accept movement",
    pass: "Pass",
    noRunLoaded: "No run loaded",
    ready: "Ready",
    missing: "Missing",
    done: "Done",
    waiting: "Waiting",
    reviewFirst: "Review first",
    proceed: "Proceed",
    policyReady: "Policy ready",
    rollingStable: "Rolling stable",
    notStableYet: "Not stable yet",
    highOverfitRisk: "High overfit risk",
    promising: "Promising",
    promoteReview: "Promote review",
    watchOnly: "Watch only",
    productionBaseline: "Production baseline",
    ruleWins: "Rule wins",
    close: "Close",
    featureHelps: "Feature helps",
    featureHurts: "Feature hurts",
    lowVolume: "Low volume",
    noFeatureSignal: "No feature signal",
    strongCandidate: "Strong candidate",
    v3Beats: "V3 beats current",
    v3Worse: "V3 worse than current",
  },
  col: {
    productName: "Product Name",
    branch: "Branch",
    channel: "Channel",
    collection: "Collection",
    category: "Category",
    candidateQty: "Candidate Qty",
    candidateV3Qty: "Candidate V3 Qty",
    currentQty: "Current Qty",
    deltaQty: "Delta Qty",
    deltaPctTxt: "Delta Pct",
    v3DeltaVsCurrentQty: "V3 Delta vs Current Qty",
    v3DeltaVsCurrentPct: "V3 Delta vs Current Pct",
    v3DeltaVsV2Qty: "V3 Delta vs V2 Qty",
    status: "Status",
    reason: "Reason",
    dataSignal: "Data Signal",
    candidateV3Rule: "Candidate V3 Rule",
    recent12Qty: "Recent 12W Qty",
    recent4Qty: "Recent 4W Qty",
    recent12ActiveWeeks: "Recent 12W Active Weeks",
    priorYearHorizonQty: "Prior-Year Same Horizon Qty",
    currentForecast: "Current Forecast",
    candidateV3Forecast: "Candidate V3 Forecast",
    recommendedForecast: "Recommended Forecast",
    deltaPct: "Delta %",
    recommendedModel: "Recommended Model",
    bcRecommendedModel: "Branch Channel Recommended Model",
    modelAlignment: "Model Alignment",
    reviewPriority: "Review Priority",
    bcModelStatus: "Branch Channel Model Status",
    bcModelImprovement: "Branch Channel Model Improvement",
    direction: "Direction",
    recent12Actual: "Recent 12W Actual",
    recent4Actual: "Recent 4W Actual",
    priorYearHorizon: "Prior Year Same Horizon",
    actualsThrough: "Actuals Through",
    horizonWeeks: "Horizon Weeks",
    currentProductionForecast: "Current Production Forecast",
    recommendedPlanningForecast: "Recommended Planning Forecast",
    plannerOverride: "Planner Override Forecast",
    finalApproved: "Final Approved Forecast",
    reviewStatus: "Review Status",
    bcModel: "Branch Channel Model",
    bcModelShort: "BC Model",
    plannerComment: "Planner Comment",
    owner: "Owner",
    decision: "Decision",
    year: "Year",
    week: "Week",
    actualQty: "Actual Qty",
    baselineForecast: "Baseline Forecast",
    baselineWape: "Baseline WAPE",
    baselineBias: "Baseline Bias",
    featureForecast: "Feature Forecast",
    featureWape: "Feature WAPE",
    featureBias: "Feature Bias",
    ma4Forecast: "MA4 Forecast",
    ma4Bias: "MA4 Bias",
    ma12Forecast: "MA12 Forecast",
    ma12Bias: "MA12 Bias",
    winner: "Winner",
    winnerForecast: "Winner Forecast",
    winnerWape: "Winner WAPE",
    baselineGapVsWinnerPts: "Baseline Gap Vs Winner Pts",
    section: "Section",
    model: "Model",
    forecastQty: "Forecast Qty",
    bias: "Bias",
    currentWape: "Current WAPE",
    seasonalWape: "Seasonal WAPE",
    improvementVsCurrent: "Improvement Vs Current",
    hierForecast: "Hier Forecast",
    hierWape: "Hier WAPE",
    seasonalForecast: "Seasonal Forecast",
    v3Forecast: "V3 Forecast",
    v4Forecast: "V4 Forecast",
    v5Forecast: "V5 Forecast",
    naiveForecast: "Naive Forecast",
    naiveWape: "Naive WAPE",
    priority: "Priority",
    risk: "Risk",
    action: "Action",
    lowerQty: "Lower Qty",
    upperQty: "Upper Qty",
    promoShare: "Promo Qty Share",
    highDiscountShare: "High Discount Qty Share",
    priceDropShare: "Price Drop Qty Share",
    stockoutShare: "Stockout Qty Share",
    invToSales: "Inventory To Sales Ratio",
    recommendedAction: "Recommended Action",
    rows: "Rows",
    highPriorityRows: "High Priority Rows",
    mediumPriorityRows: "Medium Priority Rows",
    avgAdjustmentPct: "Avg Adjustment Pct",
    signalReason: "Signal Reason",
    guardrailDecision: "Guardrail Decision",
    guardrailAction: "Guardrail Action",
    latestRunId: "Latest Run ID",
    latestRunTime: "Latest Run Time",
    previousRunId: "Previous Run ID",
    previousRunTime: "Previous Run Time",
    totalDeltaQty: "Total Delta Qty",
    totalDeltaPct: "Total Delta Pct",
    previousForecast: "Previous Forecast",
    plannerDecision: "Planner Decision",
    candidate: "Candidate",
    current: "Current",
    delta: "Delta",
    recentRunRate: "Recent Run-rate",
    seasonalRecent: "Seasonal / Recent",
    forecast: "Forecast",
    lower: "Lower",
    upper: "Upper",
    confidence: "Confidence",
    candidateV3: "Candidate V3",
    recommended: "Recommended",
    alignment: "Alignment",
    v3Delta: "V3 Delta",
    v3DeltaPct: "V3 Delta %",
    v3Rule: "V3 Rule",
    skus: "SKUs",
    previous: "Previous",
    largestSku: "Largest SKU",
    location: "Location",
    control: "Control",
    detail: "Detail",
    generated: "Generated",
    runId: "Run ID",
    latestWape: "Latest WAPE",
    latestBias: "Latest Bias",
    rollingWins: "Rolling Wins",
    rollingGap: "Rolling Gap",
    actual: "Actual",
    absError: "Abs Error",
    signal: "Signal",
    highPriority: "High Priority",
    recent4w: "Recent 4W",
    metric: "Metric",
    metricMeaning: "Metric Meaning",
    verdict: "Verdict",
    candidateWape: "Candidate WAPE",
    improvement: "Improvement",
    adjustment: "Adjustment",
    gain: "Gain",
    avgAdj: "Avg Adj.",
    promo: "Promo",
    stockout: "Stockout",
    invSales: "Inv/Sales",
    option: "Option",
    whenToUse: "When To Use",
    recommendation: "Recommendation",
    bestWape: "Best WAPE",
    aiForecast: "AI Forecast",
    baselineGap: "Baseline Gap",
    gap: "Gap",
    v4Gap: "V4 Gap",
    v5Gap: "V5 Gap",
    outcome: "Outcome",
    diagnosis: "Diagnosis",
    v4Diagnosis: "V4 Diagnosis",
    v5Diagnosis: "V5 Diagnosis",
    chosenModel: "Chosen Model",
    recentCv4: "Recent CV4",
    window: "Window",
    bestModel: "Best Model",
    ruleWape: "Rule WAPE",
    bestSingleWape: "Best Single WAPE",
    ruleBias: "Rule Bias",
    v4VsCurrent: "V4 vs Current",
    v5VsCurrent: "V5 vs Current",
    actualErp: "Actual ERPQuantity",
    forecastErp: "Forecast ERPQuantity",
  },
  txt: {
    keepCurrentBaseline: "Keep current baseline",
    keepCurrentOutput: "Keep current output",
    ruleHybrid: "Rule Hybrid",
    latestRunGenerated: "Latest run {{id}} generated {{time}}.",
  },
  csr: {
    largePct: "Large percentage gap",
    largeQty: "Large quantity impact",
    moderatePct: "Moderate percentage gap",
    moderateQty: "Moderate quantity impact",
  },
  cr: {
    largeGap: "Large current-vs-candidate gap",
    priorAbove: "Prior-year demand far above recent run-rate",
    priorBelow: "Prior-year demand far below recent run-rate",
    needsReview: "Needs business review",
    moderateGap: "Moderate gap versus current forecast",
    broadlyAligned: "Candidate broadly aligned",
  },
  conf: {
    high: "Stable history at branch/channel and SKU level",
    medium: "Usable history, review for SKU or channel shifts",
    low: "Sparse SKU history or allocated demand; review manually",
    bucket: "Forecast confidence bucket",
    title: "Forecast Confidence",
    note: "Rows and quantity share",
  },
  sig: {
    promo: "Promotion / Price",
    promoNote: "Discount, point, price-drop, or promo-lag demand risk",
    promoMetric: "Avg signal share",
    stock: "Inventory / Stockout",
    stockNote: "Potential lost demand or low-stock constrained demand",
    stockMetric: "Avg stockout share",
    holiday: "Holiday",
    holidayNote: "Recent sales may include holiday lift or holiday dip",
    holidayMetric: "Signal rows",
    noSales: "No Recent Sales",
    noSalesNote: "Forecast exists but latest 4 weeks have no demand",
    noSalesMetric: "Forecast at risk",
  },
  fg: {
    needsDataDetail: "Feature model needs backtest result before it can be used for planning review.",
    keepAction: "Use feature signals as review queue only",
    keepDetail: "Feature candidate does not beat current baseline at total level in the latest backtest.",
    pilotAction: "Pilot feature model on filtered branch/channel review",
    pilotDetail: "Feature candidate improves WAPE and high-priority risk share is controlled.",
    reviewAction: "Use as diagnostic before replacing output",
    reviewDetail: "Feature candidate has some accuracy gain, but risk queue still needs monitoring.",
    holdAction: "Do not promote feature model yet",
    holdDetail: "Feature candidate is not stable enough versus current baseline.",
    accuracy: "Accuracy impact",
    accuracyDetail: "Negative means feature model has lower WAPE than current baseline.",
    risk: "Risk pressure",
    riskValue: "{{n}} high",
    riskDetail: "{{share}} of AI review queue is high priority.",
    next: "Next action",
    nextDetail: "{{n}} feature signal groups detected in current filter.",
    title: "Feature Guardrail Recommendation",
  },
  fa: {
    watchReason: "Feature signal is not decisive enough to change planning output.",
    pilotReason: "Feature model improves WAPE enough on meaningful volume.",
    keepReason: "Feature model worsens WAPE; keep current baseline for this branch/channel.",
    lowReason: "Volume is too small for a stable model decision.",
  },
  p7: {
    backtest: "Feature model backtest",
    backtestDetail: "{{n}} weeks available; current vs feature model comparison is loaded.",
    signals: "External signals connected",
    signalsDetail: "{{n}} signal groups detected from holiday, inventory, promotion, price, or no-sales data.",
    queue: "Risk queue actionable",
    queueDetail: "{{risks}} SKU risks grouped into {{buckets}} exception buckets.",
    pilot: "Pilot decision output",
    pilotDetail: "{{pilot}} pilot candidate groups; {{keep}} keep-baseline groups.",
    guardrail: "Guardrail status",
    guardrailDetail: "{{label}}: {{action}}.",
    pilotReady: "Pilot ready",
    pilotReadyDetail: "Phase 7 UI is ready for a controlled pilot review. Keep production output unchanged until Phase 8 workflow is defined.",
    mvpReady: "MVP ready",
    mvpReadyDetail: "Phase 7 UI is useful for planning review. Remaining work is mainly validation depth and production workflow.",
    needsWork: "Needs work",
    needsWorkDetail: "Phase 7 still needs more feature evidence before it can drive planning decisions.",
    readiness: "Phase 7 Readiness",
    checksPassed: "{{done}} / {{total}} checks passed",
  },
  p8: {
    official: "Official output",
    officialDetail: "Production forecast remains Hierarchical recent12 SKU mix. AI/feature output does not replace it automatically.",
    aiUsage: "AI usage",
    aiUsageValue: "Pilot / review layer",
    aiUsageDetail: "Use AI only for branch/channel groups marked Pilot candidate in Actionable AI Output.",
    weekly: "Weekly refresh",
    weeklyValue: "Manual Monday refresh",
    weeklyDetail: "Owner: you. Run Refresh Data after weekly sales data update is completed.",
    failure: "Refresh failure",
    failureValue: "Show error, keep baseline",
    failureDetail: "If refresh fails or times out, dashboard shows error and we investigate before using new output.",
    title: "Phase 8 Production Workflow Draft",
    note: "Policy locked before production merge",
    finalLogic: "Final output logic",
    finalLogicValue: "Baseline is official; AI is pilot/review only",
    bestOption: "Best option: keep the current baseline as production forecast, use AI/feature output only for controlled branch/channel pilots, and keep baseline whenever AI output is abnormal or not reviewed.",
    runGuardrail: "Run Guardrail Decision",
    branchMovers: "Branch movers",
    weeklyChecklist: "Weekly Operating Checklist",
    weeklyChecklistNote: "Use this after every Monday Refresh Data",
    holdDetail: "Hold Reason Detail",
    holdReviewThese: "Review these before using the new run",
    guardrailDiagnostics: "Guardrail diagnostics",
    exportHold: "Export Hold CSV",
    noReviewMovers: "No review movers",
    allInside: "All SKU movers are inside guardrail.",
    reviewGroups: "Review Groups",
    byBranchChannel: "By branch / channel",
    noHoldGroups: "No hold groups in current filter.",
    topSkuActions: "Top SKU Actions",
    top12: "Top 12 movers needing attention",
    noSkuAction: "No SKU action needed.",
    controls: "Production Controls",
    controlsReady: "{{ready}} / {{total}} ready or policy-ready",
    runHistory: "Forecast Run History",
    runHistoryCompared: "Latest run compared with previous run",
    runHistoryWaiting: "Waiting for at least two runs",
    recentRuns: "Recent Forecast Runs",
    latest5: "Latest 5",
    noRunHistory: "No run history loaded.",
    bcMovement: "Branch / Channel Movement",
    top10Abs: "Top 10 by absolute delta",
    noPrevMovement: "No previous-run movement yet.",
    skuMovement: "SKU Movement vs Previous Run",
    top15Abs: "Top 15 by absolute delta",
    noSkuMovement: "No SKU run movement yet.",
  },
  mv: {
    newReason: "Forecast appears in latest run but was zero in previous run.",
    droppedReason: "Forecast dropped to zero versus previous run.",
    manualReason: "SKU movement breaches Phase 8 severe mover guardrail.",
    watchReason: "SKU changed meaningfully but below severe guardrail.",
    acceptReason: "Movement is within SKU guardrail.",
  },
  hr: {
    total: "Total movement",
    totalDetail: "{{pct}} vs previous run; {{qty}} qty.",
    branch: "Branch/channel movers",
    branchDetail: "{{n}} groups breach 30% or 5,000 qty movement.",
    sku: "SKU movers",
    skuDetail: "{{n}} SKUs breach 50% or 1,000 qty movement.",
  },
  g8: {
    noRunAction: "Do not use output",
    noRunDetail: "Dashboard cannot find a forecast run id. Refresh data or check backend before using forecast output.",
    firstAction: "Use baseline only",
    firstDetail: "This is the first stored forecast run, so week-over-week abnormal checks will activate after the next refresh.",
    holdAction: "Keep previous baseline",
    holdDetail: "Run changed {{pct}} total; {{branch}} branch/channel and {{sku}} SKU movers breach guardrail.",
    reviewAction: "Review before export",
    reviewDetail: "Run changed {{pct}} total; review top movers before sharing with planner.",
    passAction: "OK for planner review",
    passDetail: "Run-to-run movement is within guardrail. Baseline remains official; AI stays review layer.",
  },
  rh: {
    latest: "Latest run",
    previous: "Previous run",
    totalDelta: "Total delta",
    vsPrevious: "vs previous run",
    skuDelta: "SKU count delta",
    skuDeltaDetail: "{{current}} current / {{previous}} previous",
  },
  pc: {
    export: "Export CSV review",
    exportDetail: "Planning output, recommended output, AI review queue, exception buckets, and action output can be exported.",
    runId: "Run ID / generated time",
    runIdNone: "No forecast run id loaded yet.",
    compare: "Compare vs previous run",
    compareDetail: "Latest vs previous run delta is {{pct}} ({{qty}} units).",
    compareNone: "Need at least two forecast refresh runs to compare week-over-week changes.",
    rollback: "Rollback / guardrail",
  },
  wc: {
    sales: "Sales data refreshed",
    salesDetail: "Actuals through {{date}}.",
    salesNone: "No latest weekly mart date loaded.",
    run: "Forecast run generated",
    runNone: "Refresh Data has not produced a run id.",
    prev: "Previous run archived",
    prevDetail: "Previous run {{id}} is available for comparison.",
    prevNone: "Needs one more weekly refresh to activate previous-run comparison.",
    guardrail: "Guardrail checked",
    export: "Planner export",
    exportPass: "Planning CSV can be exported after final spot check.",
    exportOther: "Export Hold CSV first, then decide whether planning CSV should be shared.",
    action: "This week action",
    actionHold: "Keep baseline/previous output until top movers are reviewed.",
    actionReview: "Review branch and SKU movers before sharing.",
    actionProceed: "Proceed with normal weekly export flow.",
  },
  mr: {
    currentUse: "Default output while candidate signals are still mixed.",
    currentAction: "Keep as production baseline",
    v4Use: "Best for latest short-term signal when recent demand contradicts seasonal history.",
    v4Action: "Use as short-term challenger",
    v5Use: "Best for seasonality-first forecast with protection against extreme seasonal jumps.",
    v5Action: "Keep as next promotion candidate",
  },
  rp: {
    v5: "Promote V5 candidate review",
    v5Detail: "V5 wins enough rolling windows and improves current WAPE across validation windows.",
    v4: "Keep V4 as latest-window challenger",
    v4Detail: "V4 is strongest on the latest backtest, but rolling validation is not stable enough to replace output.",
    v5Review: "Review V5 for promotion",
    v5ReviewDetail: "V5 improves current in the latest backtest and has a clearer seasonality-first policy.",
    keepDetail: "Candidate models are informative, but current output is still the safest production baseline.",
  },
  vs: {
    needsDataDetail: "Hybrid validation needs model comparison, rule hybrid, and upper-bound backtest rows.",
    stableDetail: "Rule Hybrid wins most rolling windows and is at least as good as the best single model on average.",
    notStableDetail: "Rule Hybrid wins the latest window but does not beat the best single model consistently across earlier rolling windows.",
    overfitDetail: "Rule Hybrid matches the best-per-week upper bound on a short window. Validate on more rolling windows before using it for forecast output.",
    promisingDetail: "Rule Hybrid improves meaningfully over the current recommended single model, but still needs rolling-window validation.",
    watchDetail: "Rule Hybrid does not yet show enough stable improvement to replace the current forecast model.",
  },
  pp: {
    waitBacktest: "Wait for backtest",
    needsDataReason: "Model needs latest backtest and rolling validation before promotion review.",
    preparePilot: "Prepare controlled pilot",
    promoteReason: "{{model}} wins most rolling windows and improves WAPE versus current.",
    reviewImpact: "Review branch/SKU impact",
    pilotReason: "{{model}} has a useful accuracy signal but is not stable enough for automatic promotion.",
    keepDiagnostic: "Keep as diagnostic",
    watchReason: "{{model}} does not yet beat current output consistently enough.",
    waitRolling: "Wait for rolling validation",
    ruleNeedsData: "Rule Hybrid needs rolling validation before it can become a production candidate.",
    rulePromote: "Rule Hybrid is stable across rolling windows and can be compared against current output in a controlled pilot.",
    extendWindow: "Extend validation window",
    rulePilot: "Rule Hybrid improves recent validation but still needs more evidence against overfit risk.",
    keepOut: "Keep out of output",
    currentReason: "Stable baseline used for current dashboard output while candidate models are reviewed.",
  },
  md: {
    hier: "Forecast Branch/Channel first, then allocates to SKU by recent 12-week mix.",
    naive1: "Uses the previous week actual as the forecast.",
    ma4: "Uses the average of the previous 4 actual weeks.",
    ma12: "Uses the average of the previous 12 actual weeks.",
    seasonal: "Uses the same ISO week from the previous year when available.",
    v3: "Data-signal adjusted seasonal candidate, dampened toward current/recent demand.",
    v4: "Signal blend candidate using seasonal, current output, and last-week demand.",
    v5: "Seasonality-first candidate with guardrails for large seasonal/current gaps.",
    rule: "Rule-based hybrid model using only pre-forecast signals.",
    benchmark: "Benchmark forecast model.",
  },
  pd: {
    seasonal: "Seasonality-first baseline; keep Hierarchical for SKU allocation review.",
    v4: "V4 candidate is strongest in rolling validation; review bias and SKU allocation before promote.",
    v5: "V5 seasonality-first guardrail is strongest in rolling validation; review SKU impact before promote.",
    hybrid: "Hybrid candidate; validate more before using as forecast output.",
    keep: "Keep current model direction until rolling validation is stronger.",
  },
  ck: {
    rolling: "Rolling validation supports seasonality",
    rollingSeasonal: "Seasonal model is current recommendation across {{n}} validation windows.",
    rollingOther: "Current recommendation is {{model}}.",
    branch: "Branch/channel impact is controlled",
    branchDetail: "{{n}} branch/channel groups still need review.",
    branchNone: "No branch/channel group is flagged as Review.",
    highGroups: "High priority SKU groups are reviewed",
    highGroupsDetail: "{{high}} high and {{medium}} medium priority group(s) remain.",
    highGroupsNone: "No high priority group remains; {{medium}} medium group(s) still need routine review.",
    skuList: "SKU impact list is review-ready",
    skuListDetail: "{{n}} SKU movers are flagged Review; export CSV for business confirmation.",
    skuListNone: "No SKU mover is flagged Review.",
  },
  cd: {
    needsDataDetail: "Candidate decision needs rolling validation, branch diagnostics, and SKU impact rows.",
    promote: "Promote candidate",
    promoteDetail: "Seasonality wins rolling validation and candidate impact looks controlled.",
    reviewBefore: "Review before promote",
    reviewBeforeDetail: "Seasonality wins rolling validation, but branch/SKU impact has large movers that need business review.",
    doNot: "Do not promote yet",
    doNotDetail: "Rolling validation does not support replacing the current forecast output with Candidate V2.",
  },
  gov: {
    needsDataDetail: "Recommended output needs detail rows before it can be governed.",
    reviewOnly: "Review layer only",
    reviewOnlyDetail: "Large SKU impact or model disagreement remains. Use CSV review before changing production output.",
    pilot: "Pilot with control",
    pilotDetail: "Recommended output is usable for planning review, but still needs branch/channel owner checks.",
    ready: "Ready for promotion review",
    readyDetail: "Most rows keep current or align with branch/channel model recommendation; remaining deltas are controlled.",
  },
  pol: {
    guardrail: "Production guardrail",
    pressure: "Review pressure",
    pressureDetail: "{{share}} of rows are Review; {{watch}} rows are Watch.",
    agreement: "Model agreement",
    agreementValue: "{{n}} aligned",
    agreementDetail: "{{n}} rows disagree with branch/channel model recommendation.",
    volume: "Volume impact",
    volumeDetail: "{{share}} of rows keep the current production baseline.",
  },
  lvd: {
    total: "Overall demand accuracy. Best signal for top-line planning.",
    branch_channel: "Accuracy after splitting demand by branch and sales channel.",
    sku: "SKU allocation accuracy. Higher error is expected for sparse or irregular SKUs.",
    other: "Forecast accuracy at this level.",
  },
  btn: {
    exportCsv: "Export CSV",
  },
  fc: {
    actualForecast: "Actual + Forecast",
    actualForecastNote: "Last {{actual}} actual weeks, next {{forecast}} forecast weeks",
  },
  cand: {
    title: "Forecast V2 Candidate",
    note: "Seasonality-first, not replacing current output",
    candidateV2: "Candidate V2",
    diagnostics: "Diagnostics",
    diagCounts: "{{review}} Review / {{watch}} Watch",
    highSkuImpact: "High SKU impact",
    promoteChecklist: "Promote Checklist",
    readyToPromote: "Ready to promote",
    notReady: "Not ready yet",
    passed: "{{done}} / {{total}} passed",
    candidateTotal: "Candidate Total",
    currentTotal: "Current Total",
    hierOutput: "Hierarchical output",
    difference: "Difference",
    policyText: "Candidate V2 follows the rolling policy direction: use prior-year same ISO week demand as a seasonality-first baseline, then review SKU allocation separately before replacing the current forecast output.",
  },
  bc: {
    top10: "Top 10",
  },
  sku: {
    title: "SKU Forecast",
    rowsOf: "{{shown}} of {{total}} rows",
  },
  rec: {
    title: "Recommended Forecast Output",
    note: "Review layer, not replacing current output",
    planningCsv: "Planning CSV",
    productionBaseline: "production baseline",
    recommendedTotal: "Recommended Total",
    reviewOutput: "review output",
    reviewWatch: "Review / Watch",
    keepCurrentN: "{{n}} keep current",
    alignedReview: "aligned / review",
    p1p2: "P1 / P2 queue",
    decisionPolicy: "Decision Policy",
    unchanged: "Current production output is unchanged until this review layer is approved.",
    allDirection: "All Direction",
    allAlignment: "All Alignment",
    filterGroup: "Branch / Channel: {{value}} x",
    filterReason: "Reason: {{value}} x",
    clearDrill: "Clear drilldown",
    queue: "Review Priority Queue",
    queueNote: "Top {{n}} rows in current filter",
    modelAligned: "Model aligned",
    modelReview: "Model review",
    byGroup: "Review Summary by Branch / Channel",
    groupsN: "{{n}} groups in current filter",
    increase: "Increase",
    decrease: "Decrease",
    recVsCurrent: "Recommended {{rec}} vs current {{cur}}",
    byReason: "Review Summary by Reason",
    reasonsN: "{{n}} reasons in current filter",
  },
  csku: {
    title: "Candidate SKU Impact",
    note: "Top {{n}} movers by absolute delta",
    seasonalityBaseline: "seasonality baseline",
    dataSignalAdjusted: "data-signal adjusted",
    v3vsCurrent: "V3 vs Current",
    v3vsV2: "V3 vs V2",
    groupsWithReview: "{{n}} groups with Review or Watch SKUs",
    cardText: "V2 {{v2}} / V3 {{v3}} vs current {{cur}}",
    sigRecent12: "Recent 12W: {{n}}",
    sigActiveWeeks: "Recent active weeks: {{n}}",
    sigPriorYear: "Prior-year same horizon: {{n}}",
  },
  bt: {
    summary: "Backtest Summary",
    lastWeeks: "Last {{n}} actual weeks",
    modelUsed: "Model used",
    wapeText: "Total absolute forecast error divided by total actual quantity. Lower is better.",
    biasText: "Forecast minus actual as a share of actual. Negative means under-forecast; positive means over-forecast.",
    level: "Level",
    levelText: "Total shows planning accuracy, Branch Channel shows allocation accuracy, SKU shows item-level allocation risk.",
  },
  mc: {
    title: "Model Comparison",
    note: "Recommendation uses rolling validation when available",
    exportModels: "Export Models",
    recommendedModel: "Recommended model",
    selectedRolling: "Selected from rolling validation: {{model}} wins {{wins}} of {{windows}} windows. {{policy}}",
    selectedWape: "Selected from WAPE first, then bias when WAPE is within 3 percentage points. Current WAPE {{wape}}, bias {{bias}}.",
    currentOutput: "Current forecast output",
    rollingWinner: "Rolling winner",
    hybridStatus: "Hybrid status",
    nextDirection: "Next direction",
    latestV4Gap: "Latest V4 gap",
    v4MinusCurrent: "V4 WAPE minus current",
    latestV5Gap: "Latest V5 gap",
    v5MinusCurrent: "V5 WAPE minus current",
    promoCandidate: "Promotion Candidate",
    path: "{{label}} path",
    promoNote: "Promotion means controlled pilot first, not automatic production replacement.",
    lowVolumeTitle: "Actual quantity is small, so WAPE can look extreme. Review quantity impact before using this recommendation.",
  },
  feat: {
    candidate: "Feature Candidate",
    candidateNote: "Total-level backtest using holiday, inventory, and promotion-lag signals.",
    bestWape: "Best feature WAPE",
    gapVsCurrent: "Gap vs current",
    backtestWeeks: "Backtest weeks",
    modelSummary: "Model Summary",
    modelSummaryNote: "Total-level accuracy by candidate model",
    signalCoverage: "Feature Signal Coverage",
    signalCoverageNote: "Holiday, inventory, and promotion/price risks in current filter",
    highPriorityN: "{{n}} high priority",
    trend: "Feature Backtest Trend",
    trendNote: "Current baseline vs {{model}} by week",
    weekLabel: "Week {{label}}",
    adjustment: "Feature Adjustment",
    verdict: "Weekly Feature Verdict",
    verdictNote: "Candidate WAPE improvement versus current baseline",
    candLower: "candidate lower WAPE",
    candHigher: "candidate higher WAPE",
    avgImprovement: "Avg Improvement",
    currentMinusCand: "current WAPE minus candidate WAPE",
    bestWorst: "Best / Worst Week",
    weeklyDetail: "Weekly Backtest Detail",
    weeklyDetailNote: "Actual vs feature forecast by week",
    actionable: "Actionable AI Output",
    actionableNote: "Recommended usage by branch/channel; this does not replace production output automatically",
    pilotCandidates: "Pilot Candidates",
    pilotCandNote: "feature improves WAPE on meaningful volume",
    keepBaseline: "Keep Baseline",
    worsens: "feature worsens WAPE",
    watchLow: "Watch / Low Volume",
    insufficient: "insufficient signal for output change",
    weightedGain: "Pilot Weighted Gain",
    weightedGainNote: "WAPE-point weighted by actual quantity",
    topPilots: "Top Pilot Candidates",
    bcGroupsN: "{{n}} branch/channel groups",
    bcImpact: "Branch / Channel Feature Impact",
    bcImpactNote: "Where feature adjustment helps or hurts",
    reviewQueue: "AI Review Queue",
    reviewQueueNote: "{{shown}} of {{total}} SKU risks",
    totalRisks: "Total Risks",
    mediumPriority: "Medium Priority",
    topRisk: "Top Risk",
    allRisk: "All Risk",
    exceptions: "AI Exception Buckets",
    exceptionsNote: "Forecast quantity grouped by risk and recommended action",
    exceptionBuckets: "Exception Buckets",
    skuRowsN: "{{n}} SKU rows",
    forecastAtRisk: "Forecast at Risk",
    currentFilter: "current filter",
    highPriorityForecast: "High Priority Forecast",
    bucketHasHigh: "bucket contains high rows",
    topException: "Top Exception",
    forecastQtyN: "{{n}} forecast qty",
    riskByBc: "Risk by Branch / Channel",
    top10Filter: "Top 10 in current filter",
  },
  wt: {
    title: "Weekly Model Tracking",
    note: "Use this after each weekly data refresh to decide whether baseline, AI, or recent-run-rate models are improving.",
    export: "Export Tracking",
    latestWeek: "Latest Week",
    actualQtyN: "{{n}} actual qty",
    noRows: "No tracking rows",
    baselineAvg: "Baseline Avg WAPE",
    lastTracked: "last {{n}} tracked weeks",
    bestAvg: "Best Model Avg WAPE",
    bestAvgNote: "weekly winner after actuals are known",
    topWinner: "Most Frequent Winner",
    winsN: "{{n}} wins in recent window",
    empty: "No weekly tracking rows available",
  },
  wmc: {
    title: "Weekly Model Comparison",
    note: "Winner and forecast by week",
    export: "Export Weekly",
  },
  diag: {
    title: "Candidate Diagnostics",
    note: "V3, V4, and V5 compared with current forecast output",
    within: "within 0.5pt WAPE",
    v3Worse: "V3 worse",
    avgGap: "Avg WAPE gap",
    v3Minus: "V3 minus current",
    v4Beats: "V4 beats current",
    v4Worse: "V4 worse",
    v4AvgGap: "V4 avg gap",
    v4Minus: "V4 minus current",
    v5Beats: "V5 beats current",
    v5Worse: "V5 worse",
    v5AvgGap: "V5 avg gap",
    v5Minus: "V5 minus current",
  },
  hyb: {
    title: "Hybrid Simulation",
    note: "Best model per backtest week",
    wape: "Hybrid WAPE",
    better: "{{n}} pts better than recommended",
    check: "Potential improvement check",
    text: "This is an upper-bound simulation using the best historical model each week. It helps estimate whether a real hybrid rule is worth building.",
    winningWeeks: "{{n}} winning weeks",
  },
  rule: {
    title: "Rule Hybrid v2",
    note: "Uses only information available before the forecast week",
    validationStatus: "Validation Status",
    upperGap: "Upper-bound gap",
    vsRecommended: "{{n}} pts vs recommended",
    check: "Practical hybrid check",
    modelsUsed: "{{n}} models used",
    text: "Rule v2 uses agreement between candidate models: Naive when it is close to Hierarchical, Seasonal when it agrees with MA12 or detects a prior-week spike, otherwise MA12 or Hierarchical fallback.",
  },
  roll: {
    title: "Rolling Validation",
    note: "V4 wins {{v4}} / V5 wins {{v5}} / Rule wins {{rule}} of {{n}} windows",
    v4Wins: "V4 rolling wins",
    v5Wins: "V5 rolling wins",
    ruleWins: "Rule rolling wins",
    ruleAvgGap: "Rule avg gap",
    ruleMinusBest: "Rule minus best single",
  },
  acc: {
    title: "Accuracy by Level",
    note: "Lower WAPE is better",
    bias: "Bias {{v}}",
  },
  wavf: {
    title: "Weekly Actual vs Forecast",
    note: "Bars: Actual / Forecast, line: WAPE",
  },
  wbd: {
    note: "Actual vs forecast by model",
  },
};

const ko = {
  top: {
    title: "수요 예측",
    actualsThrough: "실적 기준일 {{date}}",
    subtitle: "주간 ERPQuantity 예측",
    env: "스테이징 API",
    refreshDataTitle: "예측 마트, 예측 결과, 백테스트를 새로 고칩니다",
    refreshData: "데이터 새로 고침",
    refresh: "새로 고침",
    loading: "불러오는 중",
  },
  f: {
    horizon: "예측 기간",
    weeksN: "{{n}}주",
    noMatches: "일치 항목 없음",
    clear: "초기화",
  },
  msg: {
    refreshRunning: "데이터 새로 고침 실행 중: 마트, 예측, 백테스트. 2~3분 정도 걸릴 수 있습니다.",
    refreshed: "예측 데이터 새로 고침 완료: {{steps}} ({{time}})",
    done: "완료",
    cannotParse: "{{endpoint}} 응답을 읽을 수 없습니다",
    cannotLoad: "{{endpoint}}을(를) 불러올 수 없습니다",
  },
  stat: {
    w1: "W+1 예측",
    w4: "4주 예측",
    weeksAvailable: "{{n}}주 사용 가능",
    total: "총 예측",
    skus: "SKU {{n}}개",
    wape: "백테스트 WAPE",
    totalLevel: "전체 수준",
  },
  tab: {
    forecast: "예측",
    sku: "SKU 상세",
    backtest: "백테스트",
  },
  level: {
    total: "전체",
    branch_channel: "지사 / 채널",
    sku: "SKU",
  },
  u: {
    pts: "{{n}}%p",
    qty: "수량 {{n}}",
    rows: "{{n}}행",
    weeks: "주",
    ofWindows: "{{n}}개 구간 중",
    skuMovers: "SKU 변동 항목",
  },
  v: {
    all: "전체",
    review: "검토",
    watch: "주시",
    ok: "정상",
    keepCurrent: "현행 유지",
    increaseForecast: "예측 상향",
    decreaseForecast: "예측 하향",
    aligned: "일치",
    high: "높음",
    medium: "보통",
    low: "낮음",
    helps: "개선",
    hurts: "악화",
    neutral: "중립",
    unknown: "알 수 없음",
    unknownReason: "사유 불명",
    unknownRisk: "위험 불명",
    reviewManually: "수동 검토",
    needsData: "데이터 필요",
    keepBaseline: "기준선 유지",
    pilotCandidate: "파일럿 후보",
    reviewCandidate: "검토 후보",
    hold: "보류",
    lowVolumeWatch: "소량 주시",
    reviewNewDemand: "신규 수요 검토",
    reviewDroppedDemand: "소멸 수요 검토",
    manualReview: "수동 검토",
    acceptMovement: "변동 수용",
    pass: "통과",
    noRunLoaded: "불러온 실행 없음",
    ready: "준비됨",
    missing: "누락",
    done: "완료",
    waiting: "대기",
    reviewFirst: "먼저 검토",
    proceed: "진행",
    policyReady: "정책 준비됨",
    rollingStable: "롤링 안정",
    notStableYet: "아직 불안정",
    highOverfitRisk: "과적합 위험 높음",
    promising: "유망",
    promoteReview: "승격 검토",
    watchOnly: "주시만",
    productionBaseline: "운영 기준선",
    ruleWins: "규칙 우세",
    close: "근접",
    featureHelps: "피처 개선",
    featureHurts: "피처 악화",
    lowVolume: "소량",
    noFeatureSignal: "피처 신호 없음",
    strongCandidate: "유력 후보",
    v3Beats: "V3가 현행보다 우수",
    v3Worse: "V3가 현행보다 열위",
  },
  col: {
    productName: "제품명",
    branch: "지사",
    channel: "채널",
    collection: "컬렉션",
    category: "카테고리",
    candidateQty: "후보 수량",
    candidateV3Qty: "후보 V3 수량",
    currentQty: "현행 수량",
    deltaQty: "차이 수량",
    deltaPctTxt: "차이율",
    v3DeltaVsCurrentQty: "V3 현행 대비 차이 수량",
    v3DeltaVsCurrentPct: "V3 현행 대비 차이율",
    v3DeltaVsV2Qty: "V3 V2 대비 차이 수량",
    status: "상태",
    reason: "사유",
    dataSignal: "데이터 신호",
    candidateV3Rule: "후보 V3 규칙",
    recent12Qty: "최근 12주 수량",
    recent4Qty: "최근 4주 수량",
    recent12ActiveWeeks: "최근 12주 중 판매 주수",
    priorYearHorizonQty: "전년 동기간 수량",
    currentForecast: "현행 예측",
    candidateV3Forecast: "후보 V3 예측",
    recommendedForecast: "권장 예측",
    deltaPct: "차이 %",
    recommendedModel: "권장 모델",
    bcRecommendedModel: "지사·채널 권장 모델",
    modelAlignment: "모델 일치",
    reviewPriority: "검토 우선순위",
    bcModelStatus: "지사·채널 모델 상태",
    bcModelImprovement: "지사·채널 모델 개선폭",
    direction: "방향",
    recent12Actual: "최근 12주 실적",
    recent4Actual: "최근 4주 실적",
    priorYearHorizon: "전년 동기간",
    actualsThrough: "실적 기준일",
    horizonWeeks: "예측 기간(주)",
    currentProductionForecast: "현행 운영 예측",
    recommendedPlanningForecast: "권장 계획 예측",
    plannerOverride: "플래너 조정 예측",
    finalApproved: "최종 승인 예측",
    reviewStatus: "검토 상태",
    bcModel: "지사·채널 모델",
    bcModelShort: "지사·채널 모델",
    plannerComment: "플래너 의견",
    owner: "담당자",
    decision: "결정",
    year: "연도",
    week: "주",
    actualQty: "실적 수량",
    baselineForecast: "기준선 예측",
    baselineWape: "기준선 WAPE",
    baselineBias: "기준선 편향",
    featureForecast: "피처 예측",
    featureWape: "피처 WAPE",
    featureBias: "피처 편향",
    ma4Forecast: "MA4 예측",
    ma4Bias: "MA4 편향",
    ma12Forecast: "MA12 예측",
    ma12Bias: "MA12 편향",
    winner: "우세 모델",
    winnerForecast: "우세 모델 예측",
    winnerWape: "우세 모델 WAPE",
    baselineGapVsWinnerPts: "기준선과 우세 모델 차이(%p)",
    section: "구분",
    model: "모델",
    forecastQty: "예측 수량",
    bias: "편향",
    currentWape: "현행 WAPE",
    seasonalWape: "계절 WAPE",
    improvementVsCurrent: "현행 대비 개선",
    hierForecast: "계층형 예측",
    hierWape: "계층형 WAPE",
    seasonalForecast: "계절 예측",
    v3Forecast: "V3 예측",
    v4Forecast: "V4 예측",
    v5Forecast: "V5 예측",
    naiveForecast: "단순 예측",
    naiveWape: "단순 예측 WAPE",
    priority: "우선순위",
    risk: "위험",
    action: "조치",
    lowerQty: "하한 수량",
    upperQty: "상한 수량",
    promoShare: "프로모션 수량 비중",
    highDiscountShare: "고할인 수량 비중",
    priceDropShare: "가격 인하 수량 비중",
    stockoutShare: "품절 수량 비중",
    invToSales: "재고 대비 판매 비율",
    recommendedAction: "권장 조치",
    rows: "행 수",
    highPriorityRows: "높은 우선순위 행",
    mediumPriorityRows: "보통 우선순위 행",
    avgAdjustmentPct: "평균 조정률",
    signalReason: "신호 사유",
    guardrailDecision: "가드레일 판정",
    guardrailAction: "가드레일 조치",
    latestRunId: "최신 실행 ID",
    latestRunTime: "최신 실행 시각",
    previousRunId: "이전 실행 ID",
    previousRunTime: "이전 실행 시각",
    totalDeltaQty: "총 차이 수량",
    totalDeltaPct: "총 차이율",
    previousForecast: "이전 예측",
    plannerDecision: "플래너 결정",
    candidate: "후보",
    current: "현행",
    delta: "차이",
    recentRunRate: "최근 판매 속도",
    seasonalRecent: "계절 / 최근",
    forecast: "예측",
    lower: "하한",
    upper: "상한",
    confidence: "신뢰도",
    candidateV3: "후보 V3",
    recommended: "권장",
    alignment: "일치",
    v3Delta: "V3 차이",
    v3DeltaPct: "V3 차이 %",
    v3Rule: "V3 규칙",
    skus: "SKU 수",
    previous: "이전",
    largestSku: "최대 변동 SKU",
    location: "위치",
    control: "통제 항목",
    detail: "상세",
    generated: "생성 시각",
    runId: "실행 ID",
    latestWape: "최신 WAPE",
    latestBias: "최신 편향",
    rollingWins: "롤링 우세 횟수",
    rollingGap: "롤링 차이",
    actual: "실적",
    absError: "절대 오차",
    signal: "신호",
    highPriority: "높은 우선순위",
    recent4w: "최근 4주",
    metric: "지표",
    metricMeaning: "지표 의미",
    verdict: "판정",
    candidateWape: "후보 WAPE",
    improvement: "개선폭",
    adjustment: "조정",
    gain: "개선폭",
    avgAdj: "평균 조정",
    promo: "프로모션",
    stockout: "품절",
    invSales: "재고/판매",
    option: "옵션",
    whenToUse: "사용 시점",
    recommendation: "권장",
    bestWape: "최저 WAPE",
    aiForecast: "AI 예측",
    baselineGap: "기준선 차이",
    gap: "차이",
    v4Gap: "V4 차이",
    v5Gap: "V5 차이",
    outcome: "결과",
    diagnosis: "진단",
    v4Diagnosis: "V4 진단",
    v5Diagnosis: "V5 진단",
    chosenModel: "선택 모델",
    recentCv4: "최근 CV4",
    window: "구간",
    bestModel: "최적 모델",
    ruleWape: "규칙 WAPE",
    bestSingleWape: "최적 단일 모델 WAPE",
    ruleBias: "규칙 편향",
    v4VsCurrent: "V4 대 현행",
    v5VsCurrent: "V5 대 현행",
    actualErp: "실적 ERPQuantity",
    forecastErp: "예측 ERPQuantity",
  },
  txt: {
    keepCurrentBaseline: "현행 기준선 유지",
    keepCurrentOutput: "현행 결과 유지",
    ruleHybrid: "규칙 하이브리드",
    latestRunGenerated: "최신 실행 {{id}}, 생성 {{time}}.",
  },
  csr: {
    largePct: "차이율 큼",
    largeQty: "수량 영향 큼",
    moderatePct: "차이율 보통",
    moderateQty: "수량 영향 보통",
  },
  cr: {
    largeGap: "현행·후보 간 차이 큼",
    priorAbove: "전년 수요가 최근 판매 속도보다 훨씬 높음",
    priorBelow: "전년 수요가 최근 판매 속도보다 훨씬 낮음",
    needsReview: "사업 검토 필요",
    moderateGap: "현행 예측 대비 차이 보통",
    broadlyAligned: "후보가 대체로 일치",
  },
  conf: {
    high: "지사·채널 및 SKU 수준의 이력이 안정적",
    medium: "사용 가능한 이력, SKU·채널 변화 검토 필요",
    low: "SKU 이력이 적거나 배분 수요임, 수동 검토 필요",
    bucket: "예측 신뢰도 구간",
    title: "예측 신뢰도",
    note: "행 수와 수량 비중",
  },
  sig: {
    promo: "프로모션 / 가격",
    promoNote: "할인, 포인트, 가격 인하 또는 프로모션 후행 수요 위험",
    promoMetric: "평균 신호 비중",
    stock: "재고 / 품절",
    stockNote: "놓친 수요 또는 재고 부족으로 제한된 수요 가능성",
    stockMetric: "평균 품절 비중",
    holiday: "휴일",
    holidayNote: "최근 판매에 휴일 상승 또는 하락이 포함되었을 수 있음",
    holidayMetric: "신호 행 수",
    noSales: "최근 판매 없음",
    noSalesNote: "예측은 있으나 최근 4주 수요가 없음",
    noSalesMetric: "위험 예측량",
  },
  fg: {
    needsDataDetail: "피처 모델은 계획 검토에 쓰기 전에 백테스트 결과가 필요합니다.",
    keepAction: "피처 신호는 검토 대기열로만 사용",
    keepDetail: "최신 백테스트에서 피처 후보가 전체 수준에서 현행 기준선보다 낫지 않습니다.",
    pilotAction: "필터된 지사·채널 검토에서 피처 모델 파일럿",
    pilotDetail: "피처 후보가 WAPE를 개선하고 높은 우선순위 위험 비중이 관리되고 있습니다.",
    reviewAction: "결과 교체 전 진단용으로 사용",
    reviewDetail: "피처 후보에 정확도 개선이 일부 있지만 위험 대기열은 계속 모니터링해야 합니다.",
    holdAction: "피처 모델은 아직 승격하지 않음",
    holdDetail: "피처 후보가 현행 기준선 대비 충분히 안정적이지 않습니다.",
    accuracy: "정확도 영향",
    accuracyDetail: "음수는 피처 모델의 WAPE가 현행 기준선보다 낮다는 뜻입니다.",
    risk: "위험 압력",
    riskValue: "높음 {{n}}",
    riskDetail: "AI 검토 대기열의 {{share}}가 높은 우선순위입니다.",
    next: "다음 조치",
    nextDetail: "현재 필터에서 피처 신호 그룹 {{n}}개가 감지되었습니다.",
    title: "피처 가드레일 권장",
  },
  fa: {
    watchReason: "피처 신호가 계획 결과를 바꿀 만큼 결정적이지 않습니다.",
    pilotReason: "의미 있는 물량에서 피처 모델이 WAPE를 충분히 개선합니다.",
    keepReason: "피처 모델이 WAPE를 악화시킵니다. 이 지사·채널은 현행 기준선을 유지하세요.",
    lowReason: "물량이 너무 적어 안정적인 모델 판단이 어렵습니다.",
  },
  p7: {
    backtest: "피처 모델 백테스트",
    backtestDetail: "{{n}}주 사용 가능, 현행 대 피처 모델 비교를 불러왔습니다.",
    signals: "외부 신호 연결됨",
    signalsDetail: "휴일, 재고, 프로모션, 가격, 무판매 데이터에서 신호 그룹 {{n}}개가 감지되었습니다.",
    queue: "위험 대기열 조치 가능",
    queueDetail: "SKU 위험 {{risks}}건이 예외 버킷 {{buckets}}개로 묶였습니다.",
    pilot: "파일럿 판단 결과",
    pilotDetail: "파일럿 후보 그룹 {{pilot}}개, 기준선 유지 그룹 {{keep}}개.",
    guardrail: "가드레일 상태",
    guardrailDetail: "{{label}}: {{action}}.",
    pilotReady: "파일럿 준비됨",
    pilotReadyDetail: "Phase 7 UI는 통제된 파일럿 검토 준비가 되었습니다. Phase 8 워크플로가 정해질 때까지 운영 결과는 그대로 두세요.",
    mvpReady: "MVP 준비됨",
    mvpReadyDetail: "Phase 7 UI는 계획 검토에 유용합니다. 남은 작업은 주로 검증 심화와 운영 워크플로입니다.",
    needsWork: "보완 필요",
    needsWorkDetail: "Phase 7은 계획 판단에 쓰기 전에 피처 근거가 더 필요합니다.",
    readiness: "Phase 7 준비도",
    checksPassed: "점검 {{total}}개 중 {{done}}개 통과",
  },
  p8: {
    official: "공식 결과",
    officialDetail: "운영 예측은 Hierarchical recent12 SKU mix를 유지합니다. AI/피처 결과가 자동으로 대체하지 않습니다.",
    aiUsage: "AI 활용",
    aiUsageValue: "파일럿 / 검토 레이어",
    aiUsageDetail: "Actionable AI Output에서 파일럿 후보로 표시된 지사·채널 그룹에만 AI를 사용하세요.",
    weekly: "주간 새로 고침",
    weeklyValue: "월요일 수동 새로 고침",
    weeklyDetail: "담당: 본인. 주간 판매 데이터 업데이트가 끝나면 데이터 새로 고침을 실행하세요.",
    failure: "새로 고침 실패",
    failureValue: "오류 표시, 기준선 유지",
    failureDetail: "새로 고침이 실패하거나 시간 초과되면 대시보드에 오류가 표시되며, 새 결과를 쓰기 전에 원인을 조사합니다.",
    title: "Phase 8 운영 워크플로 초안",
    note: "운영 반영 전 확정된 정책",
    finalLogic: "최종 결과 로직",
    finalLogicValue: "기준선이 공식이며 AI는 파일럿/검토 전용",
    bestOption: "최선의 방안: 현행 기준선을 운영 예측으로 유지하고, AI/피처 결과는 통제된 지사·채널 파일럿에만 사용하며, AI 결과가 비정상이거나 검토되지 않았을 때는 기준선을 유지합니다.",
    runGuardrail: "실행 가드레일 판정",
    branchMovers: "지사 변동",
    weeklyChecklist: "주간 운영 체크리스트",
    weeklyChecklistNote: "매주 월요일 데이터 새로 고침 후 사용하세요",
    holdDetail: "보류 사유 상세",
    holdReviewThese: "새 실행을 쓰기 전에 아래 항목을 검토하세요",
    guardrailDiagnostics: "가드레일 진단",
    exportHold: "보류 CSV 내보내기",
    noReviewMovers: "검토할 변동 항목 없음",
    allInside: "모든 SKU 변동이 가드레일 이내입니다.",
    reviewGroups: "검토 그룹",
    byBranchChannel: "지사 / 채널별",
    noHoldGroups: "현재 필터에 보류 그룹이 없습니다.",
    topSkuActions: "주요 SKU 조치",
    top12: "주의가 필요한 상위 12개 변동 항목",
    noSkuAction: "SKU 조치가 필요하지 않습니다.",
    controls: "운영 통제",
    controlsReady: "{{ready}} / {{total}} 준비 또는 정책 준비",
    runHistory: "예측 실행 이력",
    runHistoryCompared: "최신 실행과 이전 실행 비교",
    runHistoryWaiting: "실행이 2회 이상 쌓이기를 기다리는 중",
    recentRuns: "최근 예측 실행",
    latest5: "최근 5회",
    noRunHistory: "불러온 실행 이력이 없습니다.",
    bcMovement: "지사 / 채널 변동",
    top10Abs: "절대 차이 기준 상위 10",
    noPrevMovement: "아직 이전 실행 대비 변동이 없습니다.",
    skuMovement: "이전 실행 대비 SKU 변동",
    top15Abs: "절대 차이 기준 상위 15",
    noSkuMovement: "아직 SKU 실행 변동이 없습니다.",
  },
  mv: {
    newReason: "최신 실행에서 예측이 생겼지만 이전 실행에서는 0이었습니다.",
    droppedReason: "이전 실행 대비 예측이 0으로 떨어졌습니다.",
    manualReason: "SKU 변동이 Phase 8 심각 변동 가드레일을 넘었습니다.",
    watchReason: "SKU 변동이 의미 있지만 심각 가드레일보다는 낮습니다.",
    acceptReason: "변동이 SKU 가드레일 이내입니다.",
  },
  hr: {
    total: "전체 변동",
    totalDetail: "이전 실행 대비 {{pct}}, 수량 {{qty}}.",
    branch: "지사·채널 변동",
    branchDetail: "그룹 {{n}}개가 30% 또는 5,000개 변동을 넘었습니다.",
    sku: "SKU 변동",
    skuDetail: "SKU {{n}}개가 50% 또는 1,000개 변동을 넘었습니다.",
  },
  g8: {
    noRunAction: "결과 사용 금지",
    noRunDetail: "대시보드가 예측 실행 ID를 찾지 못했습니다. 예측 결과를 쓰기 전에 데이터를 새로 고치거나 백엔드를 확인하세요.",
    firstAction: "기준선만 사용",
    firstDetail: "저장된 첫 예측 실행이므로 주간 대비 이상 점검은 다음 새로 고침 후에 활성화됩니다.",
    holdAction: "이전 기준선 유지",
    holdDetail: "실행 전체 변동 {{pct}}, 지사·채널 {{branch}}개와 SKU {{sku}}개가 가드레일을 넘었습니다.",
    reviewAction: "내보내기 전 검토",
    reviewDetail: "실행 전체 변동 {{pct}}, 플래너와 공유하기 전에 주요 변동 항목을 검토하세요.",
    passAction: "플래너 검토 가능",
    passDetail: "실행 간 변동이 가드레일 이내입니다. 기준선이 공식이며 AI는 검토 레이어로 유지됩니다.",
  },
  rh: {
    latest: "최신 실행",
    previous: "이전 실행",
    totalDelta: "전체 차이",
    vsPrevious: "이전 실행 대비",
    skuDelta: "SKU 수 차이",
    skuDeltaDetail: "현재 {{current}} / 이전 {{previous}}",
  },
  pc: {
    export: "CSV 내보내기 검토",
    exportDetail: "계획 결과, 권장 결과, AI 검토 대기열, 예외 버킷, 조치 결과를 내보낼 수 있습니다.",
    runId: "실행 ID / 생성 시각",
    runIdNone: "아직 불러온 예측 실행 ID가 없습니다.",
    compare: "이전 실행과 비교",
    compareDetail: "최신 실행과 이전 실행의 차이는 {{pct}}({{qty}}개)입니다.",
    compareNone: "주간 변화를 비교하려면 예측 새로 고침 실행이 최소 2회 필요합니다.",
    rollback: "롤백 / 가드레일",
  },
  wc: {
    sales: "판매 데이터 새로 고침",
    salesDetail: "실적 기준일 {{date}}.",
    salesNone: "최신 주간 마트 날짜를 불러오지 못했습니다.",
    run: "예측 실행 생성됨",
    runNone: "데이터 새로 고침이 실행 ID를 만들지 않았습니다.",
    prev: "이전 실행 보관됨",
    prevDetail: "이전 실행 {{id}}을(를) 비교에 사용할 수 있습니다.",
    prevNone: "이전 실행 비교를 활성화하려면 주간 새로 고침이 한 번 더 필요합니다.",
    guardrail: "가드레일 점검",
    export: "플래너용 내보내기",
    exportPass: "최종 확인 후 계획 CSV를 내보낼 수 있습니다.",
    exportOther: "먼저 보류 CSV를 내보낸 뒤 계획 CSV 공유 여부를 결정하세요.",
    action: "이번 주 조치",
    actionHold: "주요 변동 항목을 검토할 때까지 기준선/이전 결과를 유지하세요.",
    actionReview: "공유 전에 지사와 SKU 변동 항목을 검토하세요.",
    actionProceed: "일반 주간 내보내기 절차대로 진행하세요.",
  },
  mr: {
    currentUse: "후보 신호가 아직 엇갈리는 동안의 기본 결과입니다.",
    currentAction: "운영 기준선으로 유지",
    v4Use: "최근 수요가 계절 이력과 어긋날 때 최신 단기 신호에 가장 적합합니다.",
    v4Action: "단기 도전 모델로 사용",
    v5Use: "극단적인 계절 급변을 막아 주는 계절성 우선 예측에 가장 적합합니다.",
    v5Action: "다음 승격 후보로 유지",
  },
  rp: {
    v5: "V5 후보 승격 검토",
    v5Detail: "V5가 충분한 롤링 구간에서 우세하고 검증 구간 전반에서 현행 WAPE를 개선합니다.",
    v4: "V4를 최신 구간 도전 모델로 유지",
    v4Detail: "V4가 최신 백테스트에서 가장 강하지만, 롤링 검증이 결과를 교체할 만큼 안정적이지 않습니다.",
    v5Review: "V5 승격 검토",
    v5ReviewDetail: "V5가 최신 백테스트에서 현행을 개선하고 계절성 우선 정책이 더 명확합니다.",
    keepDetail: "후보 모델은 참고가 되지만, 현행 결과가 여전히 가장 안전한 운영 기준선입니다.",
  },
  vs: {
    needsDataDetail: "하이브리드 검증에는 모델 비교, 규칙 하이브리드, 상한 백테스트 행이 필요합니다.",
    stableDetail: "규칙 하이브리드가 대부분의 롤링 구간에서 우세하고 평균적으로 최적 단일 모델 이상입니다.",
    notStableDetail: "규칙 하이브리드가 최신 구간에서는 우세하지만 이전 롤링 구간에서는 최적 단일 모델을 꾸준히 이기지 못합니다.",
    overfitDetail: "규칙 하이브리드가 짧은 구간에서 주별 최적 상한과 같습니다. 예측 결과에 쓰기 전에 더 많은 롤링 구간으로 검증하세요.",
    promisingDetail: "규칙 하이브리드가 현재 권장 단일 모델보다 의미 있게 개선되지만 아직 롤링 구간 검증이 필요합니다.",
    watchDetail: "규칙 하이브리드는 아직 현행 예측 모델을 대체할 만큼 안정적인 개선을 보이지 않습니다.",
  },
  pp: {
    waitBacktest: "백테스트 대기",
    needsDataReason: "승격 검토 전에 최신 백테스트와 롤링 검증이 필요합니다.",
    preparePilot: "통제된 파일럿 준비",
    promoteReason: "{{model}}이(가) 대부분의 롤링 구간에서 우세하고 현행 대비 WAPE를 개선합니다.",
    reviewImpact: "지사·SKU 영향 검토",
    pilotReason: "{{model}}에 유용한 정확도 신호가 있지만 자동 승격할 만큼 안정적이지 않습니다.",
    keepDiagnostic: "진단용으로 유지",
    watchReason: "{{model}}이(가) 아직 현행 결과를 충분히 꾸준하게 이기지 못합니다.",
    waitRolling: "롤링 검증 대기",
    ruleNeedsData: "규칙 하이브리드는 운영 후보가 되기 전에 롤링 검증이 필요합니다.",
    rulePromote: "규칙 하이브리드가 롤링 구간 전반에서 안정적이어서 통제된 파일럿에서 현행 결과와 비교할 수 있습니다.",
    extendWindow: "검증 구간 확대",
    rulePilot: "규칙 하이브리드가 최근 검증을 개선하지만 과적합 위험에 대한 근거가 더 필요합니다.",
    keepOut: "결과에서 제외",
    currentReason: "후보 모델을 검토하는 동안 현재 대시보드 결과에 쓰이는 안정적인 기준선입니다.",
  },
  md: {
    hier: "지사·채널을 먼저 예측한 뒤 최근 12주 구성비로 SKU에 배분합니다.",
    naive1: "전주 실적을 예측으로 사용합니다.",
    ma4: "직전 4주 실적의 평균을 사용합니다.",
    ma12: "직전 12주 실적의 평균을 사용합니다.",
    seasonal: "가능하면 전년 같은 ISO 주를 사용합니다.",
    v3: "데이터 신호로 조정한 계절 후보로, 현행·최근 수요 쪽으로 완화합니다.",
    v4: "계절, 현행 결과, 전주 수요를 섞은 신호 혼합 후보입니다.",
    v5: "계절·현행 간 큰 차이에 가드레일을 둔 계절성 우선 후보입니다.",
    rule: "예측 이전 신호만 쓰는 규칙 기반 하이브리드 모델입니다.",
    benchmark: "벤치마크 예측 모델입니다.",
  },
  pd: {
    seasonal: "계절성 우선 기준선, SKU 배분 검토에는 계층형을 유지하세요.",
    v4: "롤링 검증에서 V4 후보가 가장 강합니다. 승격 전에 편향과 SKU 배분을 검토하세요.",
    v5: "롤링 검증에서 V5 계절성 우선 가드레일이 가장 강합니다. 승격 전에 SKU 영향을 검토하세요.",
    hybrid: "하이브리드 후보입니다. 예측 결과로 쓰기 전에 더 검증하세요.",
    keep: "롤링 검증이 더 확실해질 때까지 현행 모델 방향을 유지하세요.",
  },
  ck: {
    rolling: "롤링 검증이 계절성을 지지함",
    rollingSeasonal: "검증 구간 {{n}}개에서 계절 모델이 현재 권장 모델입니다.",
    rollingOther: "현재 권장 모델은 {{model}}입니다.",
    branch: "지사·채널 영향이 관리됨",
    branchDetail: "지사·채널 그룹 {{n}}개가 아직 검토가 필요합니다.",
    branchNone: "검토로 표시된 지사·채널 그룹이 없습니다.",
    highGroups: "높은 우선순위 SKU 그룹 검토 완료",
    highGroupsDetail: "높은 우선순위 {{high}}개, 보통 우선순위 {{medium}}개 그룹이 남아 있습니다.",
    highGroupsNone: "높은 우선순위 그룹은 없으며, 보통 그룹 {{medium}}개는 일상 검토가 필요합니다.",
    skuList: "SKU 영향 목록 검토 준비됨",
    skuListDetail: "SKU 변동 항목 {{n}}개가 검토로 표시되었습니다. 사업 확인을 위해 CSV를 내보내세요.",
    skuListNone: "검토로 표시된 SKU 변동 항목이 없습니다.",
  },
  cd: {
    needsDataDetail: "후보 판단에는 롤링 검증, 지사 진단, SKU 영향 행이 필요합니다.",
    promote: "후보 승격",
    promoteDetail: "계절성이 롤링 검증에서 우세하고 후보 영향도 관리 가능한 수준입니다.",
    reviewBefore: "승격 전 검토",
    reviewBeforeDetail: "계절성이 롤링 검증에서 우세하지만, 지사·SKU 영향에 사업 검토가 필요한 큰 변동 항목이 있습니다.",
    doNot: "아직 승격하지 않음",
    doNotDetail: "롤링 검증이 현행 예측 결과를 후보 V2로 교체하는 것을 지지하지 않습니다.",
  },
  gov: {
    needsDataDetail: "권장 결과를 관리하려면 상세 행이 필요합니다.",
    reviewOnly: "검토 레이어 전용",
    reviewOnlyDetail: "큰 SKU 영향이나 모델 불일치가 남아 있습니다. 운영 결과를 바꾸기 전에 CSV로 검토하세요.",
    pilot: "통제된 파일럿",
    pilotDetail: "권장 결과는 계획 검토에 쓸 수 있지만 지사·채널 담당자 확인이 아직 필요합니다.",
    ready: "승격 검토 준비됨",
    readyDetail: "대부분의 행이 현행을 유지하거나 지사·채널 모델 권장과 일치하며, 남은 차이는 관리되고 있습니다.",
  },
  pol: {
    guardrail: "운영 가드레일",
    pressure: "검토 부담",
    pressureDetail: "행의 {{share}}가 검토, {{watch}}행이 주시입니다.",
    agreement: "모델 일치",
    agreementValue: "{{n}}개 일치",
    agreementDetail: "{{n}}행이 지사·채널 모델 권장과 다릅니다.",
    volume: "물량 영향",
    volumeDetail: "행의 {{share}}가 현행 운영 기준선을 유지합니다.",
  },
  lvd: {
    total: "전체 수요 정확도입니다. 상위 수준 계획에 가장 좋은 신호입니다.",
    branch_channel: "수요를 지사와 판매 채널로 나눈 뒤의 정확도입니다.",
    sku: "SKU 배분 정확도입니다. 판매가 드물거나 불규칙한 SKU는 오차가 더 클 수 있습니다.",
    other: "이 수준의 예측 정확도입니다.",
  },
  btn: {
    exportCsv: "CSV 내보내기",
  },
  fc: {
    actualForecast: "실적 + 예측",
    actualForecastNote: "최근 실적 {{actual}}주, 향후 예측 {{forecast}}주",
  },
  cand: {
    title: "예측 V2 후보",
    note: "계절성 우선, 현행 결과를 대체하지 않음",
    candidateV2: "후보 V2",
    diagnostics: "진단",
    diagCounts: "검토 {{review}} / 주시 {{watch}}",
    highSkuImpact: "큰 SKU 영향",
    promoteChecklist: "승격 체크리스트",
    readyToPromote: "승격 준비됨",
    notReady: "아직 준비되지 않음",
    passed: "{{done}} / {{total}} 통과",
    candidateTotal: "후보 합계",
    currentTotal: "현행 합계",
    hierOutput: "계층형 결과",
    difference: "차이",
    policyText: "후보 V2는 롤링 정책 방향을 따릅니다. 전년 같은 ISO 주 수요를 계절성 우선 기준선으로 쓰고, 현행 예측 결과를 대체하기 전에 SKU 배분을 따로 검토합니다.",
  },
  bc: {
    top10: "상위 10",
  },
  sku: {
    title: "SKU 예측",
    rowsOf: "{{total}}행 중 {{shown}}행",
  },
  rec: {
    title: "권장 예측 결과",
    note: "검토 레이어, 현행 결과를 대체하지 않음",
    planningCsv: "계획 CSV",
    productionBaseline: "운영 기준선",
    recommendedTotal: "권장 합계",
    reviewOutput: "검토 결과",
    reviewWatch: "검토 / 주시",
    keepCurrentN: "현행 유지 {{n}}",
    alignedReview: "일치 / 검토",
    p1p2: "P1 / P2 대기열",
    decisionPolicy: "판단 정책",
    unchanged: "이 검토 레이어가 승인될 때까지 현행 운영 결과는 바뀌지 않습니다.",
    allDirection: "전체 방향",
    allAlignment: "전체 일치 여부",
    filterGroup: "지사 / 채널: {{value}} x",
    filterReason: "사유: {{value}} x",
    clearDrill: "드릴다운 해제",
    queue: "검토 우선순위 대기열",
    queueNote: "현재 필터의 상위 {{n}}행",
    modelAligned: "모델 일치",
    modelReview: "모델 검토",
    byGroup: "지사 / 채널별 검토 요약",
    groupsN: "현재 필터의 그룹 {{n}}개",
    increase: "상향",
    decrease: "하향",
    recVsCurrent: "권장 {{rec}} 대 현행 {{cur}}",
    byReason: "사유별 검토 요약",
    reasonsN: "현재 필터의 사유 {{n}}개",
  },
  csku: {
    title: "후보 SKU 영향",
    note: "절대 차이 기준 상위 {{n}}개 변동 항목",
    seasonalityBaseline: "계절성 기준선",
    dataSignalAdjusted: "데이터 신호 조정",
    v3vsCurrent: "V3 대 현행",
    v3vsV2: "V3 대 V2",
    groupsWithReview: "검토·주시 SKU가 있는 그룹 {{n}}개",
    cardText: "V2 {{v2}} / V3 {{v3}} 대 현행 {{cur}}",
    sigRecent12: "최근 12주: {{n}}",
    sigActiveWeeks: "최근 판매 주수: {{n}}",
    sigPriorYear: "전년 동기간: {{n}}",
  },
  bt: {
    summary: "백테스트 요약",
    lastWeeks: "최근 실적 {{n}}주",
    modelUsed: "사용 모델",
    wapeText: "총 절대 예측 오차를 총 실적 수량으로 나눈 값입니다. 낮을수록 좋습니다.",
    biasText: "(예측 − 실적) ÷ 실적입니다. 음수는 과소 예측, 양수는 과대 예측입니다.",
    level: "수준",
    levelText: "전체는 계획 정확도, 지사·채널은 배분 정확도, SKU는 품목 수준 배분 위험을 보여 줍니다.",
  },
  mc: {
    title: "모델 비교",
    note: "가능하면 롤링 검증으로 권장합니다",
    exportModels: "모델 내보내기",
    recommendedModel: "권장 모델",
    selectedRolling: "롤링 검증으로 선정: {{model}}이(가) 구간 {{windows}}개 중 {{wins}}개에서 우세. {{policy}}",
    selectedWape: "WAPE를 먼저 보고, WAPE 차이가 3%p 이내이면 편향으로 선정합니다. 현재 WAPE {{wape}}, 편향 {{bias}}.",
    currentOutput: "현행 예측 결과",
    rollingWinner: "롤링 우세 모델",
    hybridStatus: "하이브리드 상태",
    nextDirection: "다음 방향",
    latestV4Gap: "최신 V4 차이",
    v4MinusCurrent: "V4 WAPE − 현행",
    latestV5Gap: "최신 V5 차이",
    v5MinusCurrent: "V5 WAPE − 현행",
    promoCandidate: "승격 후보",
    path: "{{label}} 경로",
    promoNote: "승격은 자동 운영 교체가 아니라 통제된 파일럿을 먼저 하는 것을 뜻합니다.",
    lowVolumeTitle: "실적 수량이 작아 WAPE가 극단적으로 보일 수 있습니다. 이 권장을 쓰기 전에 수량 영향을 검토하세요.",
  },
  feat: {
    candidate: "피처 후보",
    candidateNote: "휴일, 재고, 프로모션 후행 신호를 사용한 전체 수준 백테스트입니다.",
    bestWape: "최저 피처 WAPE",
    gapVsCurrent: "현행 대비 차이",
    backtestWeeks: "백테스트 주수",
    modelSummary: "모델 요약",
    modelSummaryNote: "후보 모델별 전체 수준 정확도",
    signalCoverage: "피처 신호 범위",
    signalCoverageNote: "현재 필터의 휴일, 재고, 프로모션/가격 위험",
    highPriorityN: "높은 우선순위 {{n}}",
    trend: "피처 백테스트 추이",
    trendNote: "주별 현행 기준선 대 {{model}}",
    weekLabel: "{{label}} 주",
    adjustment: "피처 조정",
    verdict: "주간 피처 판정",
    verdictNote: "현행 기준선 대비 후보 WAPE 개선",
    candLower: "후보 WAPE가 더 낮음",
    candHigher: "후보 WAPE가 더 높음",
    avgImprovement: "평균 개선",
    currentMinusCand: "현행 WAPE − 후보 WAPE",
    bestWorst: "최고 / 최저 주",
    weeklyDetail: "주간 백테스트 상세",
    weeklyDetailNote: "주별 실적 대 피처 예측",
    actionable: "실행 가능한 AI 결과",
    actionableNote: "지사·채널별 권장 사용법이며, 운영 결과를 자동으로 대체하지 않습니다",
    pilotCandidates: "파일럿 후보",
    pilotCandNote: "의미 있는 물량에서 피처가 WAPE 개선",
    keepBaseline: "기준선 유지",
    worsens: "피처가 WAPE 악화",
    watchLow: "주시 / 소량",
    insufficient: "결과를 바꾸기에 신호가 부족",
    weightedGain: "파일럿 가중 개선",
    weightedGainNote: "실적 수량으로 가중한 WAPE 포인트",
    topPilots: "주요 파일럿 후보",
    bcGroupsN: "지사·채널 그룹 {{n}}개",
    bcImpact: "지사 / 채널별 피처 영향",
    bcImpactNote: "피처 조정이 도움이 되거나 해가 되는 곳",
    reviewQueue: "AI 검토 대기열",
    reviewQueueNote: "SKU 위험 {{total}}건 중 {{shown}}건",
    totalRisks: "전체 위험",
    mediumPriority: "보통 우선순위",
    topRisk: "주요 위험",
    allRisk: "전체 위험",
    exceptions: "AI 예외 버킷",
    exceptionsNote: "위험과 권장 조치별로 묶은 예측 수량",
    exceptionBuckets: "예외 버킷",
    skuRowsN: "SKU {{n}}행",
    forecastAtRisk: "위험 예측량",
    currentFilter: "현재 필터",
    highPriorityForecast: "높은 우선순위 예측량",
    bucketHasHigh: "높은 우선순위 행이 있는 버킷",
    topException: "주요 예외",
    forecastQtyN: "예측 수량 {{n}}",
    riskByBc: "지사 / 채널별 위험",
    top10Filter: "현재 필터의 상위 10",
  },
  wt: {
    title: "주간 모델 추적",
    note: "매주 데이터 새로 고침 후 기준선, AI, 최근 판매 속도 모델 중 무엇이 개선되는지 판단할 때 사용하세요.",
    export: "추적 내보내기",
    latestWeek: "최신 주",
    actualQtyN: "실적 수량 {{n}}",
    noRows: "추적 행 없음",
    baselineAvg: "기준선 평균 WAPE",
    lastTracked: "최근 추적 {{n}}주",
    bestAvg: "최적 모델 평균 WAPE",
    bestAvgNote: "실적 확정 후 주별 우세 모델",
    topWinner: "가장 자주 우세한 모델",
    winsN: "최근 구간 {{n}}회 우세",
    empty: "주간 추적 행이 없습니다",
  },
  wmc: {
    title: "주간 모델 비교",
    note: "주별 우세 모델과 예측",
    export: "주간 내보내기",
  },
  diag: {
    title: "후보 진단",
    note: "V3, V4, V5를 현행 예측 결과와 비교",
    within: "WAPE 0.5%p 이내",
    v3Worse: "V3 열위",
    avgGap: "평균 WAPE 차이",
    v3Minus: "V3 − 현행",
    v4Beats: "V4가 현행보다 우수",
    v4Worse: "V4 열위",
    v4AvgGap: "V4 평균 차이",
    v4Minus: "V4 − 현행",
    v5Beats: "V5가 현행보다 우수",
    v5Worse: "V5 열위",
    v5AvgGap: "V5 평균 차이",
    v5Minus: "V5 − 현행",
  },
  hyb: {
    title: "하이브리드 시뮬레이션",
    note: "백테스트 주별 최적 모델",
    wape: "하이브리드 WAPE",
    better: "권장보다 {{n}}%p 우수",
    check: "개선 가능성 점검",
    text: "매주 과거 최적 모델을 쓴 상한 시뮬레이션입니다. 실제 하이브리드 규칙을 만들 가치가 있는지 가늠하는 데 도움이 됩니다.",
    winningWeeks: "우세 {{n}}주",
  },
  rule: {
    title: "규칙 하이브리드 v2",
    note: "예측 주 이전에 알 수 있는 정보만 사용",
    validationStatus: "검증 상태",
    upperGap: "상한 대비 차이",
    vsRecommended: "권장 대비 {{n}}%p",
    check: "실용 하이브리드 점검",
    modelsUsed: "사용 모델 {{n}}개",
    text: "규칙 v2는 후보 모델 간 일치도를 사용합니다. Naive가 Hierarchical과 가까우면 Naive, Seasonal이 MA12와 일치하거나 전주 급등을 감지하면 Seasonal, 그 외에는 MA12 또는 Hierarchical로 대체합니다.",
  },
  roll: {
    title: "롤링 검증",
    note: "구간 {{n}}개 중 V4 {{v4}} / V5 {{v5}} / 규칙 {{rule}} 우세",
    v4Wins: "V4 롤링 우세",
    v5Wins: "V5 롤링 우세",
    ruleWins: "규칙 롤링 우세",
    ruleAvgGap: "규칙 평균 차이",
    ruleMinusBest: "규칙 − 최적 단일 모델",
  },
  acc: {
    title: "수준별 정확도",
    note: "WAPE는 낮을수록 좋습니다",
    bias: "편향 {{v}}",
  },
  wavf: {
    title: "주간 실적 대 예측",
    note: "막대: 실적 / 예측, 선: WAPE",
  },
  wbd: {
    note: "모델별 실적 대 예측",
  },
};

const vi = {
  top: {
    title: "Dự báo nhu cầu",
    actualsThrough: "Thực tế đến {{date}}",
    subtitle: "Dự báo ERPQuantity theo tuần",
    env: "API staging",
    refreshDataTitle: "Làm mới mart dự báo, kết quả dự báo và backtest",
    refreshData: "Làm mới dữ liệu",
    refresh: "Làm mới",
    loading: "Đang tải",
  },
  f: {
    horizon: "Kỳ dự báo",
    weeksN: "{{n}} tuần",
    noMatches: "Không có kết quả",
    clear: "Xóa lọc",
  },
  msg: {
    refreshRunning: "Đang làm mới dữ liệu: mart, dự báo và backtest. Việc này có thể mất 2-3 phút.",
    refreshed: "Đã làm mới dữ liệu dự báo: {{steps}} lúc {{time}}",
    done: "xong",
    cannotParse: "Không đọc được phản hồi {{endpoint}}",
    cannotLoad: "Không tải được {{endpoint}}",
  },
  stat: {
    w1: "Dự báo W+1",
    w4: "Dự báo 4 tuần",
    weeksAvailable: "Có {{n}} tuần",
    total: "Tổng dự báo",
    skus: "{{n}} SKU",
    wape: "WAPE backtest",
    totalLevel: "Cấp tổng",
  },
  tab: {
    forecast: "Dự báo",
    sku: "Chi tiết SKU",
    backtest: "Backtest",
  },
  level: {
    total: "Tổng",
    branch_channel: "Chi nhánh / Kênh",
    sku: "SKU",
  },
  u: {
    pts: "{{n}} điểm %",
    qty: "SL {{n}}",
    rows: "{{n}} dòng",
    weeks: "tuần",
    ofWindows: "trên {{n}} cửa sổ",
    skuMovers: "SKU biến động",
  },
  v: {
    all: "Tất cả",
    review: "Cần xem xét",
    watch: "Theo dõi",
    ok: "Ổn",
    keepCurrent: "Giữ hiện tại",
    increaseForecast: "Tăng dự báo",
    decreaseForecast: "Giảm dự báo",
    aligned: "Khớp",
    high: "Cao",
    medium: "Trung bình",
    low: "Thấp",
    helps: "Có ích",
    hurts: "Gây hại",
    neutral: "Trung tính",
    unknown: "Không rõ",
    unknownReason: "Không rõ lý do",
    unknownRisk: "Không rõ rủi ro",
    reviewManually: "Xem xét thủ công",
    needsData: "Cần dữ liệu",
    keepBaseline: "Giữ baseline",
    pilotCandidate: "Ứng viên thí điểm",
    reviewCandidate: "Ứng viên cần xem xét",
    hold: "Tạm giữ",
    lowVolumeWatch: "Theo dõi (sản lượng thấp)",
    reviewNewDemand: "Xem xét nhu cầu mới",
    reviewDroppedDemand: "Xem xét nhu cầu mất đi",
    manualReview: "Xem xét thủ công",
    acceptMovement: "Chấp nhận biến động",
    pass: "Đạt",
    noRunLoaded: "Chưa tải lần chạy nào",
    ready: "Sẵn sàng",
    missing: "Thiếu",
    done: "Xong",
    waiting: "Đang chờ",
    reviewFirst: "Xem xét trước",
    proceed: "Tiếp tục",
    policyReady: "Chính sách sẵn sàng",
    rollingStable: "Ổn định khi kiểm định cuốn chiếu",
    notStableYet: "Chưa ổn định",
    highOverfitRisk: "Rủi ro quá khớp cao",
    promising: "Triển vọng",
    promoteReview: "Xem xét đưa lên chính thức",
    watchOnly: "Chỉ theo dõi",
    productionBaseline: "Baseline chính thức",
    ruleWins: "Quy tắc thắng",
    close: "Sát nhau",
    featureHelps: "Đặc trưng có ích",
    featureHurts: "Đặc trưng gây hại",
    lowVolume: "Sản lượng thấp",
    noFeatureSignal: "Không có tín hiệu đặc trưng",
    strongCandidate: "Ứng viên mạnh",
    v3Beats: "V3 tốt hơn hiện tại",
    v3Worse: "V3 kém hơn hiện tại",
  },
  col: {
    productName: "Tên sản phẩm",
    branch: "Chi nhánh",
    channel: "Kênh",
    collection: "Bộ sưu tập",
    category: "Danh mục",
    candidateQty: "SL ứng viên",
    candidateV3Qty: "SL ứng viên V3",
    currentQty: "SL hiện tại",
    deltaQty: "SL chênh lệch",
    deltaPctTxt: "Tỷ lệ chênh lệch",
    v3DeltaVsCurrentQty: "SL chênh lệch V3 so với hiện tại",
    v3DeltaVsCurrentPct: "Tỷ lệ chênh lệch V3 so với hiện tại",
    v3DeltaVsV2Qty: "SL chênh lệch V3 so với V2",
    status: "Trạng thái",
    reason: "Lý do",
    dataSignal: "Tín hiệu dữ liệu",
    candidateV3Rule: "Quy tắc ứng viên V3",
    recent12Qty: "SL 12 tuần gần nhất",
    recent4Qty: "SL 4 tuần gần nhất",
    recent12ActiveWeeks: "Số tuần có bán trong 12 tuần gần nhất",
    priorYearHorizonQty: "SL cùng kỳ năm trước",
    currentForecast: "Dự báo hiện tại",
    candidateV3Forecast: "Dự báo ứng viên V3",
    recommendedForecast: "Dự báo đề xuất",
    deltaPct: "Chênh lệch %",
    recommendedModel: "Mô hình đề xuất",
    bcRecommendedModel: "Mô hình đề xuất theo chi nhánh/kênh",
    modelAlignment: "Độ khớp mô hình",
    reviewPriority: "Mức ưu tiên xem xét",
    bcModelStatus: "Trạng thái mô hình chi nhánh/kênh",
    bcModelImprovement: "Mức cải thiện mô hình chi nhánh/kênh",
    direction: "Hướng",
    recent12Actual: "Thực tế 12 tuần gần nhất",
    recent4Actual: "Thực tế 4 tuần gần nhất",
    priorYearHorizon: "Cùng kỳ năm trước",
    actualsThrough: "Thực tế đến ngày",
    horizonWeeks: "Số tuần dự báo",
    currentProductionForecast: "Dự báo chính thức hiện tại",
    recommendedPlanningForecast: "Dự báo kế hoạch đề xuất",
    plannerOverride: "Dự báo điều chỉnh của người lập kế hoạch",
    finalApproved: "Dự báo duyệt cuối cùng",
    reviewStatus: "Trạng thái xem xét",
    bcModel: "Mô hình chi nhánh/kênh",
    bcModelShort: "Mô hình CN/kênh",
    plannerComment: "Ghi chú của người lập kế hoạch",
    owner: "Người phụ trách",
    decision: "Quyết định",
    year: "Năm",
    week: "Tuần",
    actualQty: "SL thực tế",
    baselineForecast: "Dự báo baseline",
    baselineWape: "WAPE baseline",
    baselineBias: "Độ lệch baseline",
    featureForecast: "Dự báo đặc trưng",
    featureWape: "WAPE đặc trưng",
    featureBias: "Độ lệch đặc trưng",
    ma4Forecast: "Dự báo MA4",
    ma4Bias: "Độ lệch MA4",
    ma12Forecast: "Dự báo MA12",
    ma12Bias: "Độ lệch MA12",
    winner: "Mô hình thắng",
    winnerForecast: "Dự báo mô hình thắng",
    winnerWape: "WAPE mô hình thắng",
    baselineGapVsWinnerPts: "Chênh lệch baseline so với mô hình thắng (điểm %)",
    section: "Phần",
    model: "Mô hình",
    forecastQty: "SL dự báo",
    bias: "Độ lệch",
    currentWape: "WAPE hiện tại",
    seasonalWape: "WAPE mùa vụ",
    improvementVsCurrent: "Cải thiện so với hiện tại",
    hierForecast: "Dự báo phân cấp",
    hierWape: "WAPE phân cấp",
    seasonalForecast: "Dự báo mùa vụ",
    v3Forecast: "Dự báo V3",
    v4Forecast: "Dự báo V4",
    v5Forecast: "Dự báo V5",
    naiveForecast: "Dự báo naive",
    naiveWape: "WAPE naive",
    priority: "Ưu tiên",
    risk: "Rủi ro",
    action: "Hành động",
    lowerQty: "SL cận dưới",
    upperQty: "SL cận trên",
    promoShare: "Tỷ trọng SL khuyến mãi",
    highDiscountShare: "Tỷ trọng SL giảm giá sâu",
    priceDropShare: "Tỷ trọng SL giảm giá",
    stockoutShare: "Tỷ trọng SL hết hàng",
    invToSales: "Tỷ lệ tồn kho trên doanh số",
    recommendedAction: "Hành động đề xuất",
    rows: "Số dòng",
    highPriorityRows: "Dòng ưu tiên cao",
    mediumPriorityRows: "Dòng ưu tiên trung bình",
    avgAdjustmentPct: "Tỷ lệ điều chỉnh TB",
    signalReason: "Lý do tín hiệu",
    guardrailDecision: "Quyết định ngưỡng an toàn",
    guardrailAction: "Hành động ngưỡng an toàn",
    latestRunId: "ID lần chạy mới nhất",
    latestRunTime: "Thời gian lần chạy mới nhất",
    previousRunId: "ID lần chạy trước",
    previousRunTime: "Thời gian lần chạy trước",
    totalDeltaQty: "Tổng SL chênh lệch",
    totalDeltaPct: "Tổng tỷ lệ chênh lệch",
    previousForecast: "Dự báo trước",
    plannerDecision: "Quyết định của người lập kế hoạch",
    candidate: "Ứng viên",
    current: "Hiện tại",
    delta: "Chênh lệch",
    recentRunRate: "Tốc độ bán gần đây",
    seasonalRecent: "Mùa vụ / Gần đây",
    forecast: "Dự báo",
    lower: "Cận dưới",
    upper: "Cận trên",
    confidence: "Độ tin cậy",
    candidateV3: "Ứng viên V3",
    recommended: "Đề xuất",
    alignment: "Độ khớp",
    v3Delta: "Chênh lệch V3",
    v3DeltaPct: "Chênh lệch V3 %",
    v3Rule: "Quy tắc V3",
    skus: "Số SKU",
    previous: "Trước",
    largestSku: "SKU biến động lớn nhất",
    location: "Vị trí",
    control: "Kiểm soát",
    detail: "Chi tiết",
    generated: "Thời điểm tạo",
    runId: "ID lần chạy",
    latestWape: "WAPE mới nhất",
    latestBias: "Độ lệch mới nhất",
    rollingWins: "Số lần thắng cuốn chiếu",
    rollingGap: "Chênh lệch cuốn chiếu",
    actual: "Thực tế",
    absError: "Sai số tuyệt đối",
    signal: "Tín hiệu",
    highPriority: "Ưu tiên cao",
    recent4w: "4 tuần gần nhất",
    metric: "Chỉ số",
    metricMeaning: "Ý nghĩa chỉ số",
    verdict: "Kết luận",
    candidateWape: "WAPE ứng viên",
    improvement: "Mức cải thiện",
    adjustment: "Điều chỉnh",
    gain: "Mức cải thiện",
    avgAdj: "Điều chỉnh TB",
    promo: "Khuyến mãi",
    stockout: "Hết hàng",
    invSales: "Tồn kho/Doanh số",
    option: "Phương án",
    whenToUse: "Khi nào dùng",
    recommendation: "Đề xuất",
    bestWape: "WAPE tốt nhất",
    aiForecast: "Dự báo AI",
    baselineGap: "Chênh lệch baseline",
    gap: "Chênh lệch",
    v4Gap: "Chênh lệch V4",
    v5Gap: "Chênh lệch V5",
    outcome: "Kết quả",
    diagnosis: "Chẩn đoán",
    v4Diagnosis: "Chẩn đoán V4",
    v5Diagnosis: "Chẩn đoán V5",
    chosenModel: "Mô hình được chọn",
    recentCv4: "CV4 gần đây",
    window: "Cửa sổ",
    bestModel: "Mô hình tốt nhất",
    ruleWape: "WAPE quy tắc",
    bestSingleWape: "WAPE mô hình đơn tốt nhất",
    ruleBias: "Độ lệch quy tắc",
    v4VsCurrent: "V4 so với hiện tại",
    v5VsCurrent: "V5 so với hiện tại",
    actualErp: "ERPQuantity thực tế",
    forecastErp: "ERPQuantity dự báo",
  },
  txt: {
    keepCurrentBaseline: "Giữ baseline hiện tại",
    keepCurrentOutput: "Giữ kết quả hiện tại",
    ruleHybrid: "Hybrid theo quy tắc",
    latestRunGenerated: "Lần chạy mới nhất {{id}}, tạo lúc {{time}}.",
  },
  csr: {
    largePct: "Chênh lệch tỷ lệ lớn",
    largeQty: "Ảnh hưởng số lượng lớn",
    moderatePct: "Chênh lệch tỷ lệ vừa phải",
    moderateQty: "Ảnh hưởng số lượng vừa phải",
  },
  cr: {
    largeGap: "Chênh lệch lớn giữa hiện tại và ứng viên",
    priorAbove: "Nhu cầu năm trước cao hơn nhiều so với tốc độ bán gần đây",
    priorBelow: "Nhu cầu năm trước thấp hơn nhiều so với tốc độ bán gần đây",
    needsReview: "Cần bộ phận kinh doanh xem xét",
    moderateGap: "Chênh lệch vừa phải so với dự báo hiện tại",
    broadlyAligned: "Ứng viên nhìn chung khớp",
  },
  conf: {
    high: "Lịch sử ổn định ở cấp chi nhánh/kênh và SKU",
    medium: "Lịch sử dùng được, cần xem xét thay đổi SKU hoặc kênh",
    low: "Lịch sử SKU thưa hoặc nhu cầu được phân bổ; cần xem xét thủ công",
    bucket: "Nhóm độ tin cậy dự báo",
    title: "Độ tin cậy dự báo",
    note: "Số dòng và tỷ trọng số lượng",
  },
  sig: {
    promo: "Khuyến mãi / Giá",
    promoNote: "Rủi ro nhu cầu do giảm giá, điểm thưởng, hạ giá hoặc độ trễ khuyến mãi",
    promoMetric: "Tỷ trọng tín hiệu TB",
    stock: "Tồn kho / Hết hàng",
    stockNote: "Có thể mất nhu cầu hoặc nhu cầu bị giới hạn do tồn kho thấp",
    stockMetric: "Tỷ trọng hết hàng TB",
    holiday: "Ngày lễ",
    holidayNote: "Doanh số gần đây có thể bao gồm mức tăng hoặc giảm do ngày lễ",
    holidayMetric: "Số dòng có tín hiệu",
    noSales: "Không có doanh số gần đây",
    noSalesNote: "Có dự báo nhưng 4 tuần gần nhất không có nhu cầu",
    noSalesMetric: "Dự báo có rủi ro",
  },
  fg: {
    needsDataDetail: "Mô hình đặc trưng cần có kết quả backtest trước khi dùng để xem xét kế hoạch.",
    keepAction: "Chỉ dùng tín hiệu đặc trưng làm hàng đợi xem xét",
    keepDetail: "Ở backtest mới nhất, ứng viên đặc trưng không vượt baseline hiện tại ở cấp tổng.",
    pilotAction: "Thí điểm mô hình đặc trưng trên các chi nhánh/kênh đã lọc",
    pilotDetail: "Ứng viên đặc trưng cải thiện WAPE và tỷ trọng rủi ro ưu tiên cao được kiểm soát.",
    reviewAction: "Dùng để chẩn đoán trước khi thay kết quả",
    reviewDetail: "Ứng viên đặc trưng có cải thiện độ chính xác, nhưng hàng đợi rủi ro vẫn cần theo dõi.",
    holdAction: "Chưa đưa mô hình đặc trưng lên chính thức",
    holdDetail: "Ứng viên đặc trưng chưa đủ ổn định so với baseline hiện tại.",
    accuracy: "Ảnh hưởng độ chính xác",
    accuracyDetail: "Giá trị âm nghĩa là mô hình đặc trưng có WAPE thấp hơn baseline hiện tại.",
    risk: "Áp lực rủi ro",
    riskValue: "{{n}} cao",
    riskDetail: "{{share}} hàng đợi xem xét AI có ưu tiên cao.",
    next: "Hành động tiếp theo",
    nextDetail: "Phát hiện {{n}} nhóm tín hiệu đặc trưng trong bộ lọc hiện tại.",
    title: "Đề xuất ngưỡng an toàn cho đặc trưng",
  },
  fa: {
    watchReason: "Tín hiệu đặc trưng chưa đủ rõ để thay đổi kết quả kế hoạch.",
    pilotReason: "Mô hình đặc trưng cải thiện WAPE đủ nhiều trên sản lượng đáng kể.",
    keepReason: "Mô hình đặc trưng làm WAPE kém đi; giữ baseline hiện tại cho chi nhánh/kênh này.",
    lowReason: "Sản lượng quá nhỏ để quyết định mô hình ổn định.",
  },
  p7: {
    backtest: "Backtest mô hình đặc trưng",
    backtestDetail: "Có {{n}} tuần; đã tải so sánh mô hình hiện tại và mô hình đặc trưng.",
    signals: "Đã kết nối tín hiệu bên ngoài",
    signalsDetail: "Phát hiện {{n}} nhóm tín hiệu từ dữ liệu ngày lễ, tồn kho, khuyến mãi, giá hoặc không có doanh số.",
    queue: "Hàng đợi rủi ro có thể xử lý",
    queueDetail: "{{risks}} rủi ro SKU được gom vào {{buckets}} nhóm ngoại lệ.",
    pilot: "Kết quả quyết định thí điểm",
    pilotDetail: "{{pilot}} nhóm ứng viên thí điểm; {{keep}} nhóm giữ baseline.",
    guardrail: "Trạng thái ngưỡng an toàn",
    guardrailDetail: "{{label}}: {{action}}.",
    pilotReady: "Sẵn sàng thí điểm",
    pilotReadyDetail: "Giao diện Phase 7 đã sẵn sàng cho đợt xem xét thí điểm có kiểm soát. Giữ nguyên kết quả chính thức cho đến khi quy trình Phase 8 được xác định.",
    mvpReady: "MVP sẵn sàng",
    mvpReadyDetail: "Giao diện Phase 7 hữu ích cho việc xem xét kế hoạch. Phần việc còn lại chủ yếu là kiểm định sâu hơn và quy trình chính thức.",
    needsWork: "Cần hoàn thiện",
    needsWorkDetail: "Phase 7 vẫn cần thêm bằng chứng về đặc trưng trước khi dùng để ra quyết định kế hoạch.",
    readiness: "Mức sẵn sàng Phase 7",
    checksPassed: "Đạt {{done}} / {{total}} mục kiểm tra",
  },
  p8: {
    official: "Kết quả chính thức",
    officialDetail: "Dự báo chính thức vẫn là Hierarchical recent12 SKU mix. Kết quả AI/đặc trưng không tự động thay thế.",
    aiUsage: "Sử dụng AI",
    aiUsageValue: "Lớp thí điểm / xem xét",
    aiUsageDetail: "Chỉ dùng AI cho các nhóm chi nhánh/kênh được đánh dấu Ứng viên thí điểm trong Actionable AI Output.",
    weekly: "Làm mới hằng tuần",
    weeklyValue: "Làm mới thủ công vào thứ Hai",
    weeklyDetail: "Người phụ trách: bạn. Chạy Làm mới dữ liệu sau khi dữ liệu bán hàng tuần đã cập nhật xong.",
    failure: "Làm mới thất bại",
    failureValue: "Báo lỗi, giữ baseline",
    failureDetail: "Nếu làm mới thất bại hoặc quá thời gian, dashboard sẽ báo lỗi và chúng tôi kiểm tra trước khi dùng kết quả mới.",
    title: "Bản nháp quy trình chính thức Phase 8",
    note: "Chính sách đã chốt trước khi đưa vào chính thức",
    finalLogic: "Logic kết quả cuối cùng",
    finalLogicValue: "Baseline là chính thức; AI chỉ để thí điểm/xem xét",
    bestOption: "Phương án tốt nhất: giữ baseline hiện tại làm dự báo chính thức, chỉ dùng kết quả AI/đặc trưng cho các đợt thí điểm chi nhánh/kênh có kiểm soát, và giữ baseline mỗi khi kết quả AI bất thường hoặc chưa được xem xét.",
    runGuardrail: "Quyết định ngưỡng an toàn của lần chạy",
    branchMovers: "Biến động chi nhánh",
    weeklyChecklist: "Danh mục kiểm tra vận hành hằng tuần",
    weeklyChecklistNote: "Dùng sau mỗi lần Làm mới dữ liệu vào thứ Hai",
    holdDetail: "Chi tiết lý do tạm giữ",
    holdReviewThese: "Xem xét các mục này trước khi dùng lần chạy mới",
    guardrailDiagnostics: "Chẩn đoán ngưỡng an toàn",
    exportHold: "Xuất CSV tạm giữ",
    noReviewMovers: "Không có mục biến động cần xem xét",
    allInside: "Mọi SKU biến động đều nằm trong ngưỡng an toàn.",
    reviewGroups: "Nhóm cần xem xét",
    byBranchChannel: "Theo chi nhánh / kênh",
    noHoldGroups: "Không có nhóm tạm giữ trong bộ lọc hiện tại.",
    topSkuActions: "Hành động SKU chính",
    top12: "12 mục biến động cần chú ý nhất",
    noSkuAction: "Không cần hành động nào cho SKU.",
    controls: "Kiểm soát chính thức",
    controlsReady: "{{ready}} / {{total}} sẵn sàng hoặc đã có chính sách",
    runHistory: "Lịch sử lần chạy dự báo",
    runHistoryCompared: "So sánh lần chạy mới nhất với lần trước",
    runHistoryWaiting: "Đang chờ có ít nhất hai lần chạy",
    recentRuns: "Các lần chạy dự báo gần đây",
    latest5: "5 lần gần nhất",
    noRunHistory: "Chưa tải lịch sử lần chạy.",
    bcMovement: "Biến động chi nhánh / kênh",
    top10Abs: "Top 10 theo chênh lệch tuyệt đối",
    noPrevMovement: "Chưa có biến động so với lần chạy trước.",
    skuMovement: "Biến động SKU so với lần chạy trước",
    top15Abs: "Top 15 theo chênh lệch tuyệt đối",
    noSkuMovement: "Chưa có biến động SKU giữa các lần chạy.",
  },
  mv: {
    newReason: "Dự báo xuất hiện ở lần chạy mới nhất nhưng bằng 0 ở lần chạy trước.",
    droppedReason: "Dự báo giảm về 0 so với lần chạy trước.",
    manualReason: "Biến động SKU vượt ngưỡng biến động nghiêm trọng của Phase 8.",
    watchReason: "SKU thay đổi đáng kể nhưng dưới ngưỡng nghiêm trọng.",
    acceptReason: "Biến động nằm trong ngưỡng an toàn của SKU.",
  },
  hr: {
    total: "Tổng biến động",
    totalDetail: "{{pct}} so với lần chạy trước; SL {{qty}}.",
    branch: "Biến động chi nhánh/kênh",
    branchDetail: "{{n}} nhóm vượt mức biến động 30% hoặc 5.000 SL.",
    sku: "Biến động SKU",
    skuDetail: "{{n}} SKU vượt mức biến động 50% hoặc 1.000 SL.",
  },
  g8: {
    noRunAction: "Không dùng kết quả",
    noRunDetail: "Dashboard không tìm thấy ID lần chạy dự báo. Hãy làm mới dữ liệu hoặc kiểm tra backend trước khi dùng kết quả dự báo.",
    firstAction: "Chỉ dùng baseline",
    firstDetail: "Đây là lần chạy dự báo đầu tiên được lưu, nên kiểm tra bất thường theo tuần sẽ bật sau lần làm mới tiếp theo.",
    holdAction: "Giữ baseline trước đó",
    holdDetail: "Lần chạy thay đổi tổng {{pct}}; {{branch}} chi nhánh/kênh và {{sku}} SKU vượt ngưỡng an toàn.",
    reviewAction: "Xem xét trước khi xuất",
    reviewDetail: "Lần chạy thay đổi tổng {{pct}}; hãy xem xét các mục biến động lớn trước khi gửi cho người lập kế hoạch.",
    passAction: "Có thể gửi người lập kế hoạch xem xét",
    passDetail: "Biến động giữa các lần chạy nằm trong ngưỡng an toàn. Baseline vẫn là chính thức; AI vẫn là lớp xem xét.",
  },
  rh: {
    latest: "Lần chạy mới nhất",
    previous: "Lần chạy trước",
    totalDelta: "Tổng chênh lệch",
    vsPrevious: "so với lần chạy trước",
    skuDelta: "Chênh lệch số SKU",
    skuDeltaDetail: "hiện tại {{current}} / trước {{previous}}",
  },
  pc: {
    export: "Xem xét xuất CSV",
    exportDetail: "Có thể xuất kết quả kế hoạch, kết quả đề xuất, hàng đợi xem xét AI, nhóm ngoại lệ và kết quả hành động.",
    runId: "ID lần chạy / thời điểm tạo",
    runIdNone: "Chưa tải ID lần chạy dự báo nào.",
    compare: "So sánh với lần chạy trước",
    compareDetail: "Chênh lệch giữa lần chạy mới nhất và lần trước là {{pct}} ({{qty}} đơn vị).",
    compareNone: "Cần ít nhất hai lần làm mới dự báo để so sánh thay đổi theo tuần.",
    rollback: "Hoàn tác / ngưỡng an toàn",
  },
  wc: {
    sales: "Đã làm mới dữ liệu bán hàng",
    salesDetail: "Thực tế đến {{date}}.",
    salesNone: "Chưa tải ngày mart tuần mới nhất.",
    run: "Đã tạo lần chạy dự báo",
    runNone: "Làm mới dữ liệu chưa tạo ra ID lần chạy.",
    prev: "Đã lưu lần chạy trước",
    prevDetail: "Lần chạy trước {{id}} có thể dùng để so sánh.",
    prevNone: "Cần thêm một lần làm mới hằng tuần để bật so sánh với lần chạy trước.",
    guardrail: "Đã kiểm tra ngưỡng an toàn",
    export: "Xuất cho người lập kế hoạch",
    exportPass: "Có thể xuất CSV kế hoạch sau lần kiểm tra cuối.",
    exportOther: "Xuất CSV tạm giữ trước, rồi quyết định có chia sẻ CSV kế hoạch hay không.",
    action: "Hành động tuần này",
    actionHold: "Giữ baseline/kết quả trước cho đến khi xem xét xong các mục biến động lớn.",
    actionReview: "Xem xét biến động chi nhánh và SKU trước khi chia sẻ.",
    actionProceed: "Tiếp tục quy trình xuất hằng tuần như bình thường.",
  },
  mr: {
    currentUse: "Kết quả mặc định khi tín hiệu của các ứng viên còn trái chiều.",
    currentAction: "Giữ làm baseline chính thức",
    v4Use: "Phù hợp nhất với tín hiệu ngắn hạn mới nhất khi nhu cầu gần đây trái với lịch sử mùa vụ.",
    v4Action: "Dùng làm mô hình thách thức ngắn hạn",
    v5Use: "Phù hợp nhất với dự báo ưu tiên mùa vụ, có bảo vệ trước các bước nhảy mùa vụ cực đoan.",
    v5Action: "Giữ làm ứng viên tiếp theo để đưa lên chính thức",
  },
  rp: {
    v5: "Xem xét đưa ứng viên V5 lên chính thức",
    v5Detail: "V5 thắng đủ nhiều cửa sổ cuốn chiếu và cải thiện WAPE hiện tại trên các cửa sổ kiểm định.",
    v4: "Giữ V4 làm mô hình thách thức cho cửa sổ mới nhất",
    v4Detail: "V4 mạnh nhất ở backtest mới nhất, nhưng kiểm định cuốn chiếu chưa đủ ổn định để thay kết quả.",
    v5Review: "Xem xét V5 để đưa lên chính thức",
    v5ReviewDetail: "V5 cải thiện so với hiện tại ở backtest mới nhất và có chính sách ưu tiên mùa vụ rõ ràng hơn.",
    keepDetail: "Các mô hình ứng viên có giá trị tham khảo, nhưng kết quả hiện tại vẫn là baseline chính thức an toàn nhất.",
  },
  vs: {
    needsDataDetail: "Kiểm định hybrid cần dữ liệu so sánh mô hình, hybrid theo quy tắc và backtest cận trên.",
    stableDetail: "Hybrid theo quy tắc thắng phần lớn cửa sổ cuốn chiếu và trung bình ít nhất tốt bằng mô hình đơn tốt nhất.",
    notStableDetail: "Hybrid theo quy tắc thắng ở cửa sổ mới nhất nhưng không vượt mô hình đơn tốt nhất một cách nhất quán ở các cửa sổ trước.",
    overfitDetail: "Hybrid theo quy tắc trùng với cận trên tốt nhất theo tuần trong một cửa sổ ngắn. Hãy kiểm định trên nhiều cửa sổ cuốn chiếu hơn trước khi dùng cho kết quả dự báo.",
    promisingDetail: "Hybrid theo quy tắc cải thiện đáng kể so với mô hình đơn đang đề xuất, nhưng vẫn cần kiểm định cuốn chiếu.",
    watchDetail: "Hybrid theo quy tắc chưa cho thấy cải thiện ổn định đủ để thay mô hình dự báo hiện tại.",
  },
  pp: {
    waitBacktest: "Chờ backtest",
    needsDataReason: "Mô hình cần backtest mới nhất và kiểm định cuốn chiếu trước khi xem xét đưa lên chính thức.",
    preparePilot: "Chuẩn bị thí điểm có kiểm soát",
    promoteReason: "{{model}} thắng phần lớn cửa sổ cuốn chiếu và cải thiện WAPE so với hiện tại.",
    reviewImpact: "Xem xét ảnh hưởng chi nhánh/SKU",
    pilotReason: "{{model}} có tín hiệu độ chính xác hữu ích nhưng chưa đủ ổn định để tự động đưa lên chính thức.",
    keepDiagnostic: "Giữ để chẩn đoán",
    watchReason: "{{model}} chưa vượt kết quả hiện tại một cách đủ nhất quán.",
    waitRolling: "Chờ kiểm định cuốn chiếu",
    ruleNeedsData: "Hybrid theo quy tắc cần kiểm định cuốn chiếu trước khi trở thành ứng viên chính thức.",
    rulePromote: "Hybrid theo quy tắc ổn định qua các cửa sổ cuốn chiếu và có thể so sánh với kết quả hiện tại trong một đợt thí điểm có kiểm soát.",
    extendWindow: "Mở rộng cửa sổ kiểm định",
    rulePilot: "Hybrid theo quy tắc cải thiện kết quả kiểm định gần đây nhưng vẫn cần thêm bằng chứng về rủi ro quá khớp.",
    keepOut: "Không đưa vào kết quả",
    currentReason: "Baseline ổn định dùng cho kết quả dashboard hiện tại trong khi các mô hình ứng viên được xem xét.",
  },
  md: {
    hier: "Dự báo theo chi nhánh/kênh trước, rồi phân bổ xuống SKU theo cơ cấu 12 tuần gần nhất.",
    naive1: "Dùng thực tế tuần trước làm dự báo.",
    ma4: "Dùng trung bình thực tế của 4 tuần trước.",
    ma12: "Dùng trung bình thực tế của 12 tuần trước.",
    seasonal: "Dùng cùng tuần ISO của năm trước khi có dữ liệu.",
    v3: "Ứng viên mùa vụ điều chỉnh theo tín hiệu dữ liệu, kéo về gần nhu cầu hiện tại/gần đây.",
    v4: "Ứng viên pha trộn tín hiệu từ mùa vụ, kết quả hiện tại và nhu cầu tuần trước.",
    v5: "Ứng viên ưu tiên mùa vụ, có ngưỡng an toàn cho chênh lệch lớn giữa mùa vụ và hiện tại.",
    rule: "Mô hình hybrid theo quy tắc, chỉ dùng tín hiệu có trước khi dự báo.",
    benchmark: "Mô hình dự báo đối chiếu.",
  },
  pd: {
    seasonal: "Baseline ưu tiên mùa vụ; giữ mô hình phân cấp để xem xét phân bổ SKU.",
    v4: "Ứng viên V4 mạnh nhất trong kiểm định cuốn chiếu; xem xét độ lệch và phân bổ SKU trước khi đưa lên chính thức.",
    v5: "V5 ưu tiên mùa vụ có ngưỡng an toàn mạnh nhất trong kiểm định cuốn chiếu; xem xét ảnh hưởng SKU trước khi đưa lên chính thức.",
    hybrid: "Ứng viên hybrid; cần kiểm định thêm trước khi dùng làm kết quả dự báo.",
    keep: "Giữ hướng mô hình hiện tại cho đến khi kiểm định cuốn chiếu mạnh hơn.",
  },
  ck: {
    rolling: "Kiểm định cuốn chiếu ủng hộ mùa vụ",
    rollingSeasonal: "Mô hình mùa vụ là đề xuất hiện tại trên {{n}} cửa sổ kiểm định.",
    rollingOther: "Đề xuất hiện tại là {{model}}.",
    branch: "Ảnh hưởng chi nhánh/kênh được kiểm soát",
    branchDetail: "Còn {{n}} nhóm chi nhánh/kênh cần xem xét.",
    branchNone: "Không có nhóm chi nhánh/kênh nào bị gắn Cần xem xét.",
    highGroups: "Đã xem xét các nhóm SKU ưu tiên cao",
    highGroupsDetail: "Còn {{high}} nhóm ưu tiên cao và {{medium}} nhóm ưu tiên trung bình.",
    highGroupsNone: "Không còn nhóm ưu tiên cao; {{medium}} nhóm trung bình vẫn cần xem xét định kỳ.",
    skuList: "Danh sách ảnh hưởng SKU sẵn sàng để xem xét",
    skuListDetail: "{{n}} SKU biến động bị gắn Cần xem xét; hãy xuất CSV để bộ phận kinh doanh xác nhận.",
    skuListNone: "Không có SKU biến động nào bị gắn Cần xem xét.",
  },
  cd: {
    needsDataDetail: "Quyết định về ứng viên cần dữ liệu kiểm định cuốn chiếu, chẩn đoán chi nhánh và ảnh hưởng SKU.",
    promote: "Đưa ứng viên lên chính thức",
    promoteDetail: "Mùa vụ thắng trong kiểm định cuốn chiếu và ảnh hưởng của ứng viên trông có kiểm soát.",
    reviewBefore: "Xem xét trước khi đưa lên chính thức",
    reviewBeforeDetail: "Mùa vụ thắng trong kiểm định cuốn chiếu, nhưng ảnh hưởng chi nhánh/SKU có biến động lớn cần bộ phận kinh doanh xem xét.",
    doNot: "Chưa đưa lên chính thức",
    doNotDetail: "Kiểm định cuốn chiếu không ủng hộ thay kết quả dự báo hiện tại bằng ứng viên V2.",
  },
  gov: {
    needsDataDetail: "Kết quả đề xuất cần có dữ liệu chi tiết trước khi kiểm soát được.",
    reviewOnly: "Chỉ là lớp xem xét",
    reviewOnlyDetail: "Vẫn còn ảnh hưởng SKU lớn hoặc mô hình không thống nhất. Hãy xem xét bằng CSV trước khi thay kết quả chính thức.",
    pilot: "Thí điểm có kiểm soát",
    pilotDetail: "Kết quả đề xuất dùng được cho việc xem xét kế hoạch, nhưng vẫn cần người phụ trách chi nhánh/kênh kiểm tra.",
    ready: "Sẵn sàng xem xét đưa lên chính thức",
    readyDetail: "Phần lớn dòng giữ hiện tại hoặc khớp với mô hình đề xuất theo chi nhánh/kênh; chênh lệch còn lại được kiểm soát.",
  },
  pol: {
    guardrail: "Ngưỡng an toàn chính thức",
    pressure: "Áp lực xem xét",
    pressureDetail: "{{share}} dòng là Cần xem xét; {{watch}} dòng là Theo dõi.",
    agreement: "Mức thống nhất mô hình",
    agreementValue: "{{n}} khớp",
    agreementDetail: "{{n}} dòng không khớp với mô hình đề xuất theo chi nhánh/kênh.",
    volume: "Ảnh hưởng sản lượng",
    volumeDetail: "{{share}} dòng giữ baseline chính thức hiện tại.",
  },
  lvd: {
    total: "Độ chính xác nhu cầu tổng thể. Tín hiệu tốt nhất cho kế hoạch tổng.",
    branch_channel: "Độ chính xác sau khi tách nhu cầu theo chi nhánh và kênh bán.",
    sku: "Độ chính xác phân bổ SKU. SKU bán thưa hoặc thất thường sẽ có sai số cao hơn.",
    other: "Độ chính xác dự báo ở cấp này.",
  },
  btn: {
    exportCsv: "Xuất CSV",
  },
  fc: {
    actualForecast: "Thực tế + Dự báo",
    actualForecastNote: "{{actual}} tuần thực tế gần nhất, {{forecast}} tuần dự báo tiếp theo",
  },
  cand: {
    title: "Ứng viên dự báo V2",
    note: "Ưu tiên mùa vụ, không thay kết quả hiện tại",
    candidateV2: "Ứng viên V2",
    diagnostics: "Chẩn đoán",
    diagCounts: "{{review}} Cần xem xét / {{watch}} Theo dõi",
    highSkuImpact: "Ảnh hưởng SKU lớn",
    promoteChecklist: "Danh mục kiểm tra trước khi đưa lên chính thức",
    readyToPromote: "Sẵn sàng đưa lên chính thức",
    notReady: "Chưa sẵn sàng",
    passed: "Đạt {{done}} / {{total}}",
    candidateTotal: "Tổng ứng viên",
    currentTotal: "Tổng hiện tại",
    hierOutput: "Kết quả phân cấp",
    difference: "Chênh lệch",
    policyText: "Ứng viên V2 theo hướng chính sách cuốn chiếu: dùng nhu cầu cùng tuần ISO năm trước làm baseline ưu tiên mùa vụ, sau đó xem xét riêng phân bổ SKU trước khi thay kết quả dự báo hiện tại.",
  },
  bc: {
    top10: "Top 10",
  },
  sku: {
    title: "Dự báo SKU",
    rowsOf: "{{shown}} / {{total}} dòng",
  },
  rec: {
    title: "Kết quả dự báo đề xuất",
    note: "Lớp xem xét, không thay kết quả hiện tại",
    planningCsv: "CSV kế hoạch",
    productionBaseline: "baseline chính thức",
    recommendedTotal: "Tổng đề xuất",
    reviewOutput: "kết quả xem xét",
    reviewWatch: "Cần xem xét / Theo dõi",
    keepCurrentN: "{{n}} giữ hiện tại",
    alignedReview: "khớp / cần xem xét",
    p1p2: "Hàng đợi P1 / P2",
    decisionPolicy: "Chính sách quyết định",
    unchanged: "Kết quả chính thức hiện tại giữ nguyên cho đến khi lớp xem xét này được duyệt.",
    allDirection: "Mọi hướng",
    allAlignment: "Mọi mức khớp",
    filterGroup: "Chi nhánh / Kênh: {{value}} x",
    filterReason: "Lý do: {{value}} x",
    clearDrill: "Bỏ lọc chi tiết",
    queue: "Hàng đợi ưu tiên xem xét",
    queueNote: "{{n}} dòng đầu trong bộ lọc hiện tại",
    modelAligned: "Mô hình khớp",
    modelReview: "Cần xem xét mô hình",
    byGroup: "Tóm tắt xem xét theo chi nhánh / kênh",
    groupsN: "{{n}} nhóm trong bộ lọc hiện tại",
    increase: "Tăng",
    decrease: "Giảm",
    recVsCurrent: "Đề xuất {{rec}} so với hiện tại {{cur}}",
    byReason: "Tóm tắt xem xét theo lý do",
    reasonsN: "{{n}} lý do trong bộ lọc hiện tại",
  },
  csku: {
    title: "Ảnh hưởng SKU của ứng viên",
    note: "{{n}} mục biến động lớn nhất theo chênh lệch tuyệt đối",
    seasonalityBaseline: "baseline mùa vụ",
    dataSignalAdjusted: "điều chỉnh theo tín hiệu dữ liệu",
    v3vsCurrent: "V3 so với hiện tại",
    v3vsV2: "V3 so với V2",
    groupsWithReview: "{{n}} nhóm có SKU Cần xem xét hoặc Theo dõi",
    cardText: "V2 {{v2}} / V3 {{v3}} so với hiện tại {{cur}}",
    sigRecent12: "12 tuần gần nhất: {{n}}",
    sigActiveWeeks: "Số tuần có bán gần đây: {{n}}",
    sigPriorYear: "Cùng kỳ năm trước: {{n}}",
  },
  bt: {
    summary: "Tóm tắt backtest",
    lastWeeks: "{{n}} tuần thực tế gần nhất",
    modelUsed: "Mô hình được dùng",
    wapeText: "Tổng sai số dự báo tuyệt đối chia cho tổng số lượng thực tế. Càng thấp càng tốt.",
    biasText: "(Dự báo − thực tế) chia cho thực tế. Âm là dự báo thấp; dương là dự báo cao.",
    level: "Cấp",
    levelText: "Tổng cho thấy độ chính xác kế hoạch, Chi nhánh/Kênh cho thấy độ chính xác phân bổ, SKU cho thấy rủi ro phân bổ ở cấp mặt hàng.",
  },
  mc: {
    title: "So sánh mô hình",
    note: "Đề xuất dựa trên kiểm định cuốn chiếu khi có",
    exportModels: "Xuất mô hình",
    recommendedModel: "Mô hình đề xuất",
    selectedRolling: "Chọn theo kiểm định cuốn chiếu: {{model}} thắng {{wins}} / {{windows}} cửa sổ. {{policy}}",
    selectedWape: "Chọn theo WAPE trước, rồi theo độ lệch khi WAPE chênh nhau trong 3 điểm %. WAPE hiện tại {{wape}}, độ lệch {{bias}}.",
    currentOutput: "Kết quả dự báo hiện tại",
    rollingWinner: "Mô hình thắng cuốn chiếu",
    hybridStatus: "Trạng thái hybrid",
    nextDirection: "Hướng tiếp theo",
    latestV4Gap: "Chênh lệch V4 mới nhất",
    v4MinusCurrent: "WAPE V4 trừ hiện tại",
    latestV5Gap: "Chênh lệch V5 mới nhất",
    v5MinusCurrent: "WAPE V5 trừ hiện tại",
    promoCandidate: "Ứng viên đưa lên chính thức",
    path: "Lộ trình {{label}}",
    promoNote: "Đưa lên chính thức nghĩa là thí điểm có kiểm soát trước, không tự động thay kết quả chính thức.",
    lowVolumeTitle: "Số lượng thực tế nhỏ nên WAPE có thể trông rất cao. Hãy xem xét ảnh hưởng số lượng trước khi dùng đề xuất này.",
  },
  feat: {
    candidate: "Ứng viên đặc trưng",
    candidateNote: "Backtest cấp tổng dùng tín hiệu ngày lễ, tồn kho và độ trễ khuyến mãi.",
    bestWape: "WAPE đặc trưng tốt nhất",
    gapVsCurrent: "Chênh lệch so với hiện tại",
    backtestWeeks: "Số tuần backtest",
    modelSummary: "Tóm tắt mô hình",
    modelSummaryNote: "Độ chính xác cấp tổng theo mô hình ứng viên",
    signalCoverage: "Độ phủ tín hiệu đặc trưng",
    signalCoverageNote: "Rủi ro ngày lễ, tồn kho và khuyến mãi/giá trong bộ lọc hiện tại",
    highPriorityN: "{{n}} ưu tiên cao",
    trend: "Xu hướng backtest đặc trưng",
    trendNote: "Baseline hiện tại so với {{model}} theo tuần",
    weekLabel: "Tuần {{label}}",
    adjustment: "Điều chỉnh đặc trưng",
    verdict: "Kết luận đặc trưng theo tuần",
    verdictNote: "Mức cải thiện WAPE của ứng viên so với baseline hiện tại",
    candLower: "WAPE ứng viên thấp hơn",
    candHigher: "WAPE ứng viên cao hơn",
    avgImprovement: "Cải thiện TB",
    currentMinusCand: "WAPE hiện tại trừ WAPE ứng viên",
    bestWorst: "Tuần tốt nhất / kém nhất",
    weeklyDetail: "Chi tiết backtest theo tuần",
    weeklyDetailNote: "Thực tế so với dự báo đặc trưng theo tuần",
    actionable: "Kết quả AI có thể hành động",
    actionableNote: "Cách dùng đề xuất theo chi nhánh/kênh; không tự động thay kết quả chính thức",
    pilotCandidates: "Ứng viên thí điểm",
    pilotCandNote: "đặc trưng cải thiện WAPE trên sản lượng đáng kể",
    keepBaseline: "Giữ baseline",
    worsens: "đặc trưng làm WAPE kém đi",
    watchLow: "Theo dõi / Sản lượng thấp",
    insufficient: "tín hiệu chưa đủ để thay kết quả",
    weightedGain: "Mức cải thiện có trọng số của thí điểm",
    weightedGainNote: "Điểm WAPE có trọng số theo số lượng thực tế",
    topPilots: "Ứng viên thí điểm hàng đầu",
    bcGroupsN: "{{n}} nhóm chi nhánh/kênh",
    bcImpact: "Ảnh hưởng đặc trưng theo chi nhánh / kênh",
    bcImpactNote: "Nơi điều chỉnh đặc trưng có ích hoặc gây hại",
    reviewQueue: "Hàng đợi xem xét AI",
    reviewQueueNote: "{{shown}} / {{total}} rủi ro SKU",
    totalRisks: "Tổng rủi ro",
    mediumPriority: "Ưu tiên trung bình",
    topRisk: "Rủi ro lớn nhất",
    allRisk: "Mọi rủi ro",
    exceptions: "Nhóm ngoại lệ AI",
    exceptionsNote: "Số lượng dự báo gom theo rủi ro và hành động đề xuất",
    exceptionBuckets: "Nhóm ngoại lệ",
    skuRowsN: "{{n}} dòng SKU",
    forecastAtRisk: "Dự báo có rủi ro",
    currentFilter: "bộ lọc hiện tại",
    highPriorityForecast: "Dự báo ưu tiên cao",
    bucketHasHigh: "nhóm có dòng ưu tiên cao",
    topException: "Ngoại lệ lớn nhất",
    forecastQtyN: "SL dự báo {{n}}",
    riskByBc: "Rủi ro theo chi nhánh / kênh",
    top10Filter: "Top 10 trong bộ lọc hiện tại",
  },
  wt: {
    title: "Theo dõi mô hình theo tuần",
    note: "Dùng sau mỗi lần làm mới dữ liệu hằng tuần để xem mô hình baseline, AI hay tốc độ bán gần đây đang cải thiện.",
    export: "Xuất dữ liệu theo dõi",
    latestWeek: "Tuần mới nhất",
    actualQtyN: "SL thực tế {{n}}",
    noRows: "Không có dòng theo dõi",
    baselineAvg: "WAPE TB của baseline",
    lastTracked: "{{n}} tuần theo dõi gần nhất",
    bestAvg: "WAPE TB của mô hình tốt nhất",
    bestAvgNote: "mô hình thắng mỗi tuần sau khi có thực tế",
    topWinner: "Mô hình thắng nhiều nhất",
    winsN: "{{n}} lần thắng trong cửa sổ gần đây",
    empty: "Không có dòng theo dõi hằng tuần",
  },
  wmc: {
    title: "So sánh mô hình theo tuần",
    note: "Mô hình thắng và dự báo theo tuần",
    export: "Xuất theo tuần",
  },
  diag: {
    title: "Chẩn đoán ứng viên",
    note: "So sánh V3, V4 và V5 với kết quả dự báo hiện tại",
    within: "trong phạm vi 0,5 điểm WAPE",
    v3Worse: "V3 kém hơn",
    avgGap: "Chênh lệch WAPE TB",
    v3Minus: "V3 trừ hiện tại",
    v4Beats: "V4 tốt hơn hiện tại",
    v4Worse: "V4 kém hơn",
    v4AvgGap: "Chênh lệch TB của V4",
    v4Minus: "V4 trừ hiện tại",
    v5Beats: "V5 tốt hơn hiện tại",
    v5Worse: "V5 kém hơn",
    v5AvgGap: "Chênh lệch TB của V5",
    v5Minus: "V5 trừ hiện tại",
  },
  hyb: {
    title: "Mô phỏng hybrid",
    note: "Mô hình tốt nhất cho mỗi tuần backtest",
    wape: "WAPE hybrid",
    better: "Tốt hơn đề xuất {{n}} điểm %",
    check: "Kiểm tra khả năng cải thiện",
    text: "Đây là mô phỏng cận trên, dùng mô hình tốt nhất trong quá khứ cho mỗi tuần. Nó giúp ước lượng có đáng xây một quy tắc hybrid thực sự hay không.",
    winningWeeks: "{{n}} tuần thắng",
  },
  rule: {
    title: "Hybrid theo quy tắc v2",
    note: "Chỉ dùng thông tin có trước tuần dự báo",
    validationStatus: "Trạng thái kiểm định",
    upperGap: "Chênh lệch so với cận trên",
    vsRecommended: "{{n}} điểm % so với đề xuất",
    check: "Kiểm tra hybrid thực tế",
    modelsUsed: "Dùng {{n}} mô hình",
    text: "Quy tắc v2 dựa vào mức đồng thuận giữa các mô hình ứng viên: dùng Naive khi sát với Hierarchical, dùng Seasonal khi khớp với MA12 hoặc phát hiện tuần trước tăng đột biến, nếu không thì dùng MA12 hoặc quay về Hierarchical.",
  },
  roll: {
    title: "Kiểm định cuốn chiếu",
    note: "Trên {{n}} cửa sổ: V4 thắng {{v4}} / V5 thắng {{v5}} / Quy tắc thắng {{rule}}",
    v4Wins: "V4 thắng cuốn chiếu",
    v5Wins: "V5 thắng cuốn chiếu",
    ruleWins: "Quy tắc thắng cuốn chiếu",
    ruleAvgGap: "Chênh lệch TB của quy tắc",
    ruleMinusBest: "Quy tắc trừ mô hình đơn tốt nhất",
  },
  acc: {
    title: "Độ chính xác theo cấp",
    note: "WAPE càng thấp càng tốt",
    bias: "Độ lệch {{v}}",
  },
  wavf: {
    title: "Thực tế so với dự báo theo tuần",
    note: "Cột: Thực tế / Dự báo, đường: WAPE",
  },
  wbd: {
    note: "Thực tế so với dự báo theo mô hình",
  },
};

const zh = {
  top: {
    title: "需求预测",
    actualsThrough: "实际数据截至 {{date}}",
    subtitle: "每周 ERPQuantity 预测",
    env: "测试环境 API",
    refreshDataTitle: "刷新预测数据集、预测结果和回测",
    refreshData: "刷新数据",
    refresh: "刷新",
    loading: "加载中",
  },
  f: {
    horizon: "预测周期",
    weeksN: "{{n}} 周",
    noMatches: "无匹配项",
    clear: "清除",
  },
  msg: {
    refreshRunning: "正在刷新数据：数据集、预测和回测，可能需要 2-3 分钟。",
    refreshed: "预测数据已刷新：{{steps}}，时间 {{time}}",
    done: "完成",
    cannotParse: "无法读取 {{endpoint}} 的响应",
    cannotLoad: "无法加载 {{endpoint}}",
  },
  stat: {
    w1: "W+1 预测",
    w4: "4 周预测",
    weeksAvailable: "可用 {{n}} 周",
    total: "预测总量",
    skus: "{{n}} 个 SKU",
    wape: "回测 WAPE",
    totalLevel: "总体层级",
  },
  tab: {
    forecast: "预测",
    sku: "SKU 明细",
    backtest: "回测",
  },
  level: {
    total: "总体",
    branch_channel: "分公司 / 渠道",
    sku: "SKU",
  },
  u: {
    pts: "{{n}} 个百分点",
    qty: "数量 {{n}}",
    rows: "{{n}} 行",
    weeks: "周",
    ofWindows: "共 {{n}} 个窗口",
    skuMovers: "SKU 变动项",
  },
  v: {
    all: "全部",
    review: "需复核",
    watch: "关注",
    ok: "正常",
    keepCurrent: "保持现状",
    increaseForecast: "上调预测",
    decreaseForecast: "下调预测",
    aligned: "一致",
    high: "高",
    medium: "中",
    low: "低",
    helps: "有帮助",
    hurts: "有损害",
    neutral: "中性",
    unknown: "未知",
    unknownReason: "原因未知",
    unknownRisk: "风险未知",
    reviewManually: "人工复核",
    needsData: "需要数据",
    keepBaseline: "保持基线",
    pilotCandidate: "试点候选",
    reviewCandidate: "待复核候选",
    hold: "暂缓",
    lowVolumeWatch: "低销量关注",
    reviewNewDemand: "复核新增需求",
    reviewDroppedDemand: "复核消失的需求",
    manualReview: "人工复核",
    acceptMovement: "接受变动",
    pass: "通过",
    noRunLoaded: "未加载运行批次",
    ready: "就绪",
    missing: "缺失",
    done: "已完成",
    waiting: "等待中",
    reviewFirst: "先复核",
    proceed: "继续",
    policyReady: "政策就绪",
    rollingStable: "滚动验证稳定",
    notStableYet: "尚不稳定",
    highOverfitRisk: "过拟合风险高",
    promising: "有前景",
    promoteReview: "升级复核",
    watchOnly: "仅关注",
    productionBaseline: "正式基线",
    ruleWins: "规则胜出",
    close: "接近",
    featureHelps: "特征有帮助",
    featureHurts: "特征有损害",
    lowVolume: "低销量",
    noFeatureSignal: "无特征信号",
    strongCandidate: "强候选",
    v3Beats: "V3 优于当前",
    v3Worse: "V3 劣于当前",
  },
  col: {
    productName: "产品名称",
    branch: "分公司",
    channel: "渠道",
    collection: "系列",
    category: "类别",
    candidateQty: "候选数量",
    candidateV3Qty: "候选 V3 数量",
    currentQty: "当前数量",
    deltaQty: "差异数量",
    deltaPctTxt: "差异率",
    v3DeltaVsCurrentQty: "V3 相对当前差异数量",
    v3DeltaVsCurrentPct: "V3 相对当前差异率",
    v3DeltaVsV2Qty: "V3 相对 V2 差异数量",
    status: "状态",
    reason: "原因",
    dataSignal: "数据信号",
    candidateV3Rule: "候选 V3 规则",
    recent12Qty: "近 12 周数量",
    recent4Qty: "近 4 周数量",
    recent12ActiveWeeks: "近 12 周有销售周数",
    priorYearHorizonQty: "去年同期数量",
    currentForecast: "当前预测",
    candidateV3Forecast: "候选 V3 预测",
    recommendedForecast: "建议预测",
    deltaPct: "差异 %",
    recommendedModel: "建议模型",
    bcRecommendedModel: "分公司渠道建议模型",
    modelAlignment: "模型一致性",
    reviewPriority: "复核优先级",
    bcModelStatus: "分公司渠道模型状态",
    bcModelImprovement: "分公司渠道模型改善",
    direction: "方向",
    recent12Actual: "近 12 周实际",
    recent4Actual: "近 4 周实际",
    priorYearHorizon: "去年同期",
    actualsThrough: "实际数据截至",
    horizonWeeks: "预测周数",
    currentProductionForecast: "当前正式预测",
    recommendedPlanningForecast: "建议计划预测",
    plannerOverride: "计划员调整预测",
    finalApproved: "最终批准预测",
    reviewStatus: "复核状态",
    bcModel: "分公司渠道模型",
    bcModelShort: "分公司渠道模型",
    plannerComment: "计划员备注",
    owner: "负责人",
    decision: "决定",
    year: "年份",
    week: "周",
    actualQty: "实际数量",
    baselineForecast: "基线预测",
    baselineWape: "基线 WAPE",
    baselineBias: "基线偏差",
    featureForecast: "特征预测",
    featureWape: "特征 WAPE",
    featureBias: "特征偏差",
    ma4Forecast: "MA4 预测",
    ma4Bias: "MA4 偏差",
    ma12Forecast: "MA12 预测",
    ma12Bias: "MA12 偏差",
    winner: "胜出模型",
    winnerForecast: "胜出模型预测",
    winnerWape: "胜出模型 WAPE",
    baselineGapVsWinnerPts: "基线与胜出模型差距（百分点）",
    section: "部分",
    model: "模型",
    forecastQty: "预测数量",
    bias: "偏差",
    currentWape: "当前 WAPE",
    seasonalWape: "季节性 WAPE",
    improvementVsCurrent: "相对当前的改善",
    hierForecast: "分层预测",
    hierWape: "分层 WAPE",
    seasonalForecast: "季节性预测",
    v3Forecast: "V3 预测",
    v4Forecast: "V4 预测",
    v5Forecast: "V5 预测",
    naiveForecast: "朴素预测",
    naiveWape: "朴素 WAPE",
    priority: "优先级",
    risk: "风险",
    action: "操作建议",
    lowerQty: "下限数量",
    upperQty: "上限数量",
    promoShare: "促销数量占比",
    highDiscountShare: "高折扣数量占比",
    priceDropShare: "降价数量占比",
    stockoutShare: "缺货数量占比",
    invToSales: "库存销售比",
    recommendedAction: "建议操作",
    rows: "行数",
    highPriorityRows: "高优先级行数",
    mediumPriorityRows: "中优先级行数",
    avgAdjustmentPct: "平均调整率",
    signalReason: "信号原因",
    guardrailDecision: "护栏判定",
    guardrailAction: "护栏操作",
    latestRunId: "最新运行 ID",
    latestRunTime: "最新运行时间",
    previousRunId: "上次运行 ID",
    previousRunTime: "上次运行时间",
    totalDeltaQty: "总差异数量",
    totalDeltaPct: "总差异率",
    previousForecast: "上次预测",
    plannerDecision: "计划员决定",
    candidate: "候选",
    current: "当前",
    delta: "差异",
    recentRunRate: "近期销售速率",
    seasonalRecent: "季节性 / 近期",
    forecast: "预测",
    lower: "下限",
    upper: "上限",
    confidence: "置信度",
    candidateV3: "候选 V3",
    recommended: "建议",
    alignment: "一致性",
    v3Delta: "V3 差异",
    v3DeltaPct: "V3 差异 %",
    v3Rule: "V3 规则",
    skus: "SKU 数",
    previous: "上次",
    largestSku: "变动最大的 SKU",
    location: "位置",
    control: "控制项",
    detail: "详情",
    generated: "生成时间",
    runId: "运行 ID",
    latestWape: "最新 WAPE",
    latestBias: "最新偏差",
    rollingWins: "滚动胜出次数",
    rollingGap: "滚动差距",
    actual: "实际",
    absError: "绝对误差",
    signal: "信号",
    highPriority: "高优先级",
    recent4w: "近 4 周",
    metric: "指标",
    metricMeaning: "指标含义",
    verdict: "判定",
    candidateWape: "候选 WAPE",
    improvement: "改善",
    adjustment: "调整",
    gain: "提升",
    avgAdj: "平均调整",
    promo: "促销",
    stockout: "缺货",
    invSales: "库存/销售",
    option: "方案",
    whenToUse: "适用场景",
    recommendation: "建议",
    bestWape: "最佳 WAPE",
    aiForecast: "AI 预测",
    baselineGap: "基线差距",
    gap: "差距",
    v4Gap: "V4 差距",
    v5Gap: "V5 差距",
    outcome: "结果",
    diagnosis: "诊断",
    v4Diagnosis: "V4 诊断",
    v5Diagnosis: "V5 诊断",
    chosenModel: "选用模型",
    recentCv4: "近期 CV4",
    window: "窗口",
    bestModel: "最佳模型",
    ruleWape: "规则 WAPE",
    bestSingleWape: "最佳单一模型 WAPE",
    ruleBias: "规则偏差",
    v4VsCurrent: "V4 对比当前",
    v5VsCurrent: "V5 对比当前",
    actualErp: "实际 ERPQuantity",
    forecastErp: "预测 ERPQuantity",
  },
  txt: {
    keepCurrentBaseline: "保持当前基线",
    keepCurrentOutput: "保持当前输出",
    ruleHybrid: "规则混合",
    latestRunGenerated: "最新运行 {{id}}，生成于 {{time}}。",
  },
  csr: {
    largePct: "百分比差距大",
    largeQty: "数量影响大",
    moderatePct: "百分比差距中等",
    moderateQty: "数量影响中等",
  },
  cr: {
    largeGap: "当前与候选差距大",
    priorAbove: "去年需求远高于近期销售速率",
    priorBelow: "去年需求远低于近期销售速率",
    needsReview: "需业务复核",
    moderateGap: "与当前预测差距中等",
    broadlyAligned: "候选基本一致",
  },
  conf: {
    high: "分公司/渠道及 SKU 层级历史稳定",
    medium: "历史可用，需复核 SKU 或渠道变化",
    low: "SKU 历史稀少或为分摊需求，请人工复核",
    bucket: "预测置信度分组",
    title: "预测置信度",
    note: "行数与数量占比",
  },
  sig: {
    promo: "促销 / 价格",
    promoNote: "折扣、积分、降价或促销滞后带来的需求风险",
    promoMetric: "平均信号占比",
    stock: "库存 / 缺货",
    stockNote: "可能流失的需求或受低库存限制的需求",
    stockMetric: "平均缺货占比",
    holiday: "节假日",
    holidayNote: "近期销售可能包含节假日带来的上升或下降",
    holidayMetric: "信号行数",
    noSales: "近期无销售",
    noSalesNote: "有预测但最近 4 周无需求",
    noSalesMetric: "有风险的预测量",
  },
  fg: {
    needsDataDetail: "特征模型需要先有回测结果，才能用于计划复核。",
    keepAction: "特征信号仅作为复核队列使用",
    keepDetail: "在最新回测中，特征候选在总体层级未优于当前基线。",
    pilotAction: "在筛选后的分公司/渠道复核中试点特征模型",
    pilotDetail: "特征候选改善了 WAPE，且高优先级风险占比可控。",
    reviewAction: "替换输出前先作为诊断使用",
    reviewDetail: "特征候选有一定的准确度提升，但风险队列仍需监控。",
    holdAction: "暂不升级特征模型",
    holdDetail: "与当前基线相比，特征候选还不够稳定。",
    accuracy: "准确度影响",
    accuracyDetail: "负值表示特征模型的 WAPE 低于当前基线。",
    risk: "风险压力",
    riskValue: "高 {{n}}",
    riskDetail: "AI 复核队列中有 {{share}} 为高优先级。",
    next: "下一步",
    nextDetail: "当前筛选下检测到 {{n}} 个特征信号组。",
    title: "特征护栏建议",
  },
  fa: {
    watchReason: "特征信号不足以改变计划输出。",
    pilotReason: "特征模型在有意义的销量上充分改善了 WAPE。",
    keepReason: "特征模型使 WAPE 变差；该分公司/渠道保持当前基线。",
    lowReason: "销量太小，无法做出稳定的模型判断。",
  },
  p7: {
    backtest: "特征模型回测",
    backtestDetail: "可用 {{n}} 周；已加载当前模型与特征模型的对比。",
    signals: "外部信号已接入",
    signalsDetail: "从节假日、库存、促销、价格或无销售数据中检测到 {{n}} 个信号组。",
    queue: "风险队列可执行",
    queueDetail: "{{risks}} 个 SKU 风险归入 {{buckets}} 个异常分组。",
    pilot: "试点决策输出",
    pilotDetail: "试点候选组 {{pilot}} 个；保持基线组 {{keep}} 个。",
    guardrail: "护栏状态",
    guardrailDetail: "{{label}}：{{action}}。",
    pilotReady: "可以试点",
    pilotReadyDetail: "Phase 7 界面已可用于受控试点复核。在 Phase 8 流程确定前，正式输出保持不变。",
    mvpReady: "MVP 就绪",
    mvpReadyDetail: "Phase 7 界面可用于计划复核。剩余工作主要是加深验证和正式流程。",
    needsWork: "仍需完善",
    needsWorkDetail: "Phase 7 还需要更多特征依据，才能用于计划决策。",
    readiness: "Phase 7 就绪度",
    checksPassed: "{{done}} / {{total}} 项检查通过",
  },
  p8: {
    official: "正式输出",
    officialDetail: "正式预测仍为 Hierarchical recent12 SKU mix，AI/特征输出不会自动替代它。",
    aiUsage: "AI 使用方式",
    aiUsageValue: "试点 / 复核层",
    aiUsageDetail: "仅对 Actionable AI Output 中标记为试点候选的分公司/渠道组使用 AI。",
    weekly: "每周刷新",
    weeklyValue: "周一手动刷新",
    weeklyDetail: "负责人：您。每周销售数据更新完成后，运行刷新数据。",
    failure: "刷新失败",
    failureValue: "显示错误，保持基线",
    failureDetail: "如果刷新失败或超时，看板会显示错误，我们会在使用新输出前进行排查。",
    title: "Phase 8 正式流程草案",
    note: "正式合并前已锁定的政策",
    finalLogic: "最终输出逻辑",
    finalLogicValue: "基线为正式输出；AI 仅用于试点/复核",
    bestOption: "最佳方案：将当前基线作为正式预测，AI/特征输出仅用于受控的分公司/渠道试点；AI 输出异常或未经复核时，一律保持基线。",
    runGuardrail: "运行护栏判定",
    branchMovers: "分公司变动",
    weeklyChecklist: "每周运营检查清单",
    weeklyChecklistNote: "每周一刷新数据后使用",
    holdDetail: "暂缓原因明细",
    holdReviewThese: "使用新运行前请先复核以下各项",
    guardrailDiagnostics: "护栏诊断",
    exportHold: "导出暂缓 CSV",
    noReviewMovers: "无需复核的变动项",
    allInside: "所有 SKU 变动都在护栏范围内。",
    reviewGroups: "复核分组",
    byBranchChannel: "按分公司 / 渠道",
    noHoldGroups: "当前筛选下没有暂缓分组。",
    topSkuActions: "主要 SKU 操作",
    top12: "需要关注的前 12 个变动项",
    noSkuAction: "无需 SKU 操作。",
    controls: "正式控制项",
    controlsReady: "{{ready}} / {{total}} 就绪或政策就绪",
    runHistory: "预测运行历史",
    runHistoryCompared: "最新运行与上次运行对比",
    runHistoryWaiting: "等待至少两次运行",
    recentRuns: "近期预测运行",
    latest5: "最近 5 次",
    noRunHistory: "未加载运行历史。",
    bcMovement: "分公司 / 渠道变动",
    top10Abs: "按绝对差异前 10",
    noPrevMovement: "尚无相对上次运行的变动。",
    skuMovement: "SKU 相对上次运行的变动",
    top15Abs: "按绝对差异前 15",
    noSkuMovement: "尚无 SKU 运行变动。",
  },
  mv: {
    newReason: "最新运行出现了预测，但上次运行为 0。",
    droppedReason: "与上次运行相比，预测降为 0。",
    manualReason: "SKU 变动超出 Phase 8 严重变动护栏。",
    watchReason: "SKU 变化明显，但低于严重护栏。",
    acceptReason: "变动在 SKU 护栏范围内。",
  },
  hr: {
    total: "总变动",
    totalDetail: "相对上次运行 {{pct}}；数量 {{qty}}。",
    branch: "分公司/渠道变动",
    branchDetail: "{{n}} 个组的变动超过 30% 或 5,000 件。",
    sku: "SKU 变动",
    skuDetail: "{{n}} 个 SKU 的变动超过 50% 或 1,000 件。",
  },
  g8: {
    noRunAction: "不要使用输出",
    noRunDetail: "看板找不到预测运行 ID。使用预测输出前，请刷新数据或检查后端。",
    firstAction: "仅使用基线",
    firstDetail: "这是第一次保存的预测运行，周环比异常检查将在下次刷新后启用。",
    holdAction: "保持上次基线",
    holdDetail: "运行总变动 {{pct}}；{{branch}} 个分公司/渠道和 {{sku}} 个 SKU 变动超出护栏。",
    reviewAction: "导出前先复核",
    reviewDetail: "运行总变动 {{pct}}；与计划员共享前，请先复核主要变动项。",
    passAction: "可交计划员复核",
    passDetail: "运行间变动在护栏范围内。基线仍为正式输出；AI 仍为复核层。",
  },
  rh: {
    latest: "最新运行",
    previous: "上次运行",
    totalDelta: "总差异",
    vsPrevious: "相对上次运行",
    skuDelta: "SKU 数差异",
    skuDeltaDetail: "当前 {{current}} / 上次 {{previous}}",
  },
  pc: {
    export: "导出 CSV 复核",
    exportDetail: "可导出计划输出、建议输出、AI 复核队列、异常分组和操作输出。",
    runId: "运行 ID / 生成时间",
    runIdNone: "尚未加载预测运行 ID。",
    compare: "与上次运行对比",
    compareDetail: "最新运行与上次运行的差异为 {{pct}}（{{qty}} 件）。",
    compareNone: "至少需要两次预测刷新运行才能比较周环比变化。",
    rollback: "回滚 / 护栏",
  },
  wc: {
    sales: "销售数据已刷新",
    salesDetail: "实际数据截至 {{date}}。",
    salesNone: "未加载最新的周数据集日期。",
    run: "已生成预测运行",
    runNone: "刷新数据未生成运行 ID。",
    prev: "上次运行已归档",
    prevDetail: "上次运行 {{id}} 可用于对比。",
    prevNone: "还需再进行一次每周刷新，才能启用与上次运行的对比。",
    guardrail: "护栏已检查",
    export: "计划员导出",
    exportPass: "最终抽查后即可导出计划 CSV。",
    exportOther: "先导出暂缓 CSV，再决定是否共享计划 CSV。",
    action: "本周操作",
    actionHold: "在复核主要变动项之前，保持基线/上次输出。",
    actionReview: "共享前先复核分公司和 SKU 变动项。",
    actionProceed: "按常规每周导出流程进行。",
  },
  mr: {
    currentUse: "候选信号仍不一致时的默认输出。",
    currentAction: "保留为正式基线",
    v4Use: "当近期需求与季节性历史相矛盾时，最适合用于最新的短期信号。",
    v4Action: "作为短期挑战模型使用",
    v5Use: "最适合季节性优先、并能防范极端季节性跳变的预测。",
    v5Action: "保留为下一个升级候选",
  },
  rp: {
    v5: "复核升级 V5 候选",
    v5Detail: "V5 在足够多的滚动窗口中胜出，并在各验证窗口中改善了当前 WAPE。",
    v4: "保留 V4 作为最新窗口的挑战模型",
    v4Detail: "V4 在最新回测中表现最好，但滚动验证还不够稳定，不足以替换输出。",
    v5Review: "复核 V5 是否升级",
    v5ReviewDetail: "V5 在最新回测中优于当前，且季节性优先策略更清晰。",
    keepDetail: "候选模型有参考价值，但当前输出仍是最稳妥的正式基线。",
  },
  vs: {
    needsDataDetail: "混合验证需要模型对比、规则混合和上限回测数据。",
    stableDetail: "规则混合在大多数滚动窗口中胜出，平均表现不低于最佳单一模型。",
    notStableDetail: "规则混合在最新窗口胜出，但在之前的滚动窗口中未能稳定优于最佳单一模型。",
    overfitDetail: "规则混合在短窗口内与每周最佳上限相同。用于预测输出前，请在更多滚动窗口上验证。",
    promisingDetail: "规则混合较当前建议的单一模型有明显改善，但仍需滚动窗口验证。",
    watchDetail: "规则混合尚未显示出足以替换当前预测模型的稳定改善。",
  },
  pp: {
    waitBacktest: "等待回测",
    needsDataReason: "升级复核前，模型需要最新回测和滚动验证。",
    preparePilot: "准备受控试点",
    promoteReason: "{{model}} 在大多数滚动窗口中胜出，且 WAPE 优于当前。",
    reviewImpact: "复核分公司/SKU 影响",
    pilotReason: "{{model}} 有有用的准确度信号，但还不够稳定，不能自动升级。",
    keepDiagnostic: "保留作诊断用",
    watchReason: "{{model}} 尚未足够稳定地优于当前输出。",
    waitRolling: "等待滚动验证",
    ruleNeedsData: "规则混合需要滚动验证后才能成为正式候选。",
    rulePromote: "规则混合在各滚动窗口中表现稳定，可在受控试点中与当前输出对比。",
    extendWindow: "扩大验证窗口",
    rulePilot: "规则混合改善了近期验证，但仍需更多依据排除过拟合风险。",
    keepOut: "不纳入输出",
    currentReason: "候选模型复核期间，当前看板输出使用的稳定基线。",
  },
  md: {
    hier: "先预测分公司/渠道，再按最近 12 周结构分摊到 SKU。",
    naive1: "使用上一周实际值作为预测。",
    ma4: "使用前 4 周实际值的平均。",
    ma12: "使用前 12 周实际值的平均。",
    seasonal: "如有数据，使用去年同一 ISO 周。",
    v3: "按数据信号调整的季节性候选，向当前/近期需求收敛。",
    v4: "融合季节性、当前输出和上周需求的信号混合候选。",
    v5: "季节性优先候选，对季节性与当前的较大差距设有护栏。",
    rule: "仅使用预测前信号的规则混合模型。",
    benchmark: "基准预测模型。",
  },
  pd: {
    seasonal: "季节性优先基线；SKU 分摊复核保留分层模型。",
    v4: "V4 候选在滚动验证中最强；升级前请复核偏差和 SKU 分摊。",
    v5: "V5 季节性优先护栏在滚动验证中最强；升级前请复核 SKU 影响。",
    hybrid: "混合候选；用作预测输出前需进一步验证。",
    keep: "在滚动验证更有力之前，保持当前模型方向。",
  },
  ck: {
    rolling: "滚动验证支持季节性",
    rollingSeasonal: "在 {{n}} 个验证窗口中，季节性模型为当前建议。",
    rollingOther: "当前建议为 {{model}}。",
    branch: "分公司/渠道影响可控",
    branchDetail: "仍有 {{n}} 个分公司/渠道组需要复核。",
    branchNone: "没有被标记为需复核的分公司/渠道组。",
    highGroups: "高优先级 SKU 组已复核",
    highGroupsDetail: "仍有 {{high}} 个高优先级组和 {{medium}} 个中优先级组。",
    highGroupsNone: "已无高优先级组；{{medium}} 个中优先级组仍需例行复核。",
    skuList: "SKU 影响清单可复核",
    skuListDetail: "{{n}} 个 SKU 变动项被标记为需复核；请导出 CSV 交业务确认。",
    skuListNone: "没有被标记为需复核的 SKU 变动项。",
  },
  cd: {
    needsDataDetail: "候选决策需要滚动验证、分公司诊断和 SKU 影响数据。",
    promote: "升级候选",
    promoteDetail: "季节性在滚动验证中胜出，候选影响看起来可控。",
    reviewBefore: "升级前先复核",
    reviewBeforeDetail: "季节性在滚动验证中胜出，但分公司/SKU 影响中有较大变动项需业务复核。",
    doNot: "暂不升级",
    doNotDetail: "滚动验证不支持用候选 V2 替换当前预测输出。",
  },
  gov: {
    needsDataDetail: "建议输出需要明细数据后才能管控。",
    reviewOnly: "仅作复核层",
    reviewOnlyDetail: "仍存在较大的 SKU 影响或模型分歧。更改正式输出前，请先用 CSV 复核。",
    pilot: "受控试点",
    pilotDetail: "建议输出可用于计划复核，但仍需分公司/渠道负责人检查。",
    ready: "可进行升级复核",
    readyDetail: "大多数行保持现状或与分公司/渠道模型建议一致；其余差异可控。",
  },
  pol: {
    guardrail: "正式护栏",
    pressure: "复核压力",
    pressureDetail: "{{share}} 的行为需复核；{{watch}} 行为关注。",
    agreement: "模型一致性",
    agreementValue: "{{n}} 个一致",
    agreementDetail: "{{n}} 行与分公司/渠道模型建议不一致。",
    volume: "销量影响",
    volumeDetail: "{{share}} 的行保持当前正式基线。",
  },
  lvd: {
    total: "总体需求准确度，最适合总量计划参考。",
    branch_channel: "按分公司和销售渠道拆分需求后的准确度。",
    sku: "SKU 分摊准确度。销售稀少或不规则的 SKU 误差会更高。",
    other: "该层级的预测准确度。",
  },
  btn: {
    exportCsv: "导出 CSV",
  },
  fc: {
    actualForecast: "实际 + 预测",
    actualForecastNote: "最近 {{actual}} 周实际，未来 {{forecast}} 周预测",
  },
  cand: {
    title: "预测 V2 候选",
    note: "季节性优先，不替换当前输出",
    candidateV2: "候选 V2",
    diagnostics: "诊断",
    diagCounts: "需复核 {{review}} / 关注 {{watch}}",
    highSkuImpact: "SKU 影响大",
    promoteChecklist: "升级检查清单",
    readyToPromote: "可以升级",
    notReady: "尚未就绪",
    passed: "{{done}} / {{total}} 已通过",
    candidateTotal: "候选合计",
    currentTotal: "当前合计",
    hierOutput: "分层输出",
    difference: "差异",
    policyText: "候选 V2 遵循滚动策略方向：以去年同一 ISO 周的需求作为季节性优先基线，在替换当前预测输出前，单独复核 SKU 分摊。",
  },
  bc: {
    top10: "前 10",
  },
  sku: {
    title: "SKU 预测",
    rowsOf: "{{shown}} / {{total}} 行",
  },
  rec: {
    title: "建议预测输出",
    note: "复核层，不替换当前输出",
    planningCsv: "计划 CSV",
    productionBaseline: "正式基线",
    recommendedTotal: "建议合计",
    reviewOutput: "复核输出",
    reviewWatch: "需复核 / 关注",
    keepCurrentN: "保持现状 {{n}}",
    alignedReview: "一致 / 需复核",
    p1p2: "P1 / P2 队列",
    decisionPolicy: "决策政策",
    unchanged: "在此复核层获批之前，当前正式输出保持不变。",
    allDirection: "全部方向",
    allAlignment: "全部一致性",
    filterGroup: "分公司 / 渠道：{{value}} x",
    filterReason: "原因：{{value}} x",
    clearDrill: "清除下钻",
    queue: "复核优先级队列",
    queueNote: "当前筛选下前 {{n}} 行",
    modelAligned: "模型一致",
    modelReview: "模型需复核",
    byGroup: "按分公司 / 渠道的复核汇总",
    groupsN: "当前筛选下 {{n}} 个组",
    increase: "上调",
    decrease: "下调",
    recVsCurrent: "建议 {{rec}}，当前 {{cur}}",
    byReason: "按原因的复核汇总",
    reasonsN: "当前筛选下 {{n}} 个原因",
  },
  csku: {
    title: "候选 SKU 影响",
    note: "按绝对差异排名前 {{n}} 的变动项",
    seasonalityBaseline: "季节性基线",
    dataSignalAdjusted: "按数据信号调整",
    v3vsCurrent: "V3 对比当前",
    v3vsV2: "V3 对比 V2",
    groupsWithReview: "{{n}} 个组含需复核或关注的 SKU",
    cardText: "V2 {{v2}} / V3 {{v3}}，当前 {{cur}}",
    sigRecent12: "近 12 周：{{n}}",
    sigActiveWeeks: "近期有销售周数：{{n}}",
    sigPriorYear: "去年同期：{{n}}",
  },
  bt: {
    summary: "回测汇总",
    lastWeeks: "最近 {{n}} 周实际",
    modelUsed: "使用的模型",
    wapeText: "预测绝对误差总和除以实际数量总和，越低越好。",
    biasText: "（预测 − 实际）÷ 实际。负值表示预测偏低；正值表示预测偏高。",
    level: "层级",
    levelText: "总体反映计划准确度，分公司渠道反映分摊准确度，SKU 反映单品层级的分摊风险。",
  },
  mc: {
    title: "模型对比",
    note: "如有滚动验证，建议以其为准",
    exportModels: "导出模型",
    recommendedModel: "建议模型",
    selectedRolling: "依据滚动验证选出：{{model}} 在 {{windows}} 个窗口中胜出 {{wins}} 个。{{policy}}",
    selectedWape: "先按 WAPE 选择，WAPE 相差在 3 个百分点以内时再看偏差。当前 WAPE {{wape}}，偏差 {{bias}}。",
    currentOutput: "当前预测输出",
    rollingWinner: "滚动胜出模型",
    hybridStatus: "混合模型状态",
    nextDirection: "下一步方向",
    latestV4Gap: "最新 V4 差距",
    v4MinusCurrent: "V4 WAPE 减当前",
    latestV5Gap: "最新 V5 差距",
    v5MinusCurrent: "V5 WAPE 减当前",
    promoCandidate: "升级候选",
    path: "{{label}} 路径",
    promoNote: "升级是指先进行受控试点，而不是自动替换正式输出。",
    lowVolumeTitle: "实际数量较小，WAPE 可能显得极端。使用此建议前请先复核数量影响。",
  },
  feat: {
    candidate: "特征候选",
    candidateNote: "使用节假日、库存和促销滞后信号的总体层级回测。",
    bestWape: "最佳特征 WAPE",
    gapVsCurrent: "与当前的差距",
    backtestWeeks: "回测周数",
    modelSummary: "模型汇总",
    modelSummaryNote: "各候选模型的总体层级准确度",
    signalCoverage: "特征信号覆盖",
    signalCoverageNote: "当前筛选下的节假日、库存和促销/价格风险",
    highPriorityN: "高优先级 {{n}}",
    trend: "特征回测趋势",
    trendNote: "按周对比当前基线与 {{model}}",
    weekLabel: "周次 {{label}}",
    adjustment: "特征调整",
    verdict: "每周特征判定",
    verdictNote: "候选 WAPE 相对当前基线的改善",
    candLower: "候选 WAPE 更低",
    candHigher: "候选 WAPE 更高",
    avgImprovement: "平均改善",
    currentMinusCand: "当前 WAPE 减候选 WAPE",
    bestWorst: "最佳 / 最差周",
    weeklyDetail: "每周回测明细",
    weeklyDetailNote: "按周对比实际与特征预测",
    actionable: "可执行的 AI 输出",
    actionableNote: "按分公司/渠道的建议用法；不会自动替换正式输出",
    pilotCandidates: "试点候选",
    pilotCandNote: "特征在有意义的销量上改善 WAPE",
    keepBaseline: "保持基线",
    worsens: "特征使 WAPE 变差",
    watchLow: "关注 / 低销量",
    insufficient: "信号不足以改变输出",
    weightedGain: "试点加权提升",
    weightedGainNote: "按实际数量加权的 WAPE 点数",
    topPilots: "主要试点候选",
    bcGroupsN: "{{n}} 个分公司/渠道组",
    bcImpact: "分公司 / 渠道的特征影响",
    bcImpactNote: "特征调整有帮助或有损害的地方",
    reviewQueue: "AI 复核队列",
    reviewQueueNote: "{{shown}} / {{total}} 个 SKU 风险",
    totalRisks: "风险总数",
    mediumPriority: "中优先级",
    topRisk: "主要风险",
    allRisk: "全部风险",
    exceptions: "AI 异常分组",
    exceptionsNote: "按风险和建议操作分组的预测数量",
    exceptionBuckets: "异常分组",
    skuRowsN: "{{n}} 行 SKU",
    forecastAtRisk: "有风险的预测量",
    currentFilter: "当前筛选",
    highPriorityForecast: "高优先级预测量",
    bucketHasHigh: "含高优先级行的分组",
    topException: "主要异常",
    forecastQtyN: "预测数量 {{n}}",
    riskByBc: "按分公司 / 渠道的风险",
    top10Filter: "当前筛选下前 10",
  },
  wt: {
    title: "每周模型跟踪",
    note: "每周数据刷新后使用，用于判断基线、AI 或近期销售速率模型是否在改善。",
    export: "导出跟踪",
    latestWeek: "最新一周",
    actualQtyN: "实际数量 {{n}}",
    noRows: "无跟踪数据",
    baselineAvg: "基线平均 WAPE",
    lastTracked: "最近 {{n}} 个跟踪周",
    bestAvg: "最佳模型平均 WAPE",
    bestAvgNote: "实际数据出来后的每周胜出模型",
    topWinner: "胜出次数最多的模型",
    winsN: "近期窗口胜出 {{n}} 次",
    empty: "暂无每周跟踪数据",
  },
  wmc: {
    title: "每周模型对比",
    note: "按周的胜出模型和预测",
    export: "导出每周数据",
  },
  diag: {
    title: "候选诊断",
    note: "V3、V4、V5 与当前预测输出的对比",
    within: "WAPE 相差 0.5 个百分点以内",
    v3Worse: "V3 更差",
    avgGap: "平均 WAPE 差距",
    v3Minus: "V3 减当前",
    v4Beats: "V4 优于当前",
    v4Worse: "V4 更差",
    v4AvgGap: "V4 平均差距",
    v4Minus: "V4 减当前",
    v5Beats: "V5 优于当前",
    v5Worse: "V5 更差",
    v5AvgGap: "V5 平均差距",
    v5Minus: "V5 减当前",
  },
  hyb: {
    title: "混合模拟",
    note: "每个回测周的最佳模型",
    wape: "混合 WAPE",
    better: "比建议模型好 {{n}} 个百分点",
    check: "潜在改善检查",
    text: "这是使用每周历史最佳模型的上限模拟，用于估算是否值得构建真正的混合规则。",
    winningWeeks: "胜出 {{n}} 周",
  },
  rule: {
    title: "规则混合 v2",
    note: "仅使用预测周之前可获得的信息",
    validationStatus: "验证状态",
    upperGap: "与上限的差距",
    vsRecommended: "相对建议模型 {{n}} 个百分点",
    check: "实用混合检查",
    modelsUsed: "使用 {{n}} 个模型",
    text: "规则 v2 依据候选模型之间的一致性：Naive 接近 Hierarchical 时用 Naive，Seasonal 与 MA12 一致或检测到前一周激增时用 Seasonal，否则回退到 MA12 或 Hierarchical。",
  },
  roll: {
    title: "滚动验证",
    note: "{{n}} 个窗口中：V4 胜 {{v4}} / V5 胜 {{v5}} / 规则胜 {{rule}}",
    v4Wins: "V4 滚动胜出",
    v5Wins: "V5 滚动胜出",
    ruleWins: "规则滚动胜出",
    ruleAvgGap: "规则平均差距",
    ruleMinusBest: "规则减最佳单一模型",
  },
  acc: {
    title: "各层级准确度",
    note: "WAPE 越低越好",
    bias: "偏差 {{v}}",
  },
  wavf: {
    title: "每周实际与预测",
    note: "柱：实际 / 预测，线：WAPE",
  },
  wbd: {
    note: "按模型对比实际与预测",
  },
};

// French and Japanese (task #1173, 2026-11-04) — generated from `en`; a string not yet translated reads English.
const fr = {
  "top": {
    "title": "Prévision de la demande",
    "actualsThrough": "Réalisé jusqu'au {{date}}",
    "subtitle": "Prévision hebdomadaire ERPQuantity",
    "env": "API Staging",
    "refreshDataTitle": "Actualiser le mart de prévision, la sortie de prévision et le backtest",
    "refreshData": "Actualiser les données",
    "refresh": "Actualiser",
    "loading": "Chargement"
  },
  "f": {
    "horizon": "Horizon",
    "weeksN": "{{n}} semaines",
    "noMatches": "Aucun résultat",
    "clear": "Effacer"
  },
  "msg": {
    "refreshRunning": "Actualisation des données en cours : mart, prévision et backtest. Cela peut prendre 2 à 3 minutes.",
    "refreshed": "Données de prévision actualisées : {{steps}} à {{time}}",
    "done": "terminé",
    "cannotParse": "Impossible de lire la réponse de {{endpoint}}",
    "cannotLoad": "Impossible de charger {{endpoint}}"
  },
  "stat": {
    "w1": "Prévision S+1",
    "w4": "Prévision 4S",
    "weeksAvailable": "{{n}}S disponibles",
    "total": "Prévision totale",
    "skus": "{{n}} SKU",
    "wape": "WAPE du backtest",
    "totalLevel": "Niveau total"
  },
  "tab": {
    "forecast": "Prévision",
    "sku": "Détail SKU",
    "backtest": "Backtest"
  },
  "level": {
    "total": "Total",
    "branch_channel": "Branche / Canal",
    "sku": "SKU"
  },
  "u": {
    "pts": "{{n}} pts",
    "qty": "{{n}} qté",
    "rows": "{{n}} lignes",
    "weeks": "semaines",
    "ofWindows": "sur {{n}} fenêtres",
    "skuMovers": "SKU en mouvement"
  },
  "v": {
    "all": "Tous",
    "review": "À revoir",
    "watch": "À surveiller",
    "ok": "OK",
    "keepCurrent": "Conserver l'actuel",
    "increaseForecast": "Augmenter la prévision",
    "decreaseForecast": "Diminuer la prévision",
    "aligned": "Aligné",
    "high": "Élevé",
    "medium": "Moyen",
    "low": "Faible",
    "helps": "Favorable",
    "hurts": "Défavorable",
    "neutral": "Neutre",
    "unknown": "Inconnu",
    "unknownReason": "Raison inconnue",
    "unknownRisk": "Risque inconnu",
    "reviewManually": "Revoir manuellement",
    "needsData": "Données requises",
    "keepBaseline": "Conserver la référence",
    "pilotCandidate": "Candidat pilote",
    "reviewCandidate": "Candidat à la revue",
    "hold": "Suspendu",
    "lowVolumeWatch": "Surveillance faible volume",
    "reviewNewDemand": "Revoir la nouvelle demande",
    "reviewDroppedDemand": "Revoir la demande disparue",
    "manualReview": "Revue manuelle",
    "acceptMovement": "Accepter la variation",
    "pass": "Réussi",
    "noRunLoaded": "Aucune exécution chargée",
    "ready": "Prêt",
    "missing": "Manquant",
    "done": "Terminé",
    "waiting": "En attente",
    "reviewFirst": "À revoir d'abord",
    "proceed": "Poursuivre",
    "policyReady": "Politique prête",
    "rollingStable": "Glissant stable",
    "notStableYet": "Pas encore stable",
    "highOverfitRisk": "Risque élevé de surajustement",
    "promising": "Prometteur",
    "promoteReview": "Revue de promotion",
    "watchOnly": "Surveillance uniquement",
    "productionBaseline": "Référence de production",
    "ruleWins": "La règle l'emporte",
    "close": "Proche",
    "featureHelps": "La variable aide",
    "featureHurts": "La variable pénalise",
    "lowVolume": "Faible volume",
    "noFeatureSignal": "Aucun signal de variable",
    "strongCandidate": "Candidat solide",
    "v3Beats": "V3 meilleur que l'actuel",
    "v3Worse": "V3 moins bon que l'actuel"
  },
  "col": {
    "productName": "Nom du produit",
    "branch": "Branche",
    "channel": "Canal",
    "collection": "Collection",
    "category": "Catégorie",
    "candidateQty": "Qté candidate",
    "candidateV3Qty": "Qté candidate V3",
    "currentQty": "Qté actuelle",
    "deltaQty": "Qté delta",
    "deltaPctTxt": "% delta",
    "v3DeltaVsCurrentQty": "Qté delta V3 vs actuel",
    "v3DeltaVsCurrentPct": "% delta V3 vs actuel",
    "v3DeltaVsV2Qty": "Qté delta V3 vs V2",
    "status": "Statut",
    "reason": "Raison",
    "dataSignal": "Signal de données",
    "candidateV3Rule": "Règle candidate V3",
    "recent12Qty": "Qté 12S récentes",
    "recent4Qty": "Qté 4S récentes",
    "recent12ActiveWeeks": "Semaines actives sur 12S récentes",
    "priorYearHorizonQty": "Qté même horizon année précédente",
    "currentForecast": "Prévision actuelle",
    "candidateV3Forecast": "Prévision candidate V3",
    "recommendedForecast": "Prévision recommandée",
    "deltaPct": "% delta",
    "recommendedModel": "Modèle recommandé",
    "bcRecommendedModel": "Modèle recommandé Branche Canal",
    "modelAlignment": "Alignement des modèles",
    "reviewPriority": "Priorité de revue",
    "bcModelStatus": "Statut du modèle Branche Canal",
    "bcModelImprovement": "Amélioration du modèle Branche Canal",
    "direction": "Sens",
    "recent12Actual": "Réalisé 12S récentes",
    "recent4Actual": "Réalisé 4S récentes",
    "priorYearHorizon": "Même horizon année précédente",
    "actualsThrough": "Réalisé jusqu'au",
    "horizonWeeks": "Semaines d'horizon",
    "currentProductionForecast": "Prévision de production actuelle",
    "recommendedPlanningForecast": "Prévision de planification recommandée",
    "plannerOverride": "Prévision corrigée par le planificateur",
    "finalApproved": "Prévision finale approuvée",
    "reviewStatus": "Statut de revue",
    "bcModel": "Modèle Branche Canal",
    "bcModelShort": "Modèle BC",
    "plannerComment": "Commentaire du planificateur",
    "owner": "Responsable",
    "decision": "Décision",
    "year": "Année",
    "week": "Semaine",
    "actualQty": "Qté réelle",
    "baselineForecast": "Prévision de référence",
    "baselineWape": "WAPE de référence",
    "baselineBias": "Biais de référence",
    "featureForecast": "Prévision Feature",
    "featureWape": "WAPE Feature",
    "featureBias": "Biais Feature",
    "ma4Forecast": "Prévision MA4",
    "ma4Bias": "Biais MA4",
    "ma12Forecast": "Prévision MA12",
    "ma12Bias": "Biais MA12",
    "winner": "Gagnant",
    "winnerForecast": "Prévision du gagnant",
    "winnerWape": "WAPE du gagnant",
    "baselineGapVsWinnerPts": "Écart de référence vs gagnant (pts)",
    "section": "Section",
    "model": "Modèle",
    "forecastQty": "Qté prévue",
    "bias": "Biais",
    "currentWape": "WAPE actuel",
    "seasonalWape": "WAPE saisonnier",
    "improvementVsCurrent": "Amélioration vs actuel",
    "hierForecast": "Prévision hiérarchique",
    "hierWape": "WAPE hiérarchique",
    "seasonalForecast": "Prévision saisonnière",
    "v3Forecast": "Prévision V3",
    "v4Forecast": "Prévision V4",
    "v5Forecast": "Prévision V5",
    "naiveForecast": "Prévision naïve",
    "naiveWape": "WAPE naïf",
    "priority": "Priorité",
    "risk": "Risque",
    "action": "Action",
    "lowerQty": "Qté basse",
    "upperQty": "Qté haute",
    "promoShare": "Part de qté en promotion",
    "highDiscountShare": "Part de qté à forte remise",
    "priceDropShare": "Part de qté en baisse de prix",
    "stockoutShare": "Part de qté en rupture de stock",
    "invToSales": "Ratio stock / ventes",
    "recommendedAction": "Action recommandée",
    "rows": "Lignes",
    "highPriorityRows": "Lignes à priorité élevée",
    "mediumPriorityRows": "Lignes à priorité moyenne",
    "avgAdjustmentPct": "Ajustement moyen (%)",
    "signalReason": "Motif du signal",
    "guardrailDecision": "Décision du garde-fou",
    "guardrailAction": "Action du garde-fou",
    "latestRunId": "ID du dernier run",
    "latestRunTime": "Heure du dernier run",
    "previousRunId": "ID du run précédent",
    "previousRunTime": "Heure du run précédent",
    "totalDeltaQty": "Écart total (qté)",
    "totalDeltaPct": "Écart total (%)",
    "previousForecast": "Prévision précédente",
    "plannerDecision": "Décision du planificateur",
    "candidate": "Candidat",
    "current": "Actuel",
    "delta": "Écart",
    "recentRunRate": "Rythme récent",
    "seasonalRecent": "Saisonnier / récent",
    "forecast": "Prévision",
    "lower": "Bas",
    "upper": "Haut",
    "confidence": "Confiance",
    "candidateV3": "Candidat V3",
    "recommended": "Recommandé",
    "alignment": "Alignement",
    "v3Delta": "Écart V3",
    "v3DeltaPct": "Écart V3 %",
    "v3Rule": "Règle V3",
    "skus": "SKUs",
    "previous": "Précédent",
    "largestSku": "Plus grand SKU",
    "location": "Lieu",
    "control": "Contrôle",
    "detail": "Détail",
    "generated": "Généré",
    "runId": "ID du run",
    "latestWape": "Dernier WAPE",
    "latestBias": "Dernier biais",
    "rollingWins": "Victoires glissantes",
    "rollingGap": "Écart glissant",
    "actual": "Réel",
    "absError": "Erreur abs.",
    "signal": "Signal",
    "highPriority": "Priorité élevée",
    "recent4w": "4 dernières sem.",
    "metric": "Indicateur",
    "metricMeaning": "Signification de l'indicateur",
    "verdict": "Verdict",
    "candidateWape": "WAPE du candidat",
    "improvement": "Amélioration",
    "adjustment": "Ajustement",
    "gain": "Gain",
    "avgAdj": "Ajust. moy.",
    "promo": "Promotion",
    "stockout": "Rupture de stock",
    "invSales": "Stock/Ventes",
    "option": "Option",
    "whenToUse": "Quand l'utiliser",
    "recommendation": "Recommandation",
    "bestWape": "Meilleur WAPE",
    "aiForecast": "Prévision IA",
    "baselineGap": "Écart de référence",
    "gap": "Écart",
    "v4Gap": "Écart V4",
    "v5Gap": "Écart V5",
    "outcome": "Résultat",
    "diagnosis": "Diagnostic",
    "v4Diagnosis": "Diagnostic V4",
    "v5Diagnosis": "Diagnostic V5",
    "chosenModel": "Modèle retenu",
    "recentCv4": "CV4 récent",
    "window": "Fenêtre",
    "bestModel": "Meilleur modèle",
    "ruleWape": "WAPE de la règle",
    "bestSingleWape": "Meilleur WAPE d'un modèle seul",
    "ruleBias": "Biais de la règle",
    "v4VsCurrent": "V4 vs actuel",
    "v5VsCurrent": "V5 vs actuel",
    "actualErp": "ERPQuantity réelle",
    "forecastErp": "ERPQuantity prévue"
  },
  "txt": {
    "keepCurrentBaseline": "Conserver la référence actuelle",
    "keepCurrentOutput": "Conserver la sortie actuelle",
    "ruleHybrid": "Hybride à règles",
    "latestRunGenerated": "Dernier run {{id}} généré le {{time}}."
  },
  "csr": {
    "largePct": "Écart en pourcentage important",
    "largeQty": "Impact en quantité important",
    "moderatePct": "Écart en pourcentage modéré",
    "moderateQty": "Impact en quantité modéré"
  },
  "cr": {
    "largeGap": "Écart important entre actuel et candidat",
    "priorAbove": "Demande de l'année précédente très supérieure au rythme récent",
    "priorBelow": "Demande de l'année précédente très inférieure au rythme récent",
    "needsReview": "Revue métier nécessaire",
    "moderateGap": "Écart modéré par rapport à la prévision actuelle",
    "broadlyAligned": "Candidat globalement aligné"
  },
  "conf": {
    "high": "Historique stable au niveau agence/canal et SKU",
    "medium": "Historique exploitable, à vérifier en cas de changement de SKU ou de canal",
    "low": "Historique SKU faible ou demande répartie ; à vérifier manuellement",
    "bucket": "Niveau de confiance de la prévision",
    "title": "Confiance de la prévision",
    "note": "Lignes et part de quantité"
  },
  "sig": {
    "promo": "Promotion / Prix",
    "promoNote": "Risque de demande lié à une remise, un point, une baisse de prix ou un décalage post-promotion",
    "promoMetric": "Part moyenne du signal",
    "stock": "Stock / Rupture de stock",
    "stockNote": "Demande potentiellement perdue ou limitée par un stock faible",
    "stockMetric": "Part moyenne de rupture de stock",
    "holiday": "Jours fériés",
    "holidayNote": "Les ventes récentes peuvent inclure un pic ou un creux lié aux jours fériés",
    "holidayMetric": "Lignes avec signal",
    "noSales": "Aucune vente récente",
    "noSalesNote": "Une prévision existe, mais les 4 dernières semaines n'ont aucune demande",
    "noSalesMetric": "Prévision à risque"
  },
  "fg": {
    "needsDataDetail": "Le modèle Feature a besoin d'un résultat de backtest avant de pouvoir servir à la revue de planification.",
    "keepAction": "Utiliser les signaux Feature uniquement comme file de revue",
    "keepDetail": "Dans le dernier backtest, le candidat Feature ne bat pas la référence actuelle au niveau total.",
    "pilotAction": "Piloter le modèle Feature sur une revue filtrée par agence/canal",
    "pilotDetail": "Le candidat Feature améliore le WAPE et la part de risque à priorité élevée est maîtrisée.",
    "reviewAction": "Utiliser comme diagnostic avant de remplacer la sortie",
    "reviewDetail": "Le candidat Feature apporte un certain gain de précision, mais la file de risques doit encore être surveillée.",
    "holdAction": "Ne pas encore promouvoir le modèle Feature",
    "holdDetail": "Le candidat Feature n'est pas assez stable par rapport à la référence actuelle.",
    "accuracy": "Impact sur la précision",
    "accuracyDetail": "Une valeur négative signifie que le modèle Feature a un WAPE plus bas que la référence actuelle.",
    "risk": "Pression du risque",
    "riskValue": "{{n}} élevé(s)",
    "riskDetail": "{{share}} de la file de revue IA est à priorité élevée.",
    "next": "Prochaine action",
    "nextDetail": "{{n}} groupes de signaux Feature détectés dans le filtre actuel.",
    "title": "Recommandation du garde-fou Feature"
  },
  "fa": {
    "watchReason": "Le signal Feature n'est pas assez net pour modifier la sortie de planification.",
    "pilotReason": "Le modèle Feature améliore suffisamment le WAPE sur un volume significatif.",
    "keepReason": "Le modèle Feature dégrade le WAPE ; conserver la référence actuelle pour cette agence/ce canal.",
    "lowReason": "Le volume est trop faible pour une décision de modèle stable."
  },
  "p7": {
    "backtest": "Backtest du modèle Feature",
    "backtestDetail": "{{n}} semaines disponibles ; la comparaison entre l'actuel et le modèle Feature est chargée.",
    "signals": "Signaux externes connectés",
    "signalsDetail": "{{n}} groupes de signaux détectés à partir des données de jours fériés, de stock, de promotion, de prix ou d'absence de ventes.",
    "queue": "File de risques exploitable",
    "queueDetail": "{{risks}} risques SKU regroupés en {{buckets}} catégories d'exception.",
    "pilot": "Sortie de décision pilote",
    "pilotDetail": "{{pilot}} groupes candidats au pilote ; {{keep}} groupes à conserver sur la référence.",
    "guardrail": "Statut du garde-fou",
    "guardrailDetail": "{{label}} : {{action}}.",
    "pilotReady": "Prêt pour le pilote",
    "pilotReadyDetail": "L'interface de la phase 7 est prête pour une revue pilote contrôlée. Ne pas modifier la sortie de production tant que le workflow de la phase 8 n'est pas défini.",
    "mvpReady": "MVP prêt",
    "mvpReadyDetail": "L'interface de la phase 7 est utile pour la revue de planification. Il reste surtout à approfondir la validation et à définir le workflow de production.",
    "needsWork": "À améliorer",
    "needsWorkDetail": "La phase 7 manque encore de preuves sur les Features avant de pouvoir orienter les décisions de planification.",
    "readiness": "Préparation de la phase 7",
    "checksPassed": "{{done}} / {{total}} contrôles réussis"
  },
  "p8": {
    "official": "Sortie officielle",
    "officialDetail": "La prévision de production reste le mix SKU hiérarchique recent12. La sortie IA/Feature ne la remplace pas automatiquement.",
    "aiUsage": "Usage de l'IA",
    "aiUsageValue": "Couche pilote / revue",
    "aiUsageDetail": "N'utiliser l'IA que pour les groupes agence/canal marqués Candidat pilote dans la sortie IA exploitable.",
    "weekly": "Actualisation hebdomadaire",
    "weeklyValue": "Actualisation manuelle le lundi",
    "weeklyDetail": "Responsable : vous. Lancez Refresh Data une fois la mise à jour hebdomadaire des ventes terminée.",
    "failure": "Échec de l'actualisation",
    "failureValue": "Afficher l'erreur, conserver la référence",
    "failureDetail": "Si l'actualisation échoue ou expire, le tableau de bord affiche l'erreur et nous enquêtons avant d'utiliser la nouvelle sortie.",
    "title": "Brouillon du workflow de production de la phase 8",
    "note": "Politique figée avant la fusion en production",
    "finalLogic": "Logique de la sortie finale",
    "finalLogicValue": "La référence est officielle ; l'IA sert uniquement de pilote/revue",
    "bestOption": "Meilleure option : conserver la référence actuelle comme prévision de production, n'utiliser la sortie IA/Feature que pour des pilotes contrôlés par agence/canal, et conserver la référence dès que la sortie IA est anormale ou non revue.",
    "runGuardrail": "Lancer la décision du garde-fou",
    "branchMovers": "Agences en mouvement",
    "weeklyChecklist": "Checklist opérationnelle hebdomadaire",
    "weeklyChecklistNote": "À utiliser après chaque Refresh Data du lundi",
    "holdDetail": "Détail du motif de blocage",
    "holdReviewThese": "À vérifier avant d'utiliser le nouveau run",
    "guardrailDiagnostics": "Diagnostic du garde-fou",
    "exportHold": "Exporter le CSV des blocages",
    "noReviewMovers": "Aucun mouvement à revoir",
    "allInside": "Tous les mouvements de SKU sont dans le garde-fou.",
    "reviewGroups": "Groupes à revoir",
    "byBranchChannel": "Par agence / canal",
    "noHoldGroups": "Aucun groupe bloqué dans le filtre actuel.",
    "topSkuActions": "Principales actions SKU",
    "top12": "Top 12 des mouvements nécessitant attention",
    "noSkuAction": "Aucune action SKU nécessaire.",
    "controls": "Contrôles de production",
    "controlsReady": "{{ready}} / {{total}} prêts ou conformes à la politique",
    "runHistory": "Historique des runs de prévision",
    "runHistoryCompared": "Dernier run comparé au run précédent",
    "runHistoryWaiting": "En attente d'au moins deux runs",
    "recentRuns": "Runs de prévision récents",
    "latest5": "5 derniers",
    "noRunHistory": "Aucun historique de run chargé.",
    "bcMovement": "Mouvement par agence / canal",
    "top10Abs": "Top 10 par écart absolu",
    "noPrevMovement": "Pas encore de mouvement par rapport au run précédent.",
    "skuMovement": "Mouvement SKU vs run précédent",
    "top15Abs": "Top 15 par écart absolu",
    "noSkuMovement": "Pas encore de mouvement SKU entre runs."
  },
  "mv": {
    "newReason": "La prévision apparaît dans le dernier run mais était à zéro dans le run précédent.",
    "droppedReason": "La prévision est tombée à zéro par rapport au run précédent.",
    "manualReason": "Le mouvement du SKU dépasse le garde-fou de mouvement sévère de la phase 8.",
    "watchReason": "Le SKU a évolué de façon notable, mais sous le garde-fou sévère.",
    "acceptReason": "Le mouvement reste dans le garde-fou SKU."
  },
  "hr": {
    "total": "Mouvement total",
    "totalDetail": "{{pct}} vs run précédent ; {{qty}} qté.",
    "branch": "Mouvements par agence/canal",
    "branchDetail": "{{n}} groupes dépassent 30 % ou 5 000 qté de mouvement.",
    "sku": "Mouvements de SKU",
    "skuDetail": "{{n}} SKUs dépassent 50 % ou 1 000 qté de mouvement."
  },
  "g8": {
    "noRunAction": "Ne pas utiliser la sortie",
    "noRunDetail": "Le tableau de bord ne trouve aucun ID de run de prévision. Actualisez les données ou vérifiez le backend avant d'utiliser la sortie de prévision.",
    "firstAction": "Utiliser la référence uniquement",
    "firstDetail": "Il s'agit du premier run de prévision enregistré ; les contrôles d'anomalies semaine après semaine s'activeront après la prochaine actualisation.",
    "holdAction": "Conserver la référence précédente",
    "holdDetail": "Le run a varié de {{pct}} au total ; {{branch}} mouvements agence/canal et {{sku}} mouvements SKU dépassent le garde-fou.",
    "reviewAction": "À revoir avant export",
    "reviewDetail": "Le run a varié de {{pct}} au total ; vérifiez les principaux mouvements avant de partager avec le planificateur.",
    "passAction": "OK pour la revue du planificateur",
    "passDetail": "Le mouvement d'un run à l'autre reste dans le garde-fou. La référence reste officielle ; l'IA reste une couche de revue."
  },
  "rh": {
    "latest": "Dernier run",
    "previous": "Run précédent",
    "totalDelta": "Écart total",
    "vsPrevious": "vs run précédent",
    "skuDelta": "Écart du nombre de SKU",
    "skuDeltaDetail": "{{current}} actuel / {{previous}} précédent"
  },
  "pc": {
    "export": "Revue de l'export CSV",
    "exportDetail": "La sortie de planification, la sortie recommandée, la file de revue IA, les catégories d'exception et la sortie d'actions peuvent être exportées.",
    "runId": "ID du run / heure de génération",
    "runIdNone": "Aucun ID de run de prévision chargé pour l'instant.",
    "compare": "Comparaison avec le run précédent",
    "compareDetail": "L'écart entre le dernier run et le précédent est de {{pct}} ({{qty}} unités).",
    "compareNone": "Il faut au moins deux runs d'actualisation de prévision pour comparer les évolutions d'une semaine à l'autre.",
    "rollback": "Retour arrière / garde-fou"
  },
  "wc": {
    "sales": "Données de ventes actualisées",
    "salesDetail": "Réel jusqu'au {{date}}.",
    "salesNone": "Aucune date de mart hebdomadaire récente chargée.",
    "run": "Run de prévision généré",
    "runNone": "Refresh Data n'a pas produit d'ID de run.",
    "prev": "Run précédent archivé",
    "prevDetail": "Le run précédent {{id}} est disponible pour comparaison.",
    "prevNone": "Une actualisation hebdomadaire de plus est nécessaire pour activer la comparaison avec le run précédent.",
    "guardrail": "Garde-fou vérifié",
    "export": "Export du planificateur",
    "exportPass": "Le CSV de planification peut être exporté après un dernier contrôle par sondage.",
    "exportOther": "Exportez d'abord le CSV des blocages, puis décidez si le CSV de planification doit être partagé.",
    "action": "Action de la semaine",
    "actionHold": "Conserver la référence / la sortie précédente tant que les principaux mouvements ne sont pas revus.",
    "actionReview": "Revoir les mouvements par agence et par SKU avant de partager.",
    "actionProceed": "Poursuivre le flux d'export hebdomadaire habituel."
  },
  "mr": {
    "currentUse": "Sortie par défaut tant que les signaux des candidats sont encore contrastés.",
    "currentAction": "Conserver comme référence de production",
    "v4Use": "Idéal pour le dernier signal à court terme lorsque la demande récente contredit l'historique saisonnier.",
    "v4Action": "Utiliser comme challenger à court terme",
    "v5Use": "Idéal pour une prévision privilégiant la saisonnalité, avec protection contre les pics saisonniers extrêmes.",
    "v5Action": "Conserver comme prochain candidat à la promotion"
  },
  "rp": {
    "v5": "Passer en revue la promotion du candidat V5",
    "v5Detail": "V5 remporte assez de fenêtres glissantes et améliore le WAPE actuel sur les fenêtres de validation.",
    "v4": "Conserver V4 comme challenger de la dernière fenêtre",
    "v4Detail": "V4 est le plus performant sur le dernier backtest, mais la validation glissante n'est pas assez stable pour remplacer la sortie.",
    "v5Review": "Revoir V5 en vue d'une promotion",
    "v5ReviewDetail": "V5 améliore l'actuel sur le dernier backtest et suit une politique plus claire privilégiant la saisonnalité.",
    "keepDetail": "Les modèles candidats sont instructifs, mais la sortie actuelle reste la référence de production la plus sûre."
  },
  "vs": {
    "needsDataDetail": "La validation hybride nécessite la comparaison de modèles, l'hybride à règles et les lignes de backtest de borne supérieure.",
    "stableDetail": "L'hybride à règles remporte la plupart des fenêtres glissantes et fait au moins aussi bien que le meilleur modèle seul en moyenne.",
    "notStableDetail": "L'hybride à règles remporte la dernière fenêtre mais ne bat pas systématiquement le meilleur modèle seul sur les fenêtres glissantes précédentes.",
    "overfitDetail": "L'hybride à règles atteint la borne supérieure du meilleur modèle par semaine sur une fenêtre courte. Validez-le sur davantage de fenêtres glissantes avant de l'utiliser pour la sortie de prévision.",
    "promisingDetail": "L'hybride à règles améliore nettement le modèle seul actuellement recommandé, mais doit encore être validé sur fenêtres glissantes.",
    "watchDetail": "L'hybride à règles ne montre pas encore d'amélioration assez stable pour remplacer le modèle de prévision actuel."
  },
  "pp": {
    "waitBacktest": "Attendre le backtest",
    "needsDataReason": "Le modèle a besoin du dernier backtest et d'une validation glissante avant la revue de promotion.",
    "preparePilot": "Préparer un pilote contrôlé",
    "promoteReason": "{{model}} remporte la plupart des fenêtres glissantes et améliore le WAPE par rapport à l'actuel.",
    "reviewImpact": "Revoir l'impact par agence/SKU",
    "pilotReason": "{{model}} donne un signal de précision utile mais n'est pas assez stable pour une promotion automatique.",
    "keepDiagnostic": "Conserver comme diagnostic",
    "watchReason": "{{model}} ne bat pas encore la sortie actuelle de façon assez régulière.",
    "waitRolling": "Attendre la validation glissante",
    "ruleNeedsData": "L'hybride à règles a besoin d'une validation glissante avant de devenir candidat à la production.",
    "rulePromote": "L'hybride à règles est stable sur les fenêtres glissantes et peut être comparé à la sortie actuelle dans un pilote contrôlé.",
    "extendWindow": "Étendre la fenêtre de validation",
    "rulePilot": "L'hybride à règles améliore la validation récente mais a encore besoin de preuves face au risque de surajustement.",
    "keepOut": "Laisser hors de la sortie",
    "currentReason": "Référence stable utilisée pour la sortie actuelle du tableau de bord pendant la revue des modèles candidats."
  },
  "md": {
    "hier": "Prévoit d'abord par agence/canal, puis répartit par SKU selon le mix des 12 dernières semaines.",
    "naive1": "Utilise le réel de la semaine précédente comme prévision.",
    "ma4": "Utilise la moyenne des 4 dernières semaines réelles.",
    "ma12": "Utilise la moyenne des 12 dernières semaines réelles.",
    "seasonal": "Utilise la même semaine ISO de l'année précédente lorsqu'elle est disponible.",
    "v3": "Candidat saisonnier ajusté par les signaux de données, atténué vers la demande actuelle/récente.",
    "v4": "Candidat mixant les signaux : saisonnier, sortie actuelle et demande de la semaine dernière.",
    "v5": "Candidat privilégiant la saisonnalité, avec garde-fous pour les grands écarts saisonnier/actuel.",
    "rule": "Modèle hybride à règles n'utilisant que des signaux antérieurs à la prévision.",
    "benchmark": "Modèle de prévision de référence (benchmark)."
  },
  "pd": {
    "seasonal": "Référence privilégiant la saisonnalité ; conserver Hiérarchique pour la revue de répartition par SKU.",
    "v4": "Le candidat V4 est le plus performant en validation glissante ; vérifiez le biais et la répartition par SKU avant promotion.",
    "v5": "Le garde-fou saisonnier de V5 est le plus performant en validation glissante ; vérifiez l'impact par SKU avant promotion.",
    "hybrid": "Candidat hybride ; à valider davantage avant de l'utiliser comme sortie de prévision.",
    "keep": "Conserver la direction actuelle du modèle tant que la validation glissante n'est pas plus solide."
  },
  "ck": {
    "rolling": "La validation glissante soutient la saisonnalité",
    "rollingSeasonal": "Le modèle saisonnier est la recommandation actuelle sur {{n}} fenêtres de validation.",
    "rollingOther": "La recommandation actuelle est {{model}}.",
    "branch": "L'impact par agence/canal est maîtrisé",
    "branchDetail": "{{n}} groupes agence/canal restent à revoir.",
    "branchNone": "Aucun groupe agence/canal n'est signalé À revoir.",
    "highGroups": "Les groupes de SKU à priorité élevée sont revus",
    "highGroupsDetail": "Il reste {{high}} groupe(s) à priorité élevée et {{medium}} à priorité moyenne.",
    "highGroupsNone": "Aucun groupe à priorité élevée ne reste ; {{medium}} groupe(s) à priorité moyenne nécessitent encore une revue de routine.",
    "skuList": "La liste d'impact par SKU est prête pour la revue",
    "skuListDetail": "{{n}} mouvements de SKU sont signalés À revoir ; exportez le CSV pour confirmation métier.",
    "skuListNone": "Aucun mouvement de SKU n'est signalé À revoir."
  },
  "cd": {
    "needsDataDetail": "La décision sur le candidat nécessite une validation glissante, des diagnostics par agence et des lignes d'impact par SKU.",
    "promote": "Promouvoir le candidat",
    "promoteDetail": "La saisonnalité remporte la validation glissante et l'impact du candidat semble maîtrisé.",
    "reviewBefore": "Revoir avant de promouvoir",
    "reviewBeforeDetail": "La saisonnalité remporte la validation glissante, mais l'impact par agence/SKU comporte de gros mouvements qui nécessitent une revue métier.",
    "doNot": "Ne pas encore promouvoir",
    "doNotDetail": "La validation glissante ne justifie pas de remplacer la sortie de prévision actuelle par le Candidat V2."
  },
  "gov": {
    "needsDataDetail": "La sortie recommandée a besoin de lignes de détail avant de pouvoir être pilotée.",
    "reviewOnly": "Couche de revue uniquement",
    "reviewOnlyDetail": "Un impact SKU important ou un désaccord entre modèles subsiste. Utilisez la revue CSV avant de modifier la sortie de production.",
    "pilot": "Pilote contrôlé",
    "pilotDetail": "La sortie recommandée est utilisable pour la revue de planification, mais nécessite encore des vérifications par les responsables agence/canal.",
    "ready": "Prêt pour la revue de promotion",
    "readyDetail": "La plupart des lignes conservent l'actuel ou s'alignent sur la recommandation de modèle par agence/canal ; les écarts restants sont maîtrisés."
  },
  "pol": {
    "guardrail": "Garde-fou de production",
    "pressure": "Pression de revue",
    "pressureDetail": "{{share}} des lignes sont À revoir ; {{watch}} lignes sont À surveiller.",
    "agreement": "Accord entre modèles",
    "agreementValue": "{{n}} alignées",
    "agreementDetail": "{{n}} lignes divergent de la recommandation de modèle par agence/canal.",
    "volume": "Impact sur le volume",
    "volumeDetail": "{{share}} des lignes conservent la référence de production actuelle."
  },
  "lvd": {
    "total": "Précision globale de la demande. Meilleur signal pour la planification d'ensemble.",
    "branch_channel": "Précision après répartition de la demande par agence et canal de vente.",
    "sku": "Précision de la répartition par SKU. Une erreur plus élevée est normale pour les SKU rares ou irréguliers.",
    "other": "Précision de la prévision à ce niveau."
  },
  "btn": {
    "exportCsv": "Exporter en CSV"
  },
  "fc": {
    "actualForecast": "Réel + Prévision",
    "actualForecastNote": "{{actual}} dernières semaines réelles, {{forecast}} prochaines semaines prévues"
  },
  "cand": {
    "title": "Candidat Prévision V2",
    "note": "Privilégie la saisonnalité, ne remplace pas la sortie actuelle",
    "candidateV2": "Candidat V2",
    "diagnostics": "Diagnostics",
    "diagCounts": "{{review}} À revoir / {{watch}} À surveiller",
    "highSkuImpact": "Fort impact SKU",
    "promoteChecklist": "Checklist de promotion",
    "readyToPromote": "Prêt à promouvoir",
    "notReady": "Pas encore prêt",
    "passed": "{{done}} / {{total}} réussis",
    "candidateTotal": "Total candidat",
    "currentTotal": "Total actuel",
    "hierOutput": "Sortie hiérarchique",
    "difference": "Différence",
    "policyText": "Le Candidat V2 suit la direction de la politique glissante : utiliser la demande de la même semaine ISO de l'année précédente comme référence privilégiant la saisonnalité, puis revoir séparément la répartition par SKU avant de remplacer la sortie de prévision actuelle."
  },
  "bc": {
    "top10": "Top 10"
  },
  "sku": {
    "title": "Prévision par SKU",
    "rowsOf": "{{shown}} lignes sur {{total}}"
  },
  "rec": {
    "title": "Sortie de prévision recommandée",
    "note": "Couche de revue, ne remplace pas la sortie actuelle",
    "planningCsv": "CSV de planification",
    "productionBaseline": "référence de production",
    "recommendedTotal": "Total recommandé",
    "reviewOutput": "sortie de revue",
    "reviewWatch": "À revoir / À surveiller",
    "keepCurrentN": "{{n}} à conserver",
    "alignedReview": "alignées / à revoir",
    "p1p2": "File P1 / P2",
    "decisionPolicy": "Politique de décision",
    "unchanged": "La sortie de production actuelle reste inchangée tant que cette couche de revue n'est pas approuvée.",
    "allDirection": "Toutes les directions",
    "allAlignment": "Tous les alignements",
    "filterGroup": "Agence / Canal : {{value}} x",
    "filterReason": "Motif : {{value}} x",
    "clearDrill": "Effacer le détail",
    "queue": "File de revue prioritaire",
    "queueNote": "{{n}} premières lignes du filtre actuel",
    "modelAligned": "Modèle aligné",
    "modelReview": "Modèle à revoir",
    "byGroup": "Synthèse de revue par agence / canal",
    "groupsN": "{{n}} groupes dans le filtre actuel",
    "increase": "Hausse",
    "decrease": "Baisse",
    "recVsCurrent": "Recommandé {{rec}} vs actuel {{cur}}",
    "byReason": "Synthèse de revue par motif",
    "reasonsN": "{{n}} motifs dans le filtre actuel"
  },
  "csku": {
    "title": "Impact du candidat par SKU",
    "note": "{{n}} premiers mouvements par écart absolu",
    "seasonalityBaseline": "référence saisonnière",
    "dataSignalAdjusted": "ajusté par les signaux de données",
    "v3vsCurrent": "V3 vs actuel",
    "v3vsV2": "V3 vs V2",
    "groupsWithReview": "{{n}} groupes avec des SKU À revoir ou À surveiller",
    "cardText": "V2 {{v2}} / V3 {{v3}} vs actuel {{cur}}",
    "sigRecent12": "12 dernières sem. : {{n}}",
    "sigActiveWeeks": "Semaines actives récentes : {{n}}",
    "sigPriorYear": "Même horizon l'an dernier : {{n}}"
  },
  "bt": {
    "summary": "Synthèse du backtest",
    "lastWeeks": "{{n}} dernières semaines réelles",
    "modelUsed": "Modèle utilisé",
    "wapeText": "Erreur de prévision absolue totale divisée par la quantité réelle totale. Plus c'est bas, mieux c'est.",
    "biasText": "Prévision moins réel, en part du réel. Négatif = sous-prévision ; positif = sur-prévision.",
    "level": "Niveau",
    "levelText": "Total indique la précision de planification, Agence Canal la précision de répartition, SKU le risque de répartition au niveau article."
  },
  "mc": {
    "title": "Comparaison des modèles",
    "note": "La recommandation s'appuie sur la validation glissante lorsqu'elle est disponible",
    "exportModels": "Exporter les modèles",
    "recommendedModel": "Modèle recommandé",
    "selectedRolling": "Retenu par validation glissante : {{model}} remporte {{wins}} fenêtres sur {{windows}}. {{policy}}",
    "selectedWape": "Retenu d'abord sur le WAPE, puis sur le biais lorsque le WAPE est à moins de 3 points de pourcentage. WAPE actuel {{wape}}, biais {{bias}}.",
    "currentOutput": "Sortie de prévision actuelle",
    "rollingWinner": "Gagnant glissant",
    "hybridStatus": "Statut de l'hybride",
    "nextDirection": "Prochaine direction",
    "latestV4Gap": "Dernier écart V4",
    "v4MinusCurrent": "WAPE V4 moins actuel",
    "latestV5Gap": "Dernier écart V5",
    "v5MinusCurrent": "WAPE V5 moins actuel",
    "promoCandidate": "Candidat à la promotion",
    "path": "Parcours {{label}}",
    "promoNote": "Promotion signifie d'abord un pilote contrôlé, et non un remplacement automatique en production.",
    "lowVolumeTitle": "La quantité réelle est faible : le WAPE peut paraître extrême. Vérifiez l'impact en quantité avant d'utiliser cette recommandation."
  },
  "feat": {
    "candidate": "Candidat Feature",
    "candidateNote": "Backtest au niveau total utilisant les signaux de jours fériés, de stock et de décalage post-promotion.",
    "bestWape": "Meilleur WAPE Feature",
    "gapVsCurrent": "Écart vs actuel",
    "backtestWeeks": "Semaines de backtest",
    "modelSummary": "Synthèse des modèles",
    "modelSummaryNote": "Précision au niveau total par modèle candidat",
    "signalCoverage": "Couverture des signaux Feature",
    "signalCoverageNote": "Risques liés aux jours fériés, au stock et aux promotions/prix dans le filtre actuel",
    "highPriorityN": "{{n}} à priorité élevée",
    "trend": "Tendance du backtest Feature",
    "trendNote": "Référence actuelle vs {{model}} par semaine",
    "weekLabel": "Semaine {{label}}",
    "adjustment": "Ajustement Feature",
    "verdict": "Verdict hebdomadaire Feature",
    "verdictNote": "Amélioration du WAPE du candidat par rapport à la référence actuelle",
    "candLower": "WAPE du candidat plus bas",
    "candHigher": "WAPE du candidat plus haut",
    "avgImprovement": "Amélioration moyenne",
    "currentMinusCand": "WAPE actuel moins WAPE du candidat",
    "bestWorst": "Meilleure / pire semaine",
    "weeklyDetail": "Détail hebdomadaire du backtest",
    "weeklyDetailNote": "Réel vs prévision Feature par semaine",
    "actionable": "Sortie IA exploitable",
    "actionableNote": "Usage recommandé par agence/canal ; cela ne remplace pas automatiquement la sortie de production",
    "pilotCandidates": "Candidats pilotes",
    "pilotCandNote": "Feature améliore le WAPE sur un volume significatif",
    "keepBaseline": "Conserver la référence",
    "worsens": "Feature dégrade le WAPE",
    "watchLow": "À surveiller / Faible volume",
    "insufficient": "signal insuffisant pour modifier la sortie",
    "weightedGain": "Gain pondéré du pilote",
    "weightedGainNote": "Points de WAPE pondérés par la quantité réelle",
    "topPilots": "Principaux candidats pilotes",
    "bcGroupsN": "{{n}} groupes agence/canal",
    "bcImpact": "Impact Feature par agence / canal",
    "bcImpactNote": "Où l'ajustement Feature aide ou pénalise",
    "reviewQueue": "File de revue IA",
    "reviewQueueNote": "{{shown}} risques SKU sur {{total}}",
    "totalRisks": "Total des risques",
    "mediumPriority": "Priorité moyenne",
    "topRisk": "Risque principal",
    "allRisk": "Tous les risques",
    "exceptions": "Catégories d'exception IA",
    "exceptionsNote": "Quantité prévue regroupée par risque et action recommandée",
    "exceptionBuckets": "Catégories d'exception",
    "skuRowsN": "{{n}} lignes SKU",
    "forecastAtRisk": "Prévision à risque",
    "currentFilter": "filtre actuel",
    "highPriorityForecast": "Prévision à priorité élevée",
    "bucketHasHigh": "la catégorie contient des lignes à priorité élevée",
    "topException": "Exception principale",
    "forecastQtyN": "{{n}} qté prévue",
    "riskByBc": "Risque par agence / canal",
    "top10Filter": "Top 10 du filtre actuel"
  },
  "wt": {
    "title": "Suivi hebdomadaire des modèles",
    "note": "À utiliser après chaque actualisation hebdomadaire des données pour décider si la référence, l'IA ou les modèles de rythme récent s'améliorent.",
    "export": "Exporter le suivi",
    "latestWeek": "Dernière semaine",
    "actualQtyN": "{{n}} qté réelle",
    "noRows": "Aucune ligne de suivi",
    "baselineAvg": "WAPE moyen de référence",
    "lastTracked": "{{n}} dernières semaines suivies",
    "bestAvg": "WAPE moyen du meilleur modèle",
    "bestAvgNote": "gagnant de la semaine une fois le réel connu",
    "topWinner": "Gagnant le plus fréquent",
    "winsN": "{{n}} victoires sur la fenêtre récente",
    "empty": "Aucune ligne de suivi hebdomadaire disponible"
  },
  "wmc": {
    "title": "Comparaison hebdomadaire des modèles",
    "note": "Gagnant et prévision par semaine",
    "export": "Exporter l'hebdomadaire"
  },
  "diag": {
    "title": "Diagnostics des candidats",
    "note": "V3, V4 et V5 comparés à la sortie de prévision actuelle",
    "within": "à moins de 0,5 pt de WAPE",
    "v3Worse": "V3 moins bon",
    "avgGap": "Écart moyen de WAPE",
    "v3Minus": "V3 moins actuel",
    "v4Beats": "V4 bat l'actuel",
    "v4Worse": "V4 moins bon",
    "v4AvgGap": "Écart moyen V4",
    "v4Minus": "V4 moins actuel",
    "v5Beats": "V5 bat l'actuel",
    "v5Worse": "V5 moins bon",
    "v5AvgGap": "Écart moyen V5",
    "v5Minus": "V5 moins actuel"
  },
  "hyb": {
    "title": "Simulation hybride",
    "note": "Meilleur modèle par semaine de backtest",
    "wape": "WAPE hybride",
    "better": "{{n}} pts de mieux que le recommandé",
    "check": "Vérification du potentiel d'amélioration",
    "text": "Il s'agit d'une simulation de borne supérieure utilisant chaque semaine le meilleur modèle historique. Elle aide à estimer si une vraie règle hybride vaut la peine d'être construite.",
    "winningWeeks": "{{n}} semaines gagnantes"
  },
  "rule": {
    "title": "Hybride à règles v2",
    "note": "N'utilise que les informations disponibles avant la semaine de prévision",
    "validationStatus": "Statut de validation",
    "upperGap": "Écart à la borne supérieure",
    "vsRecommended": "{{n}} pts vs recommandé",
    "check": "Vérification de l'hybride en pratique",
    "modelsUsed": "{{n}} modèles utilisés",
    "text": "La règle v2 s'appuie sur l'accord entre modèles candidats : Naïf lorsqu'il est proche de Hiérarchique, Saisonnier lorsqu'il concorde avec MA12 ou détecte un pic de la semaine précédente, sinon MA12 ou Hiérarchique en repli."
  },
  "roll": {
    "title": "Validation glissante",
    "note": "V4 gagne {{v4}} / V5 gagne {{v5}} / la règle gagne {{rule}} fenêtres sur {{n}}",
    "v4Wins": "Victoires glissantes V4",
    "v5Wins": "Victoires glissantes V5",
    "ruleWins": "Victoires glissantes de la règle",
    "ruleAvgGap": "Écart moyen de la règle",
    "ruleMinusBest": "Règle moins meilleur modèle seul"
  },
  "acc": {
    "title": "Précision par niveau",
    "note": "Plus le WAPE est bas, mieux c'est",
    "bias": "Biais {{v}}"
  },
  "wavf": {
    "title": "Réel vs prévision par semaine",
    "note": "Barres : Réel / Prévision, courbe : WAPE"
  },
  "wbd": {
    "note": "Réel vs prévision par modèle"
  }
};
const ja = {
  "top": {
    "title": "需要予測",
    "actualsThrough": "{{date}} までの実績",
    "subtitle": "週次 ERPQuantity 予測",
    "env": "Staging API",
    "refreshDataTitle": "予測マート、予測出力、バックテストを更新",
    "refreshData": "データを更新",
    "refresh": "更新",
    "loading": "読み込み中"
  },
  "f": {
    "horizon": "予測期間",
    "weeksN": "{{n}} 週間",
    "noMatches": "一致なし",
    "clear": "クリア"
  },
  "msg": {
    "refreshRunning": "データ更新を実行中：マート、予測、バックテスト。2～3 分かかる場合があります。",
    "refreshed": "予測データを更新しました：{{steps}}（{{time}}）",
    "done": "完了",
    "cannotParse": "{{endpoint}} のレスポンスを読み取れません",
    "cannotLoad": "{{endpoint}} を読み込めません"
  },
  "stat": {
    "w1": "W+1 予測",
    "w4": "4W 予測",
    "weeksAvailable": "{{n}}W 分あり",
    "total": "予測合計",
    "skus": "{{n}} SKU",
    "wape": "バックテスト WAPE",
    "totalLevel": "全体レベル"
  },
  "tab": {
    "forecast": "予測",
    "sku": "SKU 詳細",
    "backtest": "バックテスト"
  },
  "level": {
    "total": "合計",
    "branch_channel": "ブランチ / チャネル",
    "sku": "SKU"
  },
  "u": {
    "pts": "{{n}} pts",
    "qty": "{{n}} 数量",
    "rows": "{{n}} 行",
    "weeks": "週",
    "ofWindows": "{{n}} ウィンドウ中",
    "skuMovers": "SKU の増減"
  },
  "v": {
    "all": "すべて",
    "review": "要確認",
    "watch": "注視",
    "ok": "OK",
    "keepCurrent": "現状維持",
    "increaseForecast": "予測を引き上げ",
    "decreaseForecast": "予測を引き下げ",
    "aligned": "整合",
    "high": "高",
    "medium": "中",
    "low": "低",
    "helps": "プラス",
    "hurts": "マイナス",
    "neutral": "中立",
    "unknown": "不明",
    "unknownReason": "理由不明",
    "unknownRisk": "リスク不明",
    "reviewManually": "手動で確認",
    "needsData": "データが必要",
    "keepBaseline": "基準を維持",
    "pilotCandidate": "パイロット候補",
    "reviewCandidate": "レビュー候補",
    "hold": "保留",
    "lowVolumeWatch": "少量注視",
    "reviewNewDemand": "新規需要を確認",
    "reviewDroppedDemand": "需要減を確認",
    "manualReview": "手動レビュー",
    "acceptMovement": "変動を受け入れ",
    "pass": "合格",
    "noRunLoaded": "実行結果が未読み込み",
    "ready": "準備完了",
    "missing": "未入力",
    "done": "完了",
    "waiting": "待機中",
    "reviewFirst": "先に確認",
    "proceed": "進める",
    "policyReady": "ポリシー準備完了",
    "rollingStable": "ローリング安定",
    "notStableYet": "まだ安定していない",
    "highOverfitRisk": "過学習リスク高",
    "promising": "有望",
    "promoteReview": "昇格レビュー",
    "watchOnly": "注視のみ",
    "productionBaseline": "本番の基準",
    "ruleWins": "ルールが優位",
    "close": "僅差",
    "featureHelps": "特徴量がプラス",
    "featureHurts": "特徴量がマイナス",
    "lowVolume": "少量",
    "noFeatureSignal": "特徴量のシグナルなし",
    "strongCandidate": "有力候補",
    "v3Beats": "V3 が現行を上回る",
    "v3Worse": "V3 が現行を下回る"
  },
  "col": {
    "productName": "商品名",
    "branch": "ブランチ",
    "channel": "チャネル",
    "collection": "コレクション",
    "category": "カテゴリ",
    "candidateQty": "候補数量",
    "candidateV3Qty": "V3 候補数量",
    "currentQty": "現行数量",
    "deltaQty": "差分数量",
    "deltaPctTxt": "差分率",
    "v3DeltaVsCurrentQty": "V3 の現行との差分数量",
    "v3DeltaVsCurrentPct": "V3 の現行との差分率",
    "v3DeltaVsV2Qty": "V3 の V2 との差分数量",
    "status": "ステータス",
    "reason": "理由",
    "dataSignal": "データシグナル",
    "candidateV3Rule": "V3 候補ルール",
    "recent12Qty": "直近 12W 数量",
    "recent4Qty": "直近 4W 数量",
    "recent12ActiveWeeks": "直近 12W の稼働週数",
    "priorYearHorizonQty": "前年同期間の数量",
    "currentForecast": "現行予測",
    "candidateV3Forecast": "V3 候補予測",
    "recommendedForecast": "推奨予測",
    "deltaPct": "差分 %",
    "recommendedModel": "推奨モデル",
    "bcRecommendedModel": "ブランチ・チャネル推奨モデル",
    "modelAlignment": "モデルの整合性",
    "reviewPriority": "レビュー優先度",
    "bcModelStatus": "ブランチ・チャネルモデルのステータス",
    "bcModelImprovement": "ブランチ・チャネルモデルの改善",
    "direction": "方向",
    "recent12Actual": "直近 12W 実績",
    "recent4Actual": "直近 4W 実績",
    "priorYearHorizon": "前年同期間",
    "actualsThrough": "実績の基準日",
    "horizonWeeks": "予測期間（週）",
    "currentProductionForecast": "現行の本番予測",
    "recommendedPlanningForecast": "推奨する計画予測",
    "plannerOverride": "プランナー修正予測",
    "finalApproved": "最終承認予測",
    "reviewStatus": "レビューステータス",
    "bcModel": "ブランチ・チャネルモデル",
    "bcModelShort": "BC モデル",
    "plannerComment": "プランナーのコメント",
    "owner": "担当者",
    "decision": "決定",
    "year": "年",
    "week": "週",
    "actualQty": "実績数量",
    "baselineForecast": "ベースライン予測",
    "baselineWape": "ベースライン WAPE",
    "baselineBias": "ベースラインバイアス",
    "featureForecast": "Feature 予測",
    "featureWape": "Feature WAPE",
    "featureBias": "Feature バイアス",
    "ma4Forecast": "MA4 予測",
    "ma4Bias": "MA4 バイアス",
    "ma12Forecast": "MA12 予測",
    "ma12Bias": "MA12 バイアス",
    "winner": "勝者",
    "winnerForecast": "勝者の予測",
    "winnerWape": "勝者の WAPE",
    "baselineGapVsWinnerPts": "ベースラインと勝者の差（pt）",
    "section": "セクション",
    "model": "モデル",
    "forecastQty": "予測数量",
    "bias": "バイアス",
    "currentWape": "現行 WAPE",
    "seasonalWape": "季節性 WAPE",
    "improvementVsCurrent": "現行比の改善",
    "hierForecast": "階層予測",
    "hierWape": "階層 WAPE",
    "seasonalForecast": "季節性予測",
    "v3Forecast": "V3 予測",
    "v4Forecast": "V4 予測",
    "v5Forecast": "V5 予測",
    "naiveForecast": "ナイーブ予測",
    "naiveWape": "ナイーブ WAPE",
    "priority": "優先度",
    "risk": "リスク",
    "action": "アクション",
    "lowerQty": "下限数量",
    "upperQty": "上限数量",
    "promoShare": "プロモーション数量比率",
    "highDiscountShare": "高割引数量比率",
    "priceDropShare": "値下げ数量比率",
    "stockoutShare": "欠品数量比率",
    "invToSales": "在庫対売上比率",
    "recommendedAction": "推奨アクション",
    "rows": "行数",
    "highPriorityRows": "優先度の高い行",
    "mediumPriorityRows": "優先度が中の行",
    "avgAdjustmentPct": "平均調整率（%）",
    "signalReason": "シグナルの理由",
    "guardrailDecision": "ガードレール判定",
    "guardrailAction": "ガードレールのアクション",
    "latestRunId": "最新の実行 ID",
    "latestRunTime": "最新の実行日時",
    "previousRunId": "前回の実行 ID",
    "previousRunTime": "前回の実行日時",
    "totalDeltaQty": "差分合計（数量）",
    "totalDeltaPct": "差分合計（%）",
    "previousForecast": "前回の予測",
    "plannerDecision": "プランナーの判断",
    "candidate": "候補",
    "current": "現行",
    "delta": "差分",
    "recentRunRate": "直近のランレート",
    "seasonalRecent": "季節性／直近",
    "forecast": "予測",
    "lower": "下限",
    "upper": "上限",
    "confidence": "信頼度",
    "candidateV3": "候補 V3",
    "recommended": "推奨",
    "alignment": "整合性",
    "v3Delta": "V3 差分",
    "v3DeltaPct": "V3 差分 %",
    "v3Rule": "V3 ルール",
    "skus": "SKU 数",
    "previous": "前回",
    "largestSku": "最大の SKU",
    "location": "拠点",
    "control": "管理",
    "detail": "詳細",
    "generated": "生成日時",
    "runId": "実行 ID",
    "latestWape": "最新 WAPE",
    "latestBias": "最新バイアス",
    "rollingWins": "ローリング勝数",
    "rollingGap": "ローリング差",
    "actual": "実績",
    "absError": "絶対誤差",
    "signal": "シグナル",
    "highPriority": "優先度高",
    "recent4w": "直近4週",
    "metric": "指標",
    "metricMeaning": "指標の意味",
    "verdict": "判定",
    "candidateWape": "候補 WAPE",
    "improvement": "改善",
    "adjustment": "調整",
    "gain": "改善幅",
    "avgAdj": "平均調整",
    "promo": "プロモーション",
    "stockout": "欠品",
    "invSales": "在庫/売上",
    "option": "オプション",
    "whenToUse": "使う場面",
    "recommendation": "推奨",
    "bestWape": "最良 WAPE",
    "aiForecast": "AI 予測",
    "baselineGap": "ベースラインとの差",
    "gap": "差",
    "v4Gap": "V4 との差",
    "v5Gap": "V5 との差",
    "outcome": "結果",
    "diagnosis": "診断",
    "v4Diagnosis": "V4 診断",
    "v5Diagnosis": "V5 診断",
    "chosenModel": "採用モデル",
    "recentCv4": "直近 CV4",
    "window": "ウィンドウ",
    "bestModel": "最良モデル",
    "ruleWape": "ルール WAPE",
    "bestSingleWape": "単一モデル最良 WAPE",
    "ruleBias": "ルールバイアス",
    "v4VsCurrent": "V4 と現行の比較",
    "v5VsCurrent": "V5 と現行の比較",
    "actualErp": "実績 ERPQuantity",
    "forecastErp": "予測 ERPQuantity"
  },
  "txt": {
    "keepCurrentBaseline": "現行ベースラインを維持",
    "keepCurrentOutput": "現行の出力を維持",
    "ruleHybrid": "ルールハイブリッド",
    "latestRunGenerated": "最新の実行 {{id}} は {{time}} に生成されました。"
  },
  "csr": {
    "largePct": "割合の差が大きい",
    "largeQty": "数量への影響が大きい",
    "moderatePct": "割合の差が中程度",
    "moderateQty": "数量への影響が中程度"
  },
  "cr": {
    "largeGap": "現行と候補の差が大きい",
    "priorAbove": "前年の需要が直近のランレートを大きく上回っている",
    "priorBelow": "前年の需要が直近のランレートを大きく下回っている",
    "needsReview": "業務側のレビューが必要",
    "moderateGap": "現行予測との差が中程度",
    "broadlyAligned": "候補はおおむね整合"
  },
  "conf": {
    "high": "拠点/チャネルおよび SKU レベルで履歴が安定",
    "medium": "履歴は利用可能。SKU やチャネルの変化に注意してレビュー",
    "low": "SKU の履歴が少ない、または配分された需要のため、手動でレビュー",
    "bucket": "予測の信頼度区分",
    "title": "予測の信頼度",
    "note": "行数と数量の比率"
  },
  "sig": {
    "promo": "プロモーション／価格",
    "promoNote": "割引、ポイント、値下げ、プロモーション後の反動による需要リスク",
    "promoMetric": "平均シグナル比率",
    "stock": "在庫／欠品",
    "stockNote": "失われた可能性のある需要、または低在庫で制約された需要",
    "stockMetric": "平均欠品比率",
    "holiday": "祝日",
    "holidayNote": "直近の売上に祝日による増減が含まれている可能性があります",
    "holidayMetric": "シグナル行数",
    "noSales": "直近の売上なし",
    "noSalesNote": "予測はあるものの、直近4週間に需要がありません",
    "noSalesMetric": "リスクのある予測"
  },
  "fg": {
    "needsDataDetail": "Feature モデルは、計画レビューに使う前にバックテスト結果が必要です。",
    "keepAction": "Feature シグナルはレビュー用キューとしてのみ使用",
    "keepDetail": "最新のバックテストでは、Feature 候補は全体レベルで現行ベースラインを上回っていません。",
    "pilotAction": "拠点/チャネルで絞り込んだレビューで Feature モデルをパイロット運用",
    "pilotDetail": "Feature 候補は WAPE を改善しており、優先度の高いリスクの割合も抑えられています。",
    "reviewAction": "出力を置き換える前の診断として使用",
    "reviewDetail": "Feature 候補は精度がやや向上していますが、リスクキューの継続的な監視が必要です。",
    "holdAction": "Feature モデルはまだ昇格しない",
    "holdDetail": "Feature 候補は現行ベースラインと比べて十分に安定していません。",
    "accuracy": "精度への影響",
    "accuracyDetail": "マイナスは、Feature モデルの WAPE が現行ベースラインより低いことを意味します。",
    "risk": "リスクの圧力",
    "riskValue": "高 {{n}} 件",
    "riskDetail": "AI レビューキューの {{share}} が優先度高です。",
    "next": "次のアクション",
    "nextDetail": "現在のフィルターで Feature シグナルグループが {{n}} 件検出されました。",
    "title": "Feature ガードレール推奨"
  },
  "fa": {
    "watchReason": "Feature シグナルは、計画の出力を変更するには決め手に欠けます。",
    "pilotReason": "Feature モデルは、まとまった数量で WAPE を十分に改善しています。",
    "keepReason": "Feature モデルは WAPE を悪化させるため、この拠点/チャネルでは現行ベースラインを維持します。",
    "lowReason": "数量が少なく、安定したモデル判断ができません。"
  },
  "p7": {
    "backtest": "Feature モデルのバックテスト",
    "backtestDetail": "{{n}} 週分があり、現行と Feature モデルの比較を読み込み済みです。",
    "signals": "外部シグナルを接続済み",
    "signalsDetail": "祝日、在庫、プロモーション、価格、売上なしのデータから {{n}} 件のシグナルグループを検出しました。",
    "queue": "対応可能なリスクキュー",
    "queueDetail": "{{risks}} 件の SKU リスクを {{buckets}} 件の例外区分にまとめました。",
    "pilot": "パイロット判定の出力",
    "pilotDetail": "パイロット候補グループ {{pilot}} 件、ベースライン維持グループ {{keep}} 件。",
    "guardrail": "ガードレールの状態",
    "guardrailDetail": "{{label}}：{{action}}。",
    "pilotReady": "パイロット準備完了",
    "pilotReadyDetail": "フェーズ7の UI は、管理されたパイロットレビューの準備ができています。フェーズ8のワークフローが定まるまで、本番の出力は変更しないでください。",
    "mvpReady": "MVP 準備完了",
    "mvpReadyDetail": "フェーズ7の UI は計画レビューに役立ちます。残る作業は主に、検証の深掘りと本番ワークフローです。",
    "needsWork": "要改善",
    "needsWorkDetail": "フェーズ7が計画判断を支えるには、Feature に関する根拠がさらに必要です。",
    "readiness": "フェーズ7の準備状況",
    "checksPassed": "{{done}} / {{total}} 件のチェックに合格"
  },
  "p8": {
    "official": "正式な出力",
    "officialDetail": "本番の予測は、引き続き階層型 recent12 SKU ミックスです。AI/Feature の出力が自動的に置き換えることはありません。",
    "aiUsage": "AI の利用",
    "aiUsageValue": "パイロット／レビュー層",
    "aiUsageDetail": "AI は、「対応可能な AI 出力」でパイロット候補とされた拠点/チャネルのグループにのみ使用してください。",
    "weekly": "週次更新",
    "weeklyValue": "月曜に手動で更新",
    "weeklyDetail": "担当：ご自身。週次の売上データ更新が完了したら Refresh Data を実行してください。",
    "failure": "更新の失敗",
    "failureValue": "エラーを表示し、ベースラインを維持",
    "failureDetail": "更新が失敗またはタイムアウトした場合、ダッシュボードにエラーが表示され、新しい出力を使う前に原因を調査します。",
    "title": "フェーズ8 本番ワークフロー案",
    "note": "本番へのマージ前に方針を確定",
    "finalLogic": "最終出力のロジック",
    "finalLogicValue": "ベースラインが正式、AI はパイロット／レビュー専用",
    "bestOption": "最良の選択肢：現行ベースラインを本番予測として維持し、AI/Feature の出力は管理された拠点/チャネルのパイロットにのみ使い、AI の出力が異常な場合やレビュー未了の場合は常にベースラインを維持します。",
    "runGuardrail": "ガードレール判定を実行",
    "branchMovers": "変動の大きい拠点",
    "weeklyChecklist": "週次運用チェックリスト",
    "weeklyChecklistNote": "毎週月曜の Refresh Data の後に使用してください",
    "holdDetail": "保留理由の詳細",
    "holdReviewThese": "新しい実行を使う前にこれらを確認してください",
    "guardrailDiagnostics": "ガードレール診断",
    "exportHold": "保留 CSV をエクスポート",
    "noReviewMovers": "レビュー対象の変動なし",
    "allInside": "すべての SKU の変動がガードレール内です。",
    "reviewGroups": "レビュー対象グループ",
    "byBranchChannel": "拠点／チャネル別",
    "noHoldGroups": "現在のフィルターに保留グループはありません。",
    "topSkuActions": "SKU アクション上位",
    "top12": "要対応の変動 上位12件",
    "noSkuAction": "必要な SKU アクションはありません。",
    "controls": "本番コントロール",
    "controlsReady": "{{ready}} / {{total}} 件が準備完了またはポリシー準拠",
    "runHistory": "予測実行の履歴",
    "runHistoryCompared": "最新の実行を前回の実行と比較",
    "runHistoryWaiting": "実行が2回以上になるまでお待ちください",
    "recentRuns": "最近の予測実行",
    "latest5": "直近5件",
    "noRunHistory": "読み込まれた実行履歴はありません。",
    "bcMovement": "拠点／チャネルの変動",
    "top10Abs": "絶対差分の上位10件",
    "noPrevMovement": "前回実行からの変動はまだありません。",
    "skuMovement": "SKU の変動（前回実行との比較）",
    "top15Abs": "絶対差分の上位15件",
    "noSkuMovement": "SKU の実行間変動はまだありません。"
  },
  "mv": {
    "newReason": "最新の実行には予測がありますが、前回の実行ではゼロでした。",
    "droppedReason": "前回の実行と比べて、予測がゼロになりました。",
    "manualReason": "SKU の変動がフェーズ8の大幅変動ガードレールを超えています。",
    "watchReason": "SKU は大きく変動しましたが、大幅変動ガードレールは下回っています。",
    "acceptReason": "変動は SKU ガードレールの範囲内です。"
  },
  "hr": {
    "total": "全体の変動",
    "totalDetail": "前回実行比 {{pct}}、数量 {{qty}}。",
    "branch": "拠点/チャネルの変動",
    "branchDetail": "{{n}} グループが 30% または 5,000 数量の変動を超えています。",
    "sku": "SKU の変動",
    "skuDetail": "{{n}} 件の SKU が 50% または 1,000 数量の変動を超えています。"
  },
  "g8": {
    "noRunAction": "出力を使用しない",
    "noRunDetail": "ダッシュボードで予測の実行 ID が見つかりません。予測の出力を使う前に、データを更新するかバックエンドを確認してください。",
    "firstAction": "ベースラインのみを使用",
    "firstDetail": "これは保存された最初の予測実行のため、週次の異常チェックは次回の更新後から有効になります。",
    "holdAction": "前回のベースラインを維持",
    "holdDetail": "実行全体で {{pct}} 変動しました。拠点/チャネルの変動 {{branch}} 件と SKU の変動 {{sku}} 件がガードレールを超えています。",
    "reviewAction": "エクスポート前にレビュー",
    "reviewDetail": "実行全体で {{pct}} 変動しました。プランナーに共有する前に、変動の大きい項目を確認してください。",
    "passAction": "プランナーのレビューに進めます",
    "passDetail": "実行間の変動はガードレール内です。ベースラインが引き続き正式で、AI はレビュー層のままです。"
  },
  "rh": {
    "latest": "最新の実行",
    "previous": "前回の実行",
    "totalDelta": "差分合計",
    "vsPrevious": "前回実行比",
    "skuDelta": "SKU 数の差分",
    "skuDeltaDetail": "現在 {{current}} / 前回 {{previous}}"
  },
  "pc": {
    "export": "CSV エクスポートのレビュー",
    "exportDetail": "計画の出力、推奨出力、AI レビューキュー、例外区分、アクション出力をエクスポートできます。",
    "runId": "実行 ID／生成日時",
    "runIdNone": "予測の実行 ID はまだ読み込まれていません。",
    "compare": "前回実行との比較",
    "compareDetail": "最新の実行と前回の実行の差分は {{pct}}（{{qty}} 単位）です。",
    "compareNone": "前週比の変化を比較するには、予測の更新実行が2回以上必要です。",
    "rollback": "ロールバック／ガードレール"
  },
  "wc": {
    "sales": "売上データを更新済み",
    "salesDetail": "実績は {{date}} までです。",
    "salesNone": "最新の週次マートの日付が読み込まれていません。",
    "run": "予測の実行を生成済み",
    "runNone": "Refresh Data で実行 ID が生成されていません。",
    "prev": "前回の実行をアーカイブ済み",
    "prevDetail": "比較用に前回の実行 {{id}} を利用できます。",
    "prevNone": "前回実行との比較を有効にするには、あと1回の週次更新が必要です。",
    "guardrail": "ガードレールを確認済み",
    "export": "プランナー向けエクスポート",
    "exportPass": "最終の抜き取り確認の後、計画 CSV をエクスポートできます。",
    "exportOther": "先に保留 CSV をエクスポートし、そのうえで計画 CSV を共有するか判断してください。",
    "action": "今週のアクション",
    "actionHold": "変動の大きい項目をレビューするまで、ベースライン／前回の出力を維持してください。",
    "actionReview": "共有する前に、拠点と SKU の変動をレビューしてください。",
    "actionProceed": "通常の週次エクスポートの流れで進めてください。"
  },
  "mr": {
    "currentUse": "候補のシグナルがまだ割れている間の既定の出力です。",
    "currentAction": "本番のベースラインとして維持",
    "v4Use": "直近の需要が季節性の履歴と食い違うとき、最新の短期シグナルに最適です。",
    "v4Action": "短期のチャレンジャーとして使用",
    "v5Use": "季節性を優先した予測で、極端な季節変動への保護があります。",
    "v5Action": "次の昇格候補として維持"
  },
  "rp": {
    "v5": "V5 候補の昇格をレビュー",
    "v5Detail": "V5 は十分な数のローリングウィンドウで勝ち、検証ウィンドウ全体で現行 WAPE を改善しています。",
    "v4": "V4 を最新ウィンドウのチャレンジャーとして維持",
    "v4Detail": "V4 は最新のバックテストで最も優れていますが、ローリング検証は出力を置き換えるほど安定していません。",
    "v5Review": "昇格に向けて V5 をレビュー",
    "v5ReviewDetail": "V5 は最新のバックテストで現行を改善しており、季節性優先の方針も明確です。",
    "keepDetail": "候補モデルは参考になりますが、現行の出力が依然として最も安全な本番ベースラインです。"
  },
  "vs": {
    "needsDataDetail": "ハイブリッド検証には、モデル比較、ルールハイブリッド、上限バックテストの行が必要です。",
    "stableDetail": "ルールハイブリッドは多くのローリングウィンドウで勝ち、平均で最良の単一モデルと同等以上です。",
    "notStableDetail": "ルールハイブリッドは最新ウィンドウで勝っていますが、以前のローリングウィンドウでは最良の単一モデルに安定して勝てていません。",
    "overfitDetail": "ルールハイブリッドは、短いウィンドウで週ごとの最良モデルの上限に一致しています。予測の出力に使う前に、より多くのローリングウィンドウで検証してください。",
    "promisingDetail": "ルールハイブリッドは現在の推奨単一モデルを大きく改善していますが、ローリングウィンドウでの検証がまだ必要です。",
    "watchDetail": "ルールハイブリッドは、現行の予測モデルを置き換えられるほど安定した改善をまだ示していません。"
  },
  "pp": {
    "waitBacktest": "バックテストを待つ",
    "needsDataReason": "昇格レビューの前に、モデルには最新のバックテストとローリング検証が必要です。",
    "preparePilot": "管理されたパイロットを準備",
    "promoteReason": "{{model}} は多くのローリングウィンドウで勝ち、現行より WAPE を改善しています。",
    "reviewImpact": "拠点/SKU への影響をレビュー",
    "pilotReason": "{{model}} は有用な精度シグナルを示していますが、自動昇格できるほど安定していません。",
    "keepDiagnostic": "診断用として維持",
    "watchReason": "{{model}} は、現行の出力にまだ安定して勝てていません。",
    "waitRolling": "ローリング検証を待つ",
    "ruleNeedsData": "ルールハイブリッドが本番候補になる前に、ローリング検証が必要です。",
    "rulePromote": "ルールハイブリッドはローリングウィンドウ全体で安定しており、管理されたパイロットで現行の出力と比較できます。",
    "extendWindow": "検証ウィンドウを延長",
    "rulePilot": "ルールハイブリッドは直近の検証で改善していますが、過学習リスクに対する根拠がさらに必要です。",
    "keepOut": "出力には使用しない",
    "currentReason": "候補モデルのレビュー中、現在のダッシュボード出力に使う安定したベースラインです。"
  },
  "md": {
    "hier": "先に拠点/チャネルを予測し、その後、直近12週のミックスで SKU に配分します。",
    "naive1": "前週の実績を予測として使用します。",
    "ma4": "直近4週の実績の平均を使用します。",
    "ma12": "直近12週の実績の平均を使用します。",
    "seasonal": "可能な場合、前年の同じ ISO 週を使用します。",
    "v3": "データシグナルで調整した季節性の候補で、現在／直近の需要に近づけて平滑化しています。",
    "v4": "季節性、現行の出力、前週の需要を組み合わせたシグナルブレンドの候補です。",
    "v5": "季節性優先の候補で、季節性と現行の大きな差にガードレールがあります。",
    "rule": "予測前に得られるシグナルのみを使うルールベースのハイブリッドモデルです。",
    "benchmark": "ベンチマークの予測モデルです。"
  },
  "pd": {
    "seasonal": "季節性優先のベースライン。SKU 配分のレビューには階層型を維持してください。",
    "v4": "V4 候補はローリング検証で最も優れています。昇格前にバイアスと SKU 配分をレビューしてください。",
    "v5": "V5 の季節性優先ガードレールはローリング検証で最も優れています。昇格前に SKU への影響をレビューしてください。",
    "hybrid": "ハイブリッド候補。予測の出力に使う前に、さらに検証してください。",
    "keep": "ローリング検証がより強固になるまで、現行のモデル方針を維持してください。"
  },
  "ck": {
    "rolling": "ローリング検証は季節性を支持",
    "rollingSeasonal": "季節性モデルが、{{n}} 件の検証ウィンドウにわたる現在の推奨です。",
    "rollingOther": "現在の推奨は {{model}} です。",
    "branch": "拠点/チャネルへの影響は管理されている",
    "branchDetail": "{{n}} 件の拠点/チャネルグループがまだレビューを必要としています。",
    "branchNone": "レビュー対象とされた拠点/チャネルグループはありません。",
    "highGroups": "優先度の高い SKU グループはレビュー済み",
    "highGroupsDetail": "優先度高が {{high}} 件、優先度中が {{medium}} 件のグループが残っています。",
    "highGroupsNone": "優先度の高いグループは残っていません。優先度中の {{medium}} 件は、引き続き定期レビューが必要です。",
    "skuList": "SKU への影響リストはレビュー可能",
    "skuListDetail": "{{n}} 件の SKU の変動がレビュー対象です。業務確認のため CSV をエクスポートしてください。",
    "skuListNone": "レビュー対象の SKU の変動はありません。"
  },
  "cd": {
    "needsDataDetail": "候補の判定には、ローリング検証、拠点診断、SKU 影響の行が必要です。",
    "promote": "候補を昇格",
    "promoteDetail": "季節性がローリング検証で勝っており、候補の影響は管理されているようです。",
    "reviewBefore": "昇格前にレビュー",
    "reviewBeforeDetail": "季節性はローリング検証で勝っていますが、拠点/SKU への影響に、業務レビューが必要な大きな変動があります。",
    "doNot": "まだ昇格しない",
    "doNotDetail": "ローリング検証は、現行の予測の出力を候補 V2 に置き換えることを支持していません。"
  },
  "gov": {
    "needsDataDetail": "推奨出力を管理するには、先に明細行が必要です。",
    "reviewOnly": "レビュー層のみ",
    "reviewOnlyDetail": "SKU への大きな影響、またはモデル間の不一致が残っています。本番の出力を変更する前に、CSV でレビューしてください。",
    "pilot": "管理下でパイロット",
    "pilotDetail": "推奨出力は計画レビューに利用できますが、拠点/チャネル担当者による確認がまだ必要です。",
    "ready": "昇格レビューの準備完了",
    "readyDetail": "多くの行は現行を維持するか、拠点/チャネルのモデル推奨と一致しています。残りの差分は管理されています。"
  },
  "pol": {
    "guardrail": "本番ガードレール",
    "pressure": "レビューの負荷",
    "pressureDetail": "行の {{share}} がレビュー、{{watch}} 行が要注意です。",
    "agreement": "モデルの一致",
    "agreementValue": "{{n}} 件が一致",
    "agreementDetail": "{{n}} 行が拠点/チャネルのモデル推奨と一致しません。",
    "volume": "数量への影響",
    "volumeDetail": "行の {{share}} が現行の本番ベースラインを維持しています。"
  },
  "lvd": {
    "total": "全体の需要精度。トップラインの計画に最適なシグナルです。",
    "branch_channel": "需要を拠点と販売チャネルに分けた後の精度です。",
    "sku": "SKU 配分の精度です。SKU が少数または不規則な場合は、誤差が大きくなりがちです。",
    "other": "このレベルでの予測精度です。"
  },
  "btn": {
    "exportCsv": "CSV をエクスポート"
  },
  "fc": {
    "actualForecast": "実績＋予測",
    "actualForecastNote": "直近 {{actual}} 週の実績、今後 {{forecast}} 週の予測"
  },
  "cand": {
    "title": "予測 V2 候補",
    "note": "季節性優先。現行の出力は置き換えません",
    "candidateV2": "候補 V2",
    "diagnostics": "診断",
    "diagCounts": "レビュー {{review}} 件／要注意 {{watch}} 件",
    "highSkuImpact": "SKU への影響大",
    "promoteChecklist": "昇格チェックリスト",
    "readyToPromote": "昇格可能",
    "notReady": "まだ準備できていません",
    "passed": "{{done}} / {{total}} 件が合格",
    "candidateTotal": "候補の合計",
    "currentTotal": "現行の合計",
    "hierOutput": "階層型の出力",
    "difference": "差",
    "policyText": "候補 V2 はローリング方針に沿っており、前年の同じ ISO 週の需要を季節性優先のベースラインとして使います。現行の予測の出力を置き換える前に、SKU 配分を別途レビューしてください。"
  },
  "bc": {
    "top10": "上位10件"
  },
  "sku": {
    "title": "SKU 予測",
    "rowsOf": "{{total}} 行中 {{shown}} 行"
  },
  "rec": {
    "title": "推奨予測の出力",
    "note": "レビュー層。現行の出力は置き換えません",
    "planningCsv": "計画 CSV",
    "productionBaseline": "本番ベースライン",
    "recommendedTotal": "推奨の合計",
    "reviewOutput": "レビュー用の出力",
    "reviewWatch": "レビュー／要注意",
    "keepCurrentN": "現行維持 {{n}} 件",
    "alignedReview": "一致／レビュー",
    "p1p2": "P1 / P2 キュー",
    "decisionPolicy": "判断ポリシー",
    "unchanged": "このレビュー層が承認されるまで、現行の本番出力は変更されません。",
    "allDirection": "すべての方向",
    "allAlignment": "すべての整合性",
    "filterGroup": "拠点／チャネル：{{value}} x",
    "filterReason": "理由：{{value}} x",
    "clearDrill": "ドリルダウンを解除",
    "queue": "レビュー優先度キュー",
    "queueNote": "現在のフィルターの上位 {{n}} 行",
    "modelAligned": "モデル一致",
    "modelReview": "モデル要レビュー",
    "byGroup": "拠点／チャネル別レビューサマリー",
    "groupsN": "現在のフィルターに {{n}} グループ",
    "increase": "増加",
    "decrease": "減少",
    "recVsCurrent": "推奨 {{rec}}、現行 {{cur}}",
    "byReason": "理由別レビューサマリー",
    "reasonsN": "現在のフィルターに {{n}} 件の理由"
  },
  "csku": {
    "title": "候補の SKU への影響",
    "note": "絶対差分の上位 {{n}} 件",
    "seasonalityBaseline": "季節性ベースライン",
    "dataSignalAdjusted": "データシグナル調整済み",
    "v3vsCurrent": "V3 と現行の比較",
    "v3vsV2": "V3 と V2 の比較",
    "groupsWithReview": "レビューまたは要注意の SKU を含むグループ：{{n}} 件",
    "cardText": "V2 {{v2}} / V3 {{v3}}、現行 {{cur}}",
    "sigRecent12": "直近12週：{{n}}",
    "sigActiveWeeks": "直近の稼働週：{{n}}",
    "sigPriorYear": "前年の同期間：{{n}}"
  },
  "bt": {
    "summary": "バックテストの概要",
    "lastWeeks": "直近 {{n}} 週の実績",
    "modelUsed": "使用モデル",
    "wapeText": "予測の絶対誤差の合計を、実績数量の合計で割った値です。低いほど良好です。",
    "biasText": "予測から実績を引いた値の、実績に対する割合です。マイナスは過小予測、プラスは過大予測を意味します。",
    "level": "レベル",
    "levelText": "「全体」は計画の精度、「拠点チャネル」は配分の精度、「SKU」は品目レベルの配分リスクを示します。"
  },
  "mc": {
    "title": "モデル比較",
    "note": "可能な場合、推奨にはローリング検証を使用します",
    "exportModels": "モデルをエクスポート",
    "recommendedModel": "推奨モデル",
    "selectedRolling": "ローリング検証から選定：{{model}} が {{windows}} ウィンドウ中 {{wins}} で勝利。{{policy}}",
    "selectedWape": "まず WAPE で選定し、WAPE の差が3パーセントポイント以内の場合はバイアスで選定しています。現行 WAPE {{wape}}、バイアス {{bias}}。",
    "currentOutput": "現行の予測の出力",
    "rollingWinner": "ローリングの勝者",
    "hybridStatus": "ハイブリッドの状態",
    "nextDirection": "今後の方向性",
    "latestV4Gap": "最新の V4 差",
    "v4MinusCurrent": "V4 WAPE − 現行",
    "latestV5Gap": "最新の V5 差",
    "v5MinusCurrent": "V5 WAPE − 現行",
    "promoCandidate": "昇格候補",
    "path": "{{label}} の経路",
    "promoNote": "昇格とは、まず管理されたパイロットを行うことであり、本番の自動置き換えではありません。",
    "lowVolumeTitle": "実績数量が少ないため、WAPE が極端に見えることがあります。この推奨を使う前に、数量への影響を確認してください。"
  },
  "feat": {
    "candidate": "Feature 候補",
    "candidateNote": "祝日、在庫、プロモーション後の反動のシグナルを使った、全体レベルのバックテストです。",
    "bestWape": "Feature 最良 WAPE",
    "gapVsCurrent": "現行との差",
    "backtestWeeks": "バックテスト週数",
    "modelSummary": "モデルの概要",
    "modelSummaryNote": "候補モデル別の全体レベル精度",
    "signalCoverage": "Feature シグナルの網羅状況",
    "signalCoverageNote": "現在のフィルターにおける祝日、在庫、プロモーション/価格のリスク",
    "highPriorityN": "優先度高 {{n}} 件",
    "trend": "Feature バックテストの推移",
    "trendNote": "週ごとの現行ベースラインと {{model}} の比較",
    "weekLabel": "第 {{label}} 週",
    "adjustment": "Feature 調整",
    "verdict": "週次 Feature 判定",
    "verdictNote": "現行ベースラインに対する候補の WAPE 改善",
    "candLower": "候補の WAPE が低い",
    "candHigher": "候補の WAPE が高い",
    "avgImprovement": "平均改善",
    "currentMinusCand": "現行 WAPE − 候補 WAPE",
    "bestWorst": "最良／最悪の週",
    "weeklyDetail": "週次バックテストの詳細",
    "weeklyDetailNote": "週ごとの実績と Feature 予測の比較",
    "actionable": "対応可能な AI 出力",
    "actionableNote": "拠点/チャネル別の推奨用途です。本番の出力が自動的に置き換わることはありません",
    "pilotCandidates": "パイロット候補",
    "pilotCandNote": "まとまった数量で Feature が WAPE を改善",
    "keepBaseline": "ベースラインを維持",
    "worsens": "Feature が WAPE を悪化",
    "watchLow": "要注意／少数量",
    "insufficient": "出力を変更するにはシグナルが不十分",
    "weightedGain": "パイロットの加重改善",
    "weightedGainNote": "実績数量で重み付けした WAPE ポイント",
    "topPilots": "パイロット候補の上位",
    "bcGroupsN": "{{n}} 件の拠点/チャネルグループ",
    "bcImpact": "拠点／チャネル別の Feature 影響",
    "bcImpactNote": "Feature 調整が効果的な箇所と逆効果な箇所",
    "reviewQueue": "AI レビューキュー",
    "reviewQueueNote": "SKU リスク {{total}} 件中 {{shown}} 件",
    "totalRisks": "リスク合計",
    "mediumPriority": "優先度中",
    "topRisk": "最大のリスク",
    "allRisk": "すべてのリスク",
    "exceptions": "AI 例外区分",
    "exceptionsNote": "リスクと推奨アクションごとにまとめた予測数量",
    "exceptionBuckets": "例外区分",
    "skuRowsN": "SKU {{n}} 行",
    "forecastAtRisk": "リスクのある予測",
    "currentFilter": "現在のフィルター",
    "highPriorityForecast": "優先度高の予測",
    "bucketHasHigh": "区分に優先度高の行を含む",
    "topException": "最大の例外",
    "forecastQtyN": "予測数量 {{n}}",
    "riskByBc": "拠点／チャネル別のリスク",
    "top10Filter": "現在のフィルターの上位10件"
  },
  "wt": {
    "title": "週次モデルトラッキング",
    "note": "週次のデータ更新のたびに使い、ベースライン、AI、直近ランレートのモデルが改善しているかを判断します。",
    "export": "トラッキングをエクスポート",
    "latestWeek": "最新の週",
    "actualQtyN": "実績数量 {{n}}",
    "noRows": "トラッキング行なし",
    "baselineAvg": "ベースライン平均 WAPE",
    "lastTracked": "直近 {{n}} 週のトラッキング",
    "bestAvg": "最良モデルの平均 WAPE",
    "bestAvgNote": "実績確定後の週ごとの勝者",
    "topWinner": "最も多い勝者",
    "winsN": "直近のウィンドウで {{n}} 勝",
    "empty": "利用できる週次トラッキング行がありません"
  },
  "wmc": {
    "title": "週次モデル比較",
    "note": "週ごとの勝者と予測",
    "export": "週次をエクスポート"
  },
  "diag": {
    "title": "候補の診断",
    "note": "V3、V4、V5 と現行の予測の出力との比較",
    "within": "WAPE 0.5pt 以内",
    "v3Worse": "V3 が劣る",
    "avgGap": "平均 WAPE 差",
    "v3Minus": "V3 − 現行",
    "v4Beats": "V4 が現行を上回る",
    "v4Worse": "V4 が劣る",
    "v4AvgGap": "V4 の平均差",
    "v4Minus": "V4 − 現行",
    "v5Beats": "V5 が現行を上回る",
    "v5Worse": "V5 が劣る",
    "v5AvgGap": "V5 の平均差",
    "v5Minus": "V5 − 現行"
  },
  "hyb": {
    "title": "ハイブリッドシミュレーション",
    "note": "バックテスト週ごとの最良モデル",
    "wape": "ハイブリッド WAPE",
    "better": "推奨より {{n}} pt 良好",
    "check": "改善余地の確認",
    "text": "毎週、過去で最良のモデルを使った上限のシミュレーションです。実際のハイブリッドルールを構築する価値があるかを見積もるのに役立ちます。",
    "winningWeeks": "勝利週 {{n}} 週"
  },
  "rule": {
    "title": "ルールハイブリッド v2",
    "note": "予測週より前に得られる情報のみを使用します",
    "validationStatus": "検証の状態",
    "upperGap": "上限との差",
    "vsRecommended": "推奨より {{n}} pt",
    "check": "実用的なハイブリッドの確認",
    "modelsUsed": "使用モデル {{n}} 件",
    "text": "ルール v2 は候補モデル間の一致を利用します。ナイーブが階層型に近い場合はナイーブ、季節性が MA12 と一致する場合や前週のスパイクを検出した場合は季節性、それ以外は MA12 または階層型にフォールバックします。"
  },
  "roll": {
    "title": "ローリング検証",
    "note": "{{n}} ウィンドウ中、V4 が {{v4}} 勝、V5 が {{v5}} 勝、ルールが {{rule}} 勝",
    "v4Wins": "V4 ローリング勝数",
    "v5Wins": "V5 ローリング勝数",
    "ruleWins": "ルール ローリング勝数",
    "ruleAvgGap": "ルールの平均差",
    "ruleMinusBest": "ルール − 最良の単一モデル"
  },
  "acc": {
    "title": "レベル別の精度",
    "note": "WAPE が低いほど良好です",
    "bias": "バイアス {{v}}"
  },
  "wavf": {
    "title": "週次の実績と予測",
    "note": "棒：実績／予測、線：WAPE"
  },
  "wbd": {
    "note": "モデル別の実績と予測"
  }
};

const dictionary = { ns: 'xDemand', en, ko, vi, zh, fr, ja };

export default dictionary;
